import React, { useState, useEffect, useCallback } from 'react';
import { GUIDE_HTML } from './guideContent';

const API_BASE = 'https://api.civicbot.centricitygis.com';

// ─── Category Colors ───────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  'UTILITIES':    { color: '#0EA5E9', bg: '#0EA5E910', label: 'Utilities' },
  'PUBLIC WORKS': { color: '#10B981', bg: '#10B98110', label: 'Public Works' },
  'PARKS':        { color: '#8B5CF6', bg: '#8B5CF610', label: 'Parks' },
  'PLANDEV':      { color: '#F59E0B', bg: '#F59E0B10', label: 'Planning & Dev' },
  'DEFAULT':      { color: '#6B7280', bg: '#6B728010', label: 'General' },
};

function getCat(code) {
  return CATEGORY_CONFIG[code] || CATEGORY_CONFIG['DEFAULT'];
}

// ─── API helper with JWT ───────────────────────────────────────────────────────
// Logins last 7 days. The app used to trust the saved login without checking it, so an
// expired one still showed every page while each save silently failed. Now an expired login
// signs out and says so. Only the auth middleware's own messages count — other 401s (e.g.
// wrong Cityworks credentials in Test Connection) must not sign anyone out.
function expireSession() {
  ['civicbot_token','civicbot_user','civicbot_impersonating','civicbot_super_token','civicbot_super_user'].forEach(k => localStorage.removeItem(k));
  try { sessionStorage.setItem('civicbot_session_expired', '1'); } catch (e) {}
  window.location.reload();
}

function apiFetch(url, options = {}) {
  const token = localStorage.getItem('civicbot_token');
  return fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  }).then(res => {
    if (res.status === 401 && token) {
      res.clone().json().then(body => {
        if (body && (body.error === 'Invalid or expired token' || body.error === 'Authentication required')) expireSession();
      }).catch(() => {});
    }
    return res;
  });
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: #0A0F1C; color: #E2E8F0; min-height: 100vh; }
  :root {
    --accent: #3B82F6; --accent-light: #60A5FA; --accent-dim: #3B82F620;
    --surface: #111827; --surface2: #1A2235; --surface3: #222D42;
    --border: #1E2D45; --border-light: #2A3F5F;
    --text: #E2E8F0; --text-muted: #64748B; --text-dim: #94A3B8;
    --success: #10B981; --warning: #F59E0B; --danger: #EF4444;
  }
  .app { display: flex; flex-direction: column; min-height: 100vh; }

  /* ── Login ── */
  .login-wrap { display: flex; align-items: center; justify-content: center; min-height: 100vh;
    background: radial-gradient(ellipse at 30% 20%, #1E3A5F30 0%, transparent 60%),
                radial-gradient(ellipse at 70% 80%, #0EA5E915 0%, transparent 60%), #0A0F1C; }
  .login-card { width: 420px; background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 48px; box-shadow: 0 24px 80px #00000060; }
  .login-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
  .login-logo-icon { width: 42px; height: 42px; border-radius: 10px; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .login-logo-text { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .login-logo-text span { color: var(--accent-light); }
  .login-subtitle { color: var(--text-muted); font-size: 14px; margin-bottom: 32px; line-height: 1.5; }
  .login-field { margin-bottom: 16px; }
  .login-label { display: block; font-size: 12px; font-weight: 500; color: var(--text-dim); margin-bottom: 8px; letter-spacing: 0.5px; text-transform: uppercase; }
  .login-input { width: 100%; padding: 12px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 14px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .login-input:focus { border-color: var(--accent); }
  .login-btn { width: 100%; padding: 13px; background: var(--accent); border: none; border-radius: 8px; color: white; font-size: 14px; font-weight: 600; font-family: inherit; cursor: pointer; margin-top: 8px; transition: background 0.2s, transform 0.1s; }
  .login-btn:hover { background: #2563EB; }
  .login-btn:active { transform: scale(0.99); }
  .login-btn:disabled { background: var(--surface3); color: var(--text-muted); cursor: not-allowed; }
  .login-error { color: var(--danger); font-size: 13px; margin-top: 12px; text-align: center; }
  .login-forgot { text-align: center; margin-top: 14px; font-size: 12px; color: var(--text-muted); }
  .login-forgot a { color: var(--accent-light); text-decoration: none; cursor: pointer; }
  .login-forgot a:hover { text-decoration: underline; }

  /* ── Layout ── */
  .header { height: 60px; display: flex; align-items: center; justify-content: space-between; padding: 0 28px; background: var(--surface); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 10; }
  .header-logo { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 600; }
  .header-logo-icon { width: 30px; height: 30px; border-radius: 7px; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 14px; }
  .header-right { display: flex; align-items: center; gap: 20px; }
  .header-status { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--success); box-shadow: 0 0 6px var(--success); }
  .logout-btn { padding: 7px 14px; background: transparent; border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-muted); font-size: 12px; font-family: inherit; cursor: pointer; transition: all 0.2s; }
  .logout-btn:hover { border-color: var(--accent); color: var(--text); }
  .main { display: flex; flex: 1; }
  .sidebar { width: 220px; background: var(--surface); border-right: 1px solid var(--border); padding: 20px 12px; display: flex; flex-direction: column; gap: 4px; }
  .sidebar-label { font-size: 10px; font-weight: 600; color: var(--text-muted); letter-spacing: 1px; text-transform: uppercase; padding: 4px 10px; margin-top: 8px; }
  .sidebar-item { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--text-muted); transition: all 0.15s; border: 1px solid transparent; }
  .sidebar-item:hover { background: var(--surface2); color: var(--text); }
  .sidebar-item.active { background: var(--accent-dim); color: var(--accent-light); border-color: var(--accent)30; }
  .content { flex: 1; padding: 32px; overflow-y: auto; }

  /* ── Page header ── */
  .page-header { margin-bottom: 28px; }
  .page-title { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .page-subtitle { font-size: 13px; color: var(--text-muted); margin-top: 4px; }

  /* ── Stats row ── */
  .stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px 24px; }
  .stat-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .stat-value { font-size: 28px; font-weight: 600; margin-top: 6px; letter-spacing: -1px; }
  .stat-sub { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

  /* ── Card ── */
  .card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .card-header { padding: 18px 24px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .card-title { font-size: 14px; font-weight: 600; }
  .card-body { padding: 24px; }

  /* ── SR Template list ── */
  .filter-row { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
  .search-input { flex: 1; padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .search-input:focus { border-color: var(--accent); }
  .filter-btn { padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text-muted); font-size: 12px; font-family: inherit; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .filter-btn:hover, .filter-btn.active { border-color: var(--accent); color: var(--accent-light); }
  .select-all-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background: var(--surface2); border-radius: 8px; margin-bottom: 12px; font-size: 12px; color: var(--text-muted); }
  .select-all-btn { background: none; border: none; color: var(--accent-light); font-size: 12px; font-family: inherit; cursor: pointer; font-weight: 500; }
  .select-all-btn:hover { text-decoration: underline; }
  .sr-list { display: flex; flex-direction: column; gap: 6px; }
  .sr-item { display: flex; align-items: center; gap: 14px; padding: 12px 16px; background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
  .sr-item:hover { border-color: var(--border-light); background: var(--surface3); }
  .sr-item.selected { border-color: var(--accent)50; background: var(--accent-dim); }
  .sr-checkbox { width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--border-light); display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; }
  .sr-item.selected .sr-checkbox { background: var(--accent); border-color: var(--accent); }
  .sr-checkbox-check { color: white; font-size: 11px; font-weight: 700; }
  .sr-info { flex: 1; min-width: 0; }
  .sr-name { font-size: 13px; font-weight: 500; }
  .sr-code { font-size: 11px; color: var(--text-muted); font-family: 'DM Mono', monospace; margin-top: 2px; }
  .sr-badge { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; letter-spacing: 0.3px; flex-shrink: 0; }
  .sr-public-badge { font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 4px; background: #10B98115; color: #10B981; flex-shrink: 0; }

  /* ── Embed code ── */
  .embed-box { background: #080D18; border: 1px solid var(--border); border-radius: 8px; padding: 16px 20px; position: relative; }
  .embed-code { font-family: 'DM Mono', monospace; font-size: 12px; color: #7DD3FC; white-space: pre-wrap; word-break: break-all; line-height: 1.6; }
  .copy-btn { position: absolute; top: 12px; right: 12px; padding: 6px 12px; background: var(--surface3); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-dim); font-size: 11px; font-family: inherit; cursor: pointer; transition: all 0.15s; }
  .copy-btn:hover { border-color: var(--accent); color: var(--accent-light); }
  .copy-btn.copied { background: #10B98120; border-color: #10B981; color: #10B981; }

  /* ── Save bar ── */
  .save-bar { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid var(--border-light); border-radius: 12px; padding: 14px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 8px 40px #00000080; animation: slideUp 0.3s ease; }
  @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
  .save-bar-text { font-size: 13px; color: var(--text-muted); }
  .save-bar-text strong { color: var(--text); }
  .save-btn { padding: 9px 20px; background: var(--accent); border: none; border-radius: 8px; color: white; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; transition: background 0.2s; }
  .save-btn:hover { background: #2563EB; }
  .save-btn.saved { background: var(--success); }

  /* ── Settings ── */
  .settings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .setting-field { display: flex; flex-direction: column; gap: 6px; }
  .setting-label { font-size: 12px; font-weight: 500; color: var(--text-dim); }
  .setting-input { padding: 10px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .setting-input:focus { border-color: var(--accent); }
  .setting-full { grid-column: 1 / -1; }

  /* ── Toggle switch ── */
  .toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; }
  .toggle-row-info { flex: 1; }
  .toggle-row-label { font-size: 13px; font-weight: 600; color: var(--text); }
  .toggle-row-sub { font-size: 12px; color: var(--text-muted); margin-top: 3px; line-height: 1.4; }
  .toggle-switch { width: 44px; height: 24px; border-radius: 12px; border: none; cursor: pointer; position: relative; transition: background 0.2s; flex-shrink: 0; margin-left: 16px; background: var(--border-light); }
  .toggle-switch.on { background: var(--accent); }
  .toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: white; transition: transform 0.2s; box-shadow: 0 1px 4px #00000040; }
  .toggle-switch.on .toggle-thumb { transform: translateX(20px); }

  /* ── Submissions table ── */
  .submissions-table { width: 100%; border-collapse: collapse; }
  .submissions-table th { padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--border); }
  .submissions-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .submissions-table tr:hover td { background: var(--surface2); }
  .delivery-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .delivery-badge.email { background: #F59E0B15; color: #F59E0B; border: 1px solid #F59E0B30; }
  .delivery-badge.cityworks { background: #3B82F615; color: #60A5FA; border: 1px solid #3B82F630; }
  .completed-row td { background: #10B98108; }
  .sub-check { width: 16px; height: 16px; cursor: pointer; accent-color: var(--accent); }
  .notes-input { width: 100%; padding: 4px 8px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text); font-size: 11px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .notes-input:focus { border-color: var(--accent); background: var(--surface3); }
  /* ── Mobile responsive ── */
  .hamburger { display: none; flex-direction: column; justify-content: center; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 8px; border-radius: 6px; }
  .hamburger:hover { background: rgba(255,255,255,0.1); }
  .hamburger span { display: block; width: 22px; height: 2px; background: var(--text-dim); border-radius: 2px; transition: all 0.2s; }
  .sidebar-backdrop { display: none; position: fixed; inset: 0; top: 60px; background: rgba(0,0,0,0.45); z-index: 150; }
  @media (max-width: 768px) {
    .hamburger { display: flex; }
    .header { padding: 0 16px; }
    .header-status { display: none; }
    .header-right { gap: 10px; }
    .header-right span { display: none; }
    .sidebar {
      position: fixed; top: 60px; left: 0; bottom: 0; width: 260px;
      transform: translateX(-100%); transition: transform 0.25s ease;
      z-index: 200; overflow-y: auto;
    }
    .sidebar.open { transform: translateX(0); box-shadow: 4px 0 32px rgba(0,0,0,0.35); }
    .sidebar-backdrop.open { display: block; }
    .content { padding: 16px 14px; }
    .stats-row { grid-template-columns: 1fr 1fr; gap: 10px; }
    .settings-grid { grid-template-columns: 1fr; }
    .card-body { padding: 14px; }
    .card-header { padding: 14px 16px; }
    .main { flex-direction: column; }
    .filter-row { flex-wrap: wrap; gap: 8px; }
    .submissions-table th:nth-child(n+5), .submissions-table td:nth-child(n+5) { display: none; }
    .cities-table th:nth-child(n+5), .cities-table td:nth-child(n+5) { display: none; }
  }
  @media print {
    .header, .sidebar, .save-bar, .card-header button, .filter-btn, .search-input, .notes-input, .sub-check { display: none !important; }
    body { background: white !important; color: black !important; }
    .card { border: 1px solid #ccc !important; box-shadow: none !important; }
    .submissions-table th, .submissions-table td { color: black !important; border-color: #ccc !important; font-size: 11px !important; padding: 6px 8px !important; }
    .content { padding: 0 !important; }
    .stat-card { background: white !important; border: 1px solid #ccc !important; }
    .stat-value, .stat-label, .stat-sub, .page-title, .page-subtitle { color: black !important; }
  }

  /* ── Billing card ── */
  .billing-card { background: linear-gradient(135deg, #1E3A5F, #1a2235); border: 1px solid #2A3F5F; border-radius: 12px; padding: 24px; margin-bottom: 20px; }
  .billing-trial-badge { display: inline-block; background: #10B98120; border: 1px solid #10B98140; color: #10B981; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 20px; margin-bottom: 12px; }
  .billing-trial-badge.warning { background: #F59E0B20; border-color: #F59E0B40; color: #F59E0B; }
  .billing-trial-badge.danger { background: #EF444420; border-color: #EF444440; color: #EF4444; }
  .billing-plan-name { font-size: 18px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .billing-trial-days { font-size: 13px; color: var(--text-muted); margin-bottom: 16px; }
  .billing-plans-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 16px; }
  .billing-plan-option { background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px; cursor: pointer; transition: all 0.15s; text-align: center; }
  .billing-plan-option:hover { border-color: var(--accent); background: var(--accent-dim); }
  .billing-plan-option.current { border-color: var(--accent); background: var(--accent-dim); }
  .billing-plan-price { font-size: 18px; font-weight: 700; color: var(--text); }
  .billing-plan-name-sm { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .billing-plan-subs { font-size: 10px; color: var(--text-muted); margin-top: 2px; }

  /* ── Loading / Empty ── */
  .loading { display: flex; align-items: center; justify-content: center; padding: 60px; gap: 12px; color: var(--text-muted); font-size: 14px; }
  .spinner { width: 20px; height: 20px; border: 2px solid var(--border-light); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .empty { padding: 48px; text-align: center; color: var(--text-muted); font-size: 14px; }

  /* ── Toast ── */
  .toast { position: fixed; top: 20px; right: 20px; z-index: 100; padding: 12px 18px; border-radius: 8px; font-size: 13px; font-weight: 500; animation: fadeIn 0.3s ease; box-shadow: 0 4px 20px #00000060; }
  .toast.success { background: #10B98120; border: 1px solid #10B981; color: #10B981; }
  .toast.error { background: #EF444420; border: 1px solid #EF4444; color: #EF4444; }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

  /* ── Modal ── */
  .modal-overlay { position: fixed; inset: 0; background: #00000080; z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  .modal { background: var(--surface); border: 1px solid var(--border-light); border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 80px #00000080; }
  .modal-header { padding: 20px 24px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .modal-title { font-size: 16px; font-weight: 600; }
  .modal-close { background: none; border: none; color: var(--text-muted); font-size: 18px; cursor: pointer; padding: 4px; transition: color 0.15s; }
  .modal-close:hover { color: var(--text); }
  .modal-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
  .modal-footer { padding: 16px 24px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; }
  .btn { padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; border: none; transition: all 0.15s; }
  .btn-primary { background: var(--accent); color: white; }
  .btn-primary:hover { background: #2563EB; }
  .btn-primary:disabled { background: var(--surface3); color: var(--text-muted); cursor: not-allowed; }
  .btn-secondary { background: var(--surface2); border: 1px solid var(--border-light); color: var(--text-muted); }
  .btn-secondary:hover { border-color: var(--accent); color: var(--text); }
  .btn-danger { background: #EF444420; border: 1px solid #EF444440; color: var(--danger); }
  .btn-danger:hover { background: var(--danger); color: white; }

  /* ── Cities table ── */
  .cities-table { width: 100%; border-collapse: collapse; }
  .cities-table th { padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--border); white-space: nowrap; }
  .cities-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .cities-table tr:hover td { background: var(--surface2); }
  .status-badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .status-badge.trialing { background: #10B98120; color: #10B981; border: 1px solid #10B98130; }
  .status-badge.active { background: #3B82F620; color: #60A5FA; border: 1px solid #3B82F630; }
  .status-badge.past_due { background: #F59E0B20; color: #F59E0B; border: 1px solid #F59E0B30; }
  .status-badge.paused { background: #64748B20; color: #64748B; border: 1px solid #64748B30; }
  .status-badge.no_trial { background: #1E2D4580; color: #475569; border: 1px solid #2A3F5F; }
  .action-btn { padding: 5px 10px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-muted); font-size: 11px; font-family: inherit; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .action-btn:hover { border-color: var(--accent); color: var(--accent-light); }

  /* ── Preview widget ── */
  /* ── Preview widget ── */
  .widget-preview { background: #080D18; border: 1px solid var(--border); border-radius: 12px; padding: 20px; margin-top: 16px; }
  .widget-preview-title { font-size: 12px; color: var(--text-muted); margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
  .widget-bubble { width: 52px; height: 52px; border-radius: 50%; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 22px; cursor: pointer; margin-left: auto; box-shadow: 0 4px 20px #3B82F640; transition: transform 0.2s; }
  .widget-bubble:hover { transform: scale(1.08); }
  .widget-chat { background: var(--surface); border: 1px solid var(--border-light); border-radius: 12px; padding: 16px; margin-bottom: 12px; font-size: 13px; line-height: 1.5; }
  .widget-chat-name { font-size: 11px; font-weight: 600; color: var(--accent-light); margin-bottom: 6px; }
`;

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState('login');
  const [user, setUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(() => {
    // Only restore impersonation if super token is still present
    const superToken = localStorage.getItem('civicbot_super_token');
    if (!superToken) {
      localStorage.removeItem('civicbot_impersonating');
      return null;
    }
    return localStorage.getItem('civicbot_impersonating') || null;
  });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Check for existing token or set-password token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const newAccount = urlParams.get('newAccount');
    const urlCityId = urlParams.get('cityId');
    const urlCityName = urlParams.get('cityName');
    const urlEmail = urlParams.get('email');

    // New account redirect from onboarding wizard
    if (newAccount === 'true' && resetToken && urlCityId) {
      const userData = { email: urlEmail, role: 'city_admin', cityId: urlCityId, cityName: urlCityName };
      localStorage.setItem('civicbot_token', resetToken);
      localStorage.setItem('civicbot_user', JSON.stringify(userData));
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      setUser(userData);
      setPage('submissions');
      return;
    }

    // Set password flow
    if (resetToken && !newAccount) {
      setPage('set-password');
      return;
    }

    // Existing session
    const token = localStorage.getItem('civicbot_token');
    const savedUser = localStorage.getItem('civicbot_user');
    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      setPage(parsed.role === 'review_admin' ? 'review' : 'submissions');
    }
  }, []);

  function handleLogin(userData) {
    setUser(userData);
    setPage(userData.role === 'review_admin' ? 'review' : 'submissions');
  }

  function handleLogout() {
    localStorage.removeItem('civicbot_token');
    localStorage.removeItem('civicbot_user');
    setUser(null);
    setPage('login');
  }

  function exitImpersonation() {
    const superToken = localStorage.getItem('civicbot_super_token');
    const superUser = localStorage.getItem('civicbot_super_user');
    localStorage.setItem('civicbot_token', superToken);
    localStorage.setItem('civicbot_user', superUser);
    localStorage.removeItem('civicbot_super_token');
    localStorage.removeItem('civicbot_super_user');
    localStorage.removeItem('civicbot_impersonating');
    setImpersonating(null);
    setUser(JSON.parse(superUser));
    setPage('cities');
  }

  // Listen for navigate events from child components
  useEffect(() => {
    const handler = (e) => setPage(e.detail);
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  if (page === 'login') {
    return (
      <>
        <style>{styles}</style>
        <LoginPage onLogin={handleLogin} showToast={showToast} onSignup={() => setPage('signup')} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  if (page === 'signup') {
    return (
      <>
        <style>{styles}</style>
        <SignupPage onLogin={handleLogin} onBack={() => setPage('login')} showToast={showToast} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  if (page === 'set-password') {
    return (
      <>
        <style>{styles}</style>
        <SetPasswordPage onComplete={handleLogin} showToast={showToast} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <Header user={user} onLogout={handleLogout} onMenuToggle={() => setSidebarOpen(o => !o)} />
        <div className="main">
          <div className={`sidebar-backdrop${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />
          <Sidebar page={page} setPage={p => { setPage(p); setSidebarOpen(false); }} user={user} open={sidebarOpen} />
          {impersonating && (
            <div style={{position:'fixed',top:0,left:0,right:0,zIndex:9999,background:'#F59E0B',color:'#000',padding:'10px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',fontWeight:600,fontSize:13,boxShadow:'0 2px 8px rgba(0,0,0,0.3)'}}>
              <span>👁 Logged in as <strong>{impersonating}</strong> — You are acting as this city admin</span>
              <button onClick={exitImpersonation} style={{background:'#000',color:'#fff',border:'none',borderRadius:4,padding:'6px 16px',cursor:'pointer',fontWeight:600,fontSize:12}}>✕ Exit to Super Admin</button>
            </div>
          )}
          <div className="content">
            {page === 'submissions' && <SubmissionsPage user={user} showToast={showToast} />}
            {page === 'analytics' && <AnalyticsPage user={user} showToast={showToast} />}
            {page === 'templates' && <TemplatesPage showToast={showToast} />}
            {page === 'settings' && <SettingsPage showToast={showToast} />}
            {page === 'billing' && <BillingPage user={user} showToast={showToast} onAnswersEnabled={() => {
              const updated = { ...user, isAnswersPlan: true };
              setUser(updated);
              localStorage.setItem('civicbot_user', JSON.stringify(updated));
            }} />}
            {page === 'embed' && <EmbedPage user={user} />}
            {page === 'team' && <TeamPage user={user} showToast={showToast} />}
            {page === 'emergency' && <EmergencyRoutingPage user={user} showToast={showToast} />}
            {page === 'review' && <ReviewQueuePage user={user} showToast={showToast} />}
            {page === 'answers' && <AnswersPage user={user} showToast={showToast} impersonating={impersonating} />}
            {page === 'cities' && <CitiesPage user={user} showToast={showToast} setImpersonating={setImpersonating} />}
            {page === 'guide' && <GuidePage />}
          </div>
        </div>
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}


// ─── Set Password Page ────────────────────────────────────────────────────────
// ─── Tooltip Component ───────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [show, setShow] = React.useState(false);
  const [pos, setPos] = React.useState({top:0, left:0});
  function handleMouseEnter(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, left: Math.min(rect.left - 120, window.innerWidth - 280) });
    setShow(true);
  }
  return (
    <span style={{position:'relative',display:'inline-flex',alignItems:'center',marginLeft:6}}>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        style={{width:16,height:16,borderRadius:'50%',background:'var(--surface2)',border:'1px solid var(--border-light)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'var(--text-muted)',cursor:'help',fontWeight:700,flexShrink:0}}
      >?</span>
      {show && (
        <div style={{position:'fixed',top:pos.top,left:pos.left,background:'#1E293B',border:'1px solid var(--border2)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text2)',lineHeight:1.5,zIndex:99999,width:260,boxShadow:'0 8px 24px rgba(0,0,0,0.4)',whiteSpace:'normal'}}>
          {text}
          <div style={{position:'absolute',bottom:'100%',left:'20px',width:0,height:0,borderLeft:'6px solid transparent',borderRight:'6px solid transparent',borderBottom:'6px solid #1E293B'}} />
        </div>
      )}
    </span>
  );
}

function SetPasswordPage({ onComplete, showToast }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  const token = new URLSearchParams(window.location.search).get('token');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password || !confirm) { setError('Please fill in both fields'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/set-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        const sessionUser = {
          email: data.email, role: data.role || 'city_admin',
          cityId: data.cityId, cityName: data.cityName,
          isAnswersPlan: data.isAnswersPlan || false, isCmmsPlan: data.isCmmsPlan === true
        };
        localStorage.setItem('civicbot_user', JSON.stringify(sessionUser));
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        showToast('Password set! Welcome to CivicBot.');
        onComplete(sessionUser);
      } else {
        if (data.error?.includes('expired') || data.error?.includes('Invalid')) {
          setTokenValid(false);
        }
        setError(data.error || 'Failed to set password');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  if (!token) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{textAlign:'center'}}>
          <div style={{fontSize:32, marginBottom:16}}>🔗</div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Invalid Link</div>
          <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:24}}>This link is invalid or missing a token.</div>
          <a href="/" style={{color:'var(--accent-light)', fontSize:13}}>← Back to login</a>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{textAlign:'center'}}>
          <div style={{fontSize:32, marginBottom:16}}>⏰</div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Link Expired</div>
          <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:24, lineHeight:1.6}}>
            This password setup link has expired (links are valid for 48 hours).<br/>
            Please use the "Forgot your password?" link to request a new one.
          </div>
          <button className="login-btn" onClick={() => { window.history.replaceState({}, '', window.location.pathname); window.location.reload(); }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#1e3a5f"/><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#3b82f6"/></svg>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">Welcome! Set your password to access your CivicBot admin dashboard.</p>
        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <label className="login-label">New Password</label>
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoFocus
            />
          </div>
          <div className="login-field">
            <label className="login-label">Confirm Password</label>
            <input
              className="login-input"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
            />
          </div>
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--text-muted)', marginBottom:8, lineHeight:1.6}}>
            🔒 Use at least 8 characters. Your password is encrypted and never stored in plain text.
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Setting password...' : 'Set Password & Sign In →'}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
      </div>
    </div>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
// ─── Signup Page ───────────────────────────────────────────────────────────────
function SignupPage({ onLogin, onBack, showToast }) {
  const [form, setForm] = useState({ cityName: '', email: '', password: '', state: '', deliveryType: 'email' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const US_STATES = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ];

  async function handleSignup(e) {
    e.preventDefault();
    if (!form.cityName || !form.email || !form.password || !form.state) { setError('All fields are required'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cityName: form.cityName, email: form.email, password: form.password, state: form.state, isCityworks: form.deliveryType === 'cityworks' }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        localStorage.setItem('civicbot_user', JSON.stringify({ email: data.email, role: 'city_admin', cityId: data.cityId, cityName: data.cityName, isAnswersPlan: false }));
        showToast('Welcome to CivicBot! Your 30-day trial has started.');
        onLogin({ email: data.email, role: 'city_admin', cityId: data.cityId, cityName: data.cityName, isAnswersPlan: false });
      } else {
        setError(data.error || 'Signup failed — try again');
      }
    } catch {
      setError('Cannot connect to CivicBot server');
    }
    setLoading(false);
  }

  return (
    <div className="login-wrap">
      <div className="login-card" style={{maxWidth:460}}>
        <div className="login-logo">
          <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#1e3a5f"/><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#3b82f6"/></svg>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">Start your 30-day free trial — no credit card required.</p>
        <form onSubmit={handleSignup}>
          <div className="login-field">
            <label className="login-label">City / Organization Name</label>
            <input className="login-input" value={form.cityName} onChange={e => setForm(f=>({...f,cityName:e.target.value}))} placeholder="City of Springfield" autoFocus />
          </div>
          <div className="login-field">
            <label className="login-label">State</label>
            <select className="login-input" value={form.state} onChange={e => setForm(f=>({...f,state:e.target.value}))} style={{cursor:'pointer'}}>
              <option value="">Select state...</option>
              {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
          <div className="login-field">
            <label className="login-label">Admin Email</label>
            <input className="login-input" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="admin@yourcity.gov" />
          </div>
          <div className="login-field">
            <label className="login-label">Password</label>
            <input className="login-input" type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="Min. 8 characters" />
          </div>
          <div className="login-field">
            <label className="login-label">How will you receive service requests?</label>
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button type="button" onClick={() => setForm(f=>({...f,deliveryType:'email'}))} style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${form.deliveryType==='email'?'var(--accent)':'var(--border)'}`,background:form.deliveryType==='email'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'center'}}>
                <div style={{fontSize:18,marginBottom:2}}>📧</div>
                <div style={{fontSize:12,fontWeight:600,color:form.deliveryType==='email'?'var(--accent-light)':'var(--text)'}}>Email</div>
              </button>
              <button type="button" onClick={() => setForm(f=>({...f,deliveryType:'cityworks'}))} style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${form.deliveryType==='cityworks'?'var(--accent)':'var(--border)'}`,background:form.deliveryType==='cityworks'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'center'}}>
                <div style={{fontSize:18,marginBottom:2}}>🔗</div>
                <div style={{fontSize:12,fontWeight:600,color:form.deliveryType==='cityworks'?'var(--accent-light)':'var(--text)'}}>Cityworks</div>
              </button>
            </div>
          </div>
          <button className="login-btn" type="submit" disabled={loading} style={{marginTop:8}}>
            {loading ? 'Creating account...' : 'Start Free Trial →'}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
        <div className="login-forgot" style={{textAlign:'center',marginTop:16}}>
          <a onClick={onBack} style={{cursor:'pointer'}}>← Back to Sign In</a>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ onLogin, showToast, onSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    try {
      if (sessionStorage.getItem('civicbot_session_expired')) {
        sessionStorage.removeItem('civicbot_session_expired');
        return 'Your session expired. Please sign in again.';
      }
    } catch (e) {}
    return '';
  });
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        localStorage.setItem('civicbot_user', JSON.stringify({ email: data.email, role: data.role, cityId: data.cityId, cityName: data.cityName, isAnswersPlan: data.isAnswersPlan || false, isCmmsPlan: data.isCmmsPlan === true }));
        onLogin({ email: data.email, role: data.role, cityId: data.cityId, cityName: data.cityName, isAnswersPlan: data.isAnswersPlan || false, isCmmsPlan: data.isCmmsPlan === true });
        if (data.cityId) {
          fetch(API_BASE + '/api/config/' + data.cityId).then(r=>r.json()).then(cfg => {
            if (cfg.success && cfg.config && cfg.config.logoUrl) {
              const u = JSON.parse(localStorage.getItem('civicbot_user')||'{}');
              u.cityLogo = cfg.config.logoUrl;
              localStorage.setItem('civicbot_user', JSON.stringify(u));
            }
          }).catch(()=>{});
        }
        showToast('Welcome back!');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Cannot connect to CivicBot server');
    }
    setLoading(false);
  }

  async function handleForgot(e) {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true); setError('');
    try {
      await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setForgotSent(true);
    } catch {
      setError('Could not send reset email');
    }
    setLoading(false);
  }

  if (forgotSent) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <div className="login-logo">
            <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#1e3a5f"/><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#3b82f6"/></svg>
            <div className="login-logo-text">Civic<span>Bot</span></div>
          </div>
          <div style={{textAlign:'center', padding:'20px 0'}}>
            <div style={{fontSize:32, marginBottom:16}}>📧</div>
            <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Check your inbox</div>
            <div style={{fontSize:13, color:'var(--text-muted)', lineHeight:1.6}}>We sent a password reset link to <strong>{email}</strong>. Click the link in the email to set your password.</div>
            <button className="login-btn" style={{marginTop:24}} onClick={() => { setForgotMode(false); setForgotSent(false); }}>Back to Sign In</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#1e3a5f"/><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#3b82f6"/></svg>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">
          {forgotMode ? 'Enter your email to receive a password reset link.' : 'Admin portal for managing your city\'s AI-powered service request chatbot.'}
        </p>
        <form onSubmit={forgotMode ? handleForgot : handleLogin}>
          <div className="login-field">
            <label className="login-label">Email</label>
            <input className="login-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@yourcity.gov" autoFocus />
          </div>
          {!forgotMode && (
            <div className="login-field">
              <label className="login-label">Password</label>
              <input className="login-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
          )}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? (forgotMode ? 'Sending...' : 'Signing in...') : (forgotMode ? 'Send Reset Link' : 'Sign In')}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
        <div className="login-forgot">
          {forgotMode
            ? <a onClick={() => { setForgotMode(false); setError(''); }}>← Back to Sign In</a>
            : <a onClick={() => { setForgotMode(true); setError(''); }}>Forgot your password?</a>
          }
        </div>
        {!forgotMode && onSignup && (
          <div style={{textAlign:'center', marginTop:20, fontSize:13, color:'var(--text-muted)'}}>
            New to CivicBot?{' '}
            <a onClick={onSignup} style={{color:'var(--accent-light)', cursor:'pointer', fontWeight:600}}>Start free trial →</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────────
function Header({ user, onLogout, onMenuToggle }) {
  const [showChangePwd, setShowChangePwd] = React.useState(false);
  const [pwdForm, setPwdForm] = React.useState({ current: '', next: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = React.useState('');
  const [pwdSaving, setPwdSaving] = React.useState(false);

  async function goToSalesPortal() {
    try {
      const r = await apiFetch('/api/sales/auth/admin-sso');
      const data = await r.json();
      if (data.token) {
        window.open(`https://sales.civicbot.centricitygis.com?sso=${encodeURIComponent(data.token)}`, '_blank');
      }
    } catch (e) { alert('Could not open Sales Portal: ' + e.message); }
  }

  async function handleChangePwd(e) {
    e.preventDefault();
    setPwdMsg('');
    if (pwdForm.next !== pwdForm.confirm) { setPwdMsg('New passwords do not match'); return; }
    if (pwdForm.next.length < 8) { setPwdMsg('Password must be at least 8 characters'); return; }
    setPwdSaving(true);
    try {
      const r = await apiFetch('/api/auth/change-password', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ currentPassword: pwdForm.current, newPassword: pwdForm.next }) });
      const data = await r.json();
      if (data.success) { setPwdMsg('✅ Password updated successfully'); setPwdForm({ current: '', next: '', confirm: '' }); setTimeout(() => setShowChangePwd(false), 1500); }
      else setPwdMsg(data.error || 'Failed to update password');
    } catch { setPwdMsg('Network error'); }
    setPwdSaving(false);
  }

  return (
    <>
    <div className="header">
      <button className="hamburger" onClick={onMenuToggle} aria-label="Open menu">
        <span /><span /><span />
      </button>
      <div className="header-logo">
        <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" style={{flexShrink:0}}><rect width="36" height="36" rx="8" fill="rgba(255,255,255,0.15)"/><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#93c5fd"/></svg>
        CivicBot Admin
        {user?.cityName && <span style={{fontSize:12, color:'var(--text-muted)', fontWeight:400, marginLeft:8}}>— {user.cityName}</span>}
      </div>
      <div className="header-right">
        <div className="header-status">
          <div className="status-dot" />
          {user?.role === 'super_admin' ? 'Centricity GIS · Super Admin' : 'Connected'}
        </div>
        <span style={{fontSize:12, color:'var(--text-muted)'}}>{user?.email}</span>
        <button className="logout-btn" onClick={() => setShowChangePwd(true)} title="Change your password">🔑</button>
        {user?.role === 'super_admin' && (
          <button onClick={goToSalesPortal} style={{padding:'5px 12px',borderRadius:6,border:'1px solid rgba(255,255,255,0.3)',background:'rgba(255,255,255,0.12)',color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',marginRight:4}}>
            Sales Portal →
          </button>
        )}
        <button className="logout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </div>

    {showChangePwd && (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowChangePwd(false)}>
        <div className="modal" style={{maxWidth:400}}>
          <div className="modal-header">
            <div className="modal-title">Change Password</div>
            <button className="modal-close" onClick={() => setShowChangePwd(false)}>✕</button>
          </div>
          <form onSubmit={handleChangePwd} style={{padding:'20px 24px'}}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input type="password" className="form-input" value={pwdForm.current} onChange={e => setPwdForm(f => ({...f, current: e.target.value}))} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input type="password" className="form-input" value={pwdForm.next} onChange={e => setPwdForm(f => ({...f, next: e.target.value}))} required minLength={8} placeholder="Min. 8 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input type="password" className="form-input" value={pwdForm.confirm} onChange={e => setPwdForm(f => ({...f, confirm: e.target.value}))} required />
            </div>
            {pwdMsg && <div style={{padding:'8px 12px',borderRadius:6,fontSize:13,background:pwdMsg.startsWith('✅')?'#10B98120':'#EF444420',color:pwdMsg.startsWith('✅')?'#10B981':'#EF4444',border:`1px solid ${pwdMsg.startsWith('✅')?'#10B981':'#EF4444'}`}} style={{marginBottom:12}}>{pwdMsg}</div>}
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowChangePwd(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={pwdSaving}>{pwdSaving ? 'Saving…' : 'Update Password'}</button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}

// ─── User Guide (in-app) ─────────────────────────────────────────────────────
// The guide now lives inside the admin SPA so it's naturally gated by the admin
// login — no second login, no cross-origin localStorage check (the old guide.html
// gate read the wrong origin's storage and was unreachable for everyone). Rendered
// in a srcDoc iframe so the guide's own CSS can't collide with the admin styles.
function GuidePage() {
  return (
    <div style={{ height: 'calc(100vh - 60px)', margin: -24, background: '#fff' }}>
      <iframe
        title="CivicBot User Guide"
        srcDoc={GUIDE_HTML}
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    </div>
  );
}

// ─── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ page, setPage, user, open }) {
  // review_admin accounts see ONLY the Review Queue — the backend enforces the same
  // restriction (deny-by-default allowlist), this just keeps the UI honest about it.
  const cityItems = user?.role === 'review_admin' ? [
    { id: 'review', icon: '📥', label: 'Review Queue' },
  ] : [
    { id: 'submissions', icon: '📋', label: 'Submissions' },
    { id: 'analytics', icon: '📊', label: 'Analytics' },
    { id: 'templates', icon: '☑️', label: 'SR Templates' },
    // Review Queue requires a CMMS backend (Cityworks today; future: other CMMS systems).
    // Show when isCmmsPlan is true OR undefined (old session without the flag — page itself handles email-only gracefully).
    ...(user?.isCmmsPlan !== false ? [{ id: 'review', icon: '📥', label: 'Review Queue' }] : []),
    { id: 'emergency', icon: '🚨', label: 'Emergency Routing' },
    { id: 'embed', icon: '🔗', label: 'Embed Code' },
    { id: 'billing', icon: '💳', label: 'Billing' },
    { id: 'team', icon: '👥', label: 'Team' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
    ...(user?.isAnswersPlan ? [{ id: 'answers', icon: '🤖', label: 'CivicBot Answers' }] : []),
    { id: 'guide', icon: '📖', label: 'User Guide' },
  ];
  const adminItems = [
    { id: 'cities', icon: '🏙️', label: 'All Cities' },
  ];
  return (
    <div className={`sidebar${open ? ' open' : ''}`}>
      {user?.role === 'super_admin' && <>
        <div className="sidebar-label">Centricity Admin</div>
        {adminItems.map(item => (
          <div key={item.id} className={`sidebar-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
      </>}
      <div className="sidebar-label">Navigation</div>
      {cityItems.map(item => (
        item.href ? (
          <a key={item.id} href={item.href} target="_blank" rel="noopener noreferrer" className="sidebar-item" style={{textDecoration:'none'}}>
            <span>{item.icon}</span>
            <span>{item.label}</span>
            <span style={{marginLeft:'auto',fontSize:11,opacity:0.5}}>↗</span>
          </a>
        ) : (
          <div key={item.id} className={`sidebar-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        )
      ))}
    </div>
  );
}

// ─── Submissions Page ──────────────────────────────────────────────────────────

// ─── ANALYTICS PAGE ───────────────────────────────────────────────────────────
function AnalyticsPage({ user, showToast }) {
  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [usage, setUsage] = React.useState(null);
  const [dateRange, setDateRange] = React.useState('month');
  const [emergencyStats, setEmergencyStats] = React.useState(null);

  const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#84CC16','#F97316'];

  const cityId = user?.cityId;

  function loadEmergencyStats() {
    if (!cityId) return;
    apiFetch('/api/admin/emergency-stats/' + cityId)
      .then(r => r.json())
      .then(data => { if (data.success) setEmergencyStats(data); })
      .catch(() => {});
  }

  React.useEffect(() => {
    if (!cityId) return;
    apiFetch('/api/submissions?cityId=' + cityId)
      .then(r => r.json())
      .then(data => { if (data.success) setSubmissions(data.submissions || []); })
      .catch(() => showToast('Failed to load analytics', 'error'))
      .finally(() => setLoading(false));
    apiFetch('/api/usage/' + cityId)
      .then(r => r.json())
      .then(data => { if (data.success) setUsage(data); })
      .catch(() => {});
    loadEmergencyStats();
  }, [user]);

  async function deleteDeflection(id) {
    if (!window.confirm('Delete this emergency deflection record? This will remove it from your analytics counts.')) return;
    try {
      const r = await apiFetch('/api/admin/emergency-deflections/' + id, { method: 'DELETE' });
      const data = await r.json();
      if (data.success) {
        showToast('Deflection record deleted', 'success');
        loadEmergencyStats();
      } else {
        showToast('Delete failed', 'error');
      }
    } catch {
      showToast('Delete failed', 'error');
    }
  }

  function getFilteredSubs() {
    const now = new Date();
    return submissions.filter(s => {
      if (!s.submitted_at) return false;
      const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
      if (dateRange === 'week') { const start = new Date(now); start.setDate(now.getDate() - 7); return d >= start; }
      if (dateRange === 'month') { const start = new Date(now.getFullYear(), now.getMonth(), 1); return d >= start; }
      if (dateRange === 'year') { return d.getFullYear() === now.getFullYear(); }
      return true;
    });
  }

  function getByType(subs) {
    const counts = {};
    subs.forEach(s => { const t = s.sr_type || 'Unknown'; counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a,b) => b.count - a.count);
  }

  function getByDay(subs) {
    const counts = {};
    subs.forEach(s => {
      if (!s.submitted_at) return;
      const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
      const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([date, count]) => ({ date, count }));
  }

  function getByDelivery(subs) {
    const email = subs.filter(s => s.delivery_method === 'email').length;
    const cw = subs.filter(s => s.delivery_method === 'cityworks').length;
    return [{ name: 'Email', count: email }, { name: 'Cityworks', count: cw }].filter(d => d.count > 0);
  }

  function MiniPie({ data }) {
    if (!data || data.length === 0) return <div style={{textAlign:'center',padding:'20px',color:'var(--text-muted)',fontSize:13}}>No data</div>;
    const total = data.reduce((s,d) => s + d.count, 0);
    let angle = 0;
    const size = 140, cx = 70, cy = 70, r = 55;
    const toRad = deg => (deg - 90) * Math.PI / 180;
    const slices = data.map((d, i) => {
      const pct = d.count / total;
      const start = angle;
      angle += pct * 360;
      const end = angle;
      if (end - start >= 360) { return <circle key={i} cx={cx} cy={cy} r={r} fill={COLORS[i % COLORS.length]} />; }
      const x1 = cx + r * Math.cos(toRad(start)), y1 = cy + r * Math.sin(toRad(start));
      const x2 = cx + r * Math.cos(toRad(end)), y2 = cy + r * Math.sin(toRad(end));
      const large = end - start > 180 ? 1 : 0;
      return <path key={i} d={"M "+cx+" "+cy+" L "+x1+" "+y1+" A "+r+" "+r+" 0 "+large+" 1 "+x2+" "+y2+" Z"} fill={COLORS[i % COLORS.length]} />;
    });
    return (
      <div style={{display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
        <svg width={size} height={size}>{slices}</svg>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {data.slice(0,6).map((d,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <div style={{width:10,height:10,borderRadius:2,background:COLORS[i % COLORS.length],flexShrink:0}} />
              <span style={{color:'var(--text-muted)'}}>{d.name}</span>
              <span style={{fontWeight:600,marginLeft:'auto',paddingLeft:8}}>{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function BarChart({ data }) {
    if (!data || data.length === 0) return <div style={{textAlign:'center',padding:'20px',color:'var(--text-muted)',fontSize:13}}>No data</div>;
    const max = Math.max(...data.map(d => d.count), 1);
    return (
      <div style={{display:'flex',alignItems:'flex-end',gap:4,height:80,paddingTop:8}}>
        {data.slice(-14).map((d,i) => (
          <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
            <div style={{width:'100%',background:'#3B82F6',borderRadius:'2px 2px 0 0',height: Math.max((d.count/max)*72, 2) +'px',opacity:0.85}} title={d.date+': '+d.count} />
            <span style={{fontSize:9,color:'var(--text-muted)',transform:'rotate(-45deg)',transformOrigin:'center',whiteSpace:'nowrap'}}>{d.date}</span>
          </div>
        ))}
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading analytics...</div>;

  const filtered = getFilteredSubs();
  const byType = getByType(filtered);
  const byDay = getByDay(filtered);
  const byDelivery = getByDelivery(filtered);
  const total = filtered.length;
  const allTime = submissions.length;
  const thisMonth = submissions.filter(s => {
    if (!s.submitted_at) return false;
    const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const topType = byType[0]?.name || '—';
  const photoCount = filtered.filter(s => s.photo_attached).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Analytics</div>
          <div className="page-subtitle">Service request insights for {user?.cityName}</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          {['week','month','year','all'].map(r => (
            <button key={r} onClick={() => setDateRange(r)}
              style={{padding:'6px 14px',borderRadius:6,border:'1px solid var(--border)',background: dateRange===r ? '#3B82F6' : 'var(--surface)',color: dateRange===r ? '#fff' : 'var(--text)',cursor:'pointer',fontSize:13,fontWeight: dateRange===r ? 600 : 400}}>
              {r === 'all' ? 'All Time' : r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Emergency Deflection Card */}
      {emergencyStats && emergencyStats.records && emergencyStats.records.length > 0 && (
        <div style={{background:'linear-gradient(135deg,#2A0F0F 0%,#1A2235 100%)',border:'1px solid #DC262640',borderRadius:12,padding:'18px 22px',marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:24,flexWrap:'wrap',marginBottom: emergencyStats.records.length > 0 ? 14 : 0}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{fontSize:38}}>🚨</div>
              <div>
                <div style={{fontSize:34,fontWeight:800,color:'#FCA5A5',lineHeight:1}}>{emergencyStats.thisMonth}</div>
                <div style={{fontSize:11,color:'#F87171',textTransform:'uppercase',letterSpacing:0.5,fontWeight:600,marginTop:2}}>Emergencies Deflected to Phone</div>
                <div style={{fontSize:11,color:'#9CA3AF',marginTop:2}}>this month — routed to a live human instead of the form</div>
              </div>
            </div>
            {emergencyStats.byCategory && emergencyStats.byCategory.length > 0 && (
              <div style={{flex:1,minWidth:200,marginLeft:'auto'}}>
                <div style={{fontSize:10,fontWeight:700,color:'#F87171',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>Top Categories This Month</div>
                {emergencyStats.byCategory.slice(0,4).map((c, i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#CBD5E1',padding:'2px 0'}}>
                    <span>{c.template_name || '(unknown)'}</span>
                    <span style={{fontWeight:700,color:'#FCA5A5'}}>{c.cnt}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Individual deflection records with delete */}
          <div style={{borderTop:'1px solid #3F1515',paddingTop:12}}>
            <div style={{fontSize:10,fontWeight:700,color:'#F87171',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>All Records (most recent first)</div>
            <div style={{maxHeight:200,overflowY:'auto'}}>
              {emergencyStats.records.map(r => {
                const tz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                const ts = new Date(r.created_at.includes('T') ? r.created_at : r.created_at.replace(' ','T')+'Z');
                const label = ts.toLocaleString('en-US',{timeZone:tz,month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'});
                return (
                  <div key={r.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 8px',borderRadius:6,marginBottom:3,background:'#1F0C0C80',gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <span style={{fontSize:12,color:'#FCA5A5',fontWeight:600}}>{r.template_name || '(unknown)'}</span>
                      {r.matched_text && <span style={{fontSize:11,color:'#94A3B8',marginLeft:8}}>"{r.matched_text}"</span>}
                    </div>
                    <span style={{fontSize:11,color:'#64748B',whiteSpace:'nowrap'}}>{label}</span>
                    <button
                      onClick={() => deleteDeflection(r.id)}
                      title="Delete this record"
                      style={{background:'none',border:'none',cursor:'pointer',color:'#64748B',fontSize:15,padding:'0 4px',lineHeight:1,flexShrink:0}}
                    >🗑</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:16,marginBottom:24}}>
        {[
          { label: 'This Period', value: total, icon: '📋', color: '#3B82F6' },
          { label: 'This Month', value: thisMonth, icon: '📅', color: '#10B981' },
          { label: 'All Time', value: allTime, icon: '🏆', color: '#8B5CF6' },
          { label: 'Top Issue', value: topType, icon: '🔝', color: '#F59E0B', small: true },
          { label: 'With Photos', value: photoCount, icon: '📷', color: '#06B6D4' },
        ].map((card, i) => (
          <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'16px 20px',borderTop:'3px solid '+card.color}}>
            <div style={{fontSize:20,marginBottom:6}}>{card.icon}</div>
            <div style={{fontSize: card.small ? 14 : 28, fontWeight:700, color:'var(--text)', lineHeight:1.1, marginBottom:4}}>{card.value}</div>
            <div style={{fontSize:12,color:'var(--text-muted)',fontWeight:500}}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Submissions Over Time</div>
          <BarChart data={byDay} />
        </div>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Delivery Method</div>
          <MiniPie data={byDelivery} />
        </div>
      </div>

      {/* Issue Types */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20,marginBottom:24}}>
        <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Issues by Type</div>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:16,alignItems:'center'}}>
          <MiniPie data={byType.slice(0,8)} />
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {byType.slice(0,10).map((t,i) => {
              const pct = total > 0 ? Math.round(t.count/total*100) : 0;
              return (
                <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:10,height:10,borderRadius:2,background:COLORS[i % COLORS.length],flexShrink:0}} />
                  <span style={{fontSize:13,flex:1,color:'var(--text)'}}>{t.name}</span>
                  <div style={{width:120,height:6,background:'var(--surface2)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{width:pct+'%',height:'100%',background:COLORS[i % COLORS.length],borderRadius:3}} />
                  </div>
                  <span style={{fontSize:12,color:'var(--text-muted)',width:30,textAlign:'right'}}>{t.count}</span>
                  <span style={{fontSize:11,color:'var(--text-muted)',width:32,textAlign:'right'}}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Usage / Plan */}
      {usage && (
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Plan Usage — {new Date().toLocaleString('en-US',{month:'long',year:'numeric'})}</div>
          <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <span style={{fontSize:13,color:'var(--text-muted)'}}>Submissions used</span>
                <span style={{fontSize:13,fontWeight:600}}>{usage.monthCount || 0} / {usage.limit || '—'}</span>
              </div>
              <div style={{height:8,background:'var(--surface2)',borderRadius:4,overflow:'hidden'}}>
                <div style={{
                  height:'100%',
                  borderRadius:4,
                  background: (usage.monthCount||0)/(usage.limit||1) > 0.9 ? '#EF4444' : (usage.monthCount||0)/(usage.limit||1) > 0.7 ? '#F59E0B' : '#10B981',
                  width: Math.min(100, Math.round((usage.monthCount||0)/(usage.limit||1)*100)) + '%'
                }} />
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:12,color:'var(--text-muted)'}}>Plan</div>
              <div style={{fontWeight:600,fontSize:14}}>{usage.planName || '—'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function SubmissionsPage({ user, showToast }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [monthCount, setMonthCount] = useState(0);
  const [usage, setUsage] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [adminDashboard, setAdminDashboard] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  function getWeekStart() { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0,10); }
  function getToday() { return new Date().toISOString().slice(0,10); }
  const [dateFrom, setDateFrom] = useState(getWeekStart);
  const [dateTo, setDateTo] = useState(getToday);
  const [descSearch, setDescSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('week');
  const [doneFilter, setDoneFilter] = useState('ALL');

  useEffect(() => {
    const cityId = user?.cityId;
    // Super admin — load global dashboard instead
    if (!cityId && user?.role === 'super_admin') {
      apiFetch('/api/admin/submissions/all')
        .then(r => r.json())
        .then(data => { if (data.success) setAdminDashboard(data); })
        .catch(() => showToast('Failed to load dashboard', 'error'))
        .finally(() => setLoading(false));
      return;
    }
    if (!cityId) return;

    apiFetch(`/api/submissions?cityId=${cityId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setSubmissions(data.submissions);
          setTotal(data.total);
          setMonthCount(data.monthCount);
        } else {
          showToast('Failed to load submissions', 'error');
        }
      })
      .catch(() => showToast('Failed to load submissions', 'error'))
      .finally(() => setLoading(false));

    // Fetch usage metering
    if (cityId) {
      apiFetch(`/api/usage/${cityId}`)
        .then(r => r.json())
        .then(data => { if (data.success) setUsage(data); })
        .catch(() => {});
    }
  }, [user, showToast]);

  const filtered = submissions.filter(s => {
    const matchSearch = !search ||
      (s.confirmation_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.sr_type || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.address || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.caller_name || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'ALL' || s.delivery_method === filter.toLowerCase();
    const matchDateFrom = !dateFrom || (s.submitted_at || '') >= dateFrom;
    const matchDateTo = !dateTo || (s.submitted_at || '') <= dateTo + 'T23:59:59';
    const matchDesc = !descSearch || (s.description || '').toLowerCase().includes(descSearch.toLowerCase()) || (s.internal_notes || '').toLowerCase().includes(descSearch.toLowerCase());
    const matchDone = doneFilter === 'ALL' || (doneFilter === 'DONE' && !!s.completed) || (doneFilter === 'NOT_DONE' && !s.completed);
    return matchSearch && matchFilter && matchDateFrom && matchDateTo && matchDesc && matchDone;
  });

  function formatDate(dt) {
    if (!dt) return '—';
    const normalized = dt.includes('T') ? dt : dt.replace(' ', 'T') + 'Z';
    return new Date(normalized).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function exportCSV() {
    const headers = ['Confirmation #','SR Type','Address','Submitted By','Phone','Email','Delivery','Date','Completed','Notes'];
    const rows = filtered.map(function(s) {
      return [
        s.confirmation_number || '', s.sr_type || '',
        s.geo_address || s.address || '', s.caller_name || '',
        s.caller_phone || '', s.caller_email || '',
        s.delivery_method || '',
        s.submitted_at ? formatDate(s.submitted_at) : '',
        s.completed ? 'Yes' : 'No', s.internal_notes || ''
      ];
    });
    const nl = String.fromCharCode(10);
    const csv = [headers].concat(rows).map(function(r) {
      return r.map(function(v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
    }).join(nl);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'submissions-' + new Date().toISOString().slice(0,10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function applyQuickFilter(val) {
    setQuickFilter(val);
    const today = new Date();
    const pad = d => d.toISOString().slice(0,10);
    if (val === 'today') { setDateFrom(pad(today)); setDateTo(pad(today)); }
    else if (val === 'week') { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); setDateFrom(pad(s)); setDateTo(pad(today)); }
    else if (val === 'month') { const s = new Date(today.getFullYear(), today.getMonth(), 1); setDateFrom(pad(s)); setDateTo(pad(today)); }
    else if (val === 'year') { setDateFrom(today.getFullYear() + '-01-01'); setDateTo(pad(today)); }
    else if (val === 'all') { setDateFrom(''); setDateTo(''); }
  }

  async function updateSubmission(id, updates) {
    setSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    try {
      await apiFetch(`/api/submissions/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
    } catch { showToast('Failed to save', 'error'); }
  }

  async function deleteSubmission(s) {
    if (!window.confirm(`Delete submission ${s.confirmation_number || ''}? This removes it from your CivicBot list. It does NOT delete any record already sent to your CMMS — manage that there.`)) return;
    const prev = submissions;
    setSubmissions(list => list.filter(x => x.id !== s.id));
    try {
      const r = await apiFetch(`/api/submissions/${s.id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!d.success) { setSubmissions(prev); showToast(d.error || 'Delete failed', 'error'); }
      else showToast('Submission deleted');
    } catch { setSubmissions(prev); showToast('Delete failed', 'error'); }
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading submissions...</div>;

  // Super Admin Global Dashboard
  if (user?.role === 'super_admin' && !user?.cityId) {
    if (!adminDashboard) return <div className="loading"><div className="spinner" />Loading dashboard...</div>;
    const { stats, byCity, submissions: allSubs } = adminDashboard;
    const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#84CC16','#F97316'];

    const PieChart = ({ data, title }) => {
      if (!data || data.length === 0) return (
        <div style={{textAlign:'center',padding:'20px 0'}}>
          <div style={{fontSize:13,color:'var(--text-muted)'}}>No data</div>
        </div>
      );
      const total = data.reduce((s, d) => s + d.count, 0);
      let angle = 0;
      const slices = data.map((d, i) => {
        const pct = d.count / total;
        const startAngle = angle;
        angle += pct * 360;
        return { ...d, pct, startAngle, endAngle: angle, color: COLORS[i % COLORS.length] };
      });
      const size = 160;
      const cx = size / 2, cy = size / 2, r = 68;
      const toRad = deg => (deg - 90) * Math.PI / 180;
      const arc = (start, end, color) => {
        if (end - start >= 360) end = 359.99;
        const x1 = cx + r * Math.cos(toRad(start));
        const y1 = cy + r * Math.sin(toRad(start));
        const x2 = cx + r * Math.cos(toRad(end));
        const y2 = cy + r * Math.sin(toRad(end));
        const large = end - start > 180 ? 1 : 0;
        return <path key={color+start} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={color} />;
      };
      return (
        <div>
          <div style={{fontSize:12,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,marginBottom:8}}>{title}</div>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <svg width={size} height={size} style={{flexShrink:0}}>
              {slices.map(s => arc(s.startAngle, s.endAngle, s.color))}
              <circle cx={cx} cy={cy} r={38} fill="var(--surface)" />
              <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" style={{fontSize:14,fontWeight:700,fill:'var(--text)'}}>{total}</text>
            </svg>
            <div style={{fontSize:11,lineHeight:1.8}}>
              {slices.map((s,i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:6}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0}} />
                  <span style={{color:'var(--text-muted)'}}>{s.city_name}</span>
                  <span style={{color:'var(--text)',fontWeight:600,marginLeft:'auto',paddingLeft:8}}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const filteredSubs = allSubs.filter(s =>
      !adminSearch ||
      (s.city_name||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.confirmation_number||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.sr_type||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.address||'').toLowerCase().includes(adminSearch.toLowerCase())
    );

    return (
      <div>
        <div className="page-header">
          <div className="page-title">Global Submissions Dashboard</div>
          <div className="page-subtitle">All service requests across all cities</div>
        </div>

        {/* Stats Row */}
        <div className="stats-row" style={{marginBottom:24}}>
          <div className="stat-card"><div className="stat-label">Today</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{stats.today}</div><div className="stat-sub">submissions</div></div>
          <div className="stat-card"><div className="stat-label">This Week</div><div className="stat-value" style={{color:'var(--success)'}}>{stats.week}</div><div className="stat-sub">last 7 days</div></div>
          <div className="stat-card"><div className="stat-label">This Month</div><div className="stat-value" style={{color:'#F59E0B'}}>{stats.month}</div><div className="stat-sub">{new Date().toLocaleString('default',{month:'long'})}</div></div>
          <div className="stat-card"><div className="stat-label">This Year</div><div className="stat-value">{stats.year}</div><div className="stat-sub">{new Date().getFullYear()}</div></div>
        </div>

        {/* Pie Charts */}
        <div className="card" style={{marginBottom:24}}>
          <div className="card-header"><div className="card-title">Submissions by City</div></div>
          <div className="card-body">
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:24}}>
              <PieChart data={byCity.today} title="Today" />
              <PieChart data={byCity.week} title="This Week" />
              <PieChart data={byCity.month} title="This Month" />
              <PieChart data={byCity.year} title="This Year" />
            </div>
          </div>
        </div>

        {/* All Submissions Table */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">All Submissions</div>
            <input className="search-input" placeholder="Search by city, type, address..." value={adminSearch} onChange={e => setAdminSearch(e.target.value)} style={{width:280}} />
          </div>
          <div style={{overflowX:'auto'}}>
            {filteredSubs.length === 0 ? (
              <div className="empty">No submissions found.</div>
            ) : (
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>City</th>
                    <th>Confirmation #</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Submitted By</th>
                    <th>Delivery</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map(s => (
                    <tr key={s.id}>
                      <td style={{fontWeight:600,fontSize:12}}>{s.city_name}</td>
                      <td style={{fontFamily:'monospace',fontSize:12,color:'var(--accent-light)'}}>{String(s.confirmation_number || '').replace(/\.0+$/, '')}{s.cityworks_sr_id ? <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'monospace',marginTop:2}}>CMMS #{String(s.cityworks_sr_id).replace(/\.0+$/, '')}</div> : null}</td>
                      <td style={{fontSize:12}}>{s.sr_type}</td>
                      <td style={{fontSize:12,color:'var(--text-muted)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.address}</td>
                      <td style={{fontSize:12}}>{s.caller_name || 'Anonymous'}</td>
                      <td>{s.review_status==='pending'
                        ? <span className="delivery-badge" style={{background:'#F59E0B20',color:'#F59E0B',border:'1px solid #F59E0B40'}}>📥 Pending</span>
                        : s.review_status==='rejected'
                        ? <span className="delivery-badge" style={{background:'#EF444420',color:'#EF4444',border:'1px solid #EF444440'}}>✕ Rejected</span>
                        : <span className={'delivery-badge ' + (s.delivery_method==='email'?'email':'cityworks')}>{s.delivery_method==='email'?'📧 Email':'🔗 CMMS'}</span>}</td>
                      <td style={{fontSize:11,color:'var(--text-muted)'}}>{formatDate(s.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Submissions</div>
        <div className="page-subtitle">All service requests submitted through CivicBot</div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">This Month</div>
          <div className="stat-value" style={{color:'var(--accent-light)'}}>{monthCount}</div>
          <div className="stat-sub">Submissions in {new Date().toLocaleString('default', {month:'long'})}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Submissions</div>
          <div className="stat-value">{total}</div>
          <div className="stat-sub">All time</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Delivery Methods</div>
          <div className="stat-value" style={{color:'var(--success)', fontSize:18, marginTop:10}}>
            📧 {submissions.filter(s => s.delivery_method === 'email').length} &nbsp;
            🔗 {submissions.filter(s => s.delivery_method === 'cityworks').length}
          </div>
          <div className="stat-sub">Email · Cityworks</div>
        </div>
      </div>

      {usage && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header">
            <div className="card-title">📊 Monthly Usage</div>
            <span style={{fontSize:12, color:'var(--text-muted)'}}>{new Date().toLocaleString('default', {month:'long', year:'numeric'})}</span>
          </div>
          <div className="card-body">
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
              <span style={{fontSize:13, color:'var(--text)'}}>
                <strong style={{fontSize:20, color: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--accent-light)'}}>{usage.monthCount}</strong>
                <span style={{color:'var(--text-muted)'}}> of {usage.limit} submissions used</span>
              </span>
              <span style={{fontSize:13, fontWeight:600, color: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--success)'}}>
                {usage.percent}%
              </span>
            </div>
            <div style={{background:'var(--surface2)', borderRadius:8, height:14, overflow:'hidden', marginBottom:10}}>
              <div style={{
                height:'100%',
                width:`${Math.min(usage.percent, 100)}%`,
                borderRadius:8,
                background: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--accent)',
                transition:'width 0.4s ease'
              }} />
            </div>
            <div style={{display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text-muted)'}}>
              <span>{usage.remaining} submissions remaining this month</span>
              {usage.percent >= 80 && usage.percent < 100 && <span style={{color:'var(--warning)'}}>⚠️ Approaching limit — consider upgrading</span>}
              {usage.percent >= 100 && <span style={{color:'var(--danger)'}}>🚫 Limit reached — submissions are blocked</span>}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Service Request Log</div>
          <div style={{display:'flex', gap:8}}>
            <button className={`filter-btn ${filter==='ALL'?'active':''}`} onClick={() => setFilter('ALL')}>All</button>
            <button className={`filter-btn ${filter==='EMAIL'?'active':''}`} onClick={() => setFilter('EMAIL')}>📧 Email</button>
            <button className={`filter-btn ${filter==='CITYWORKS'?'active':''}`} onClick={() => setFilter('CITYWORKS')}>🔗 Cityworks</button>
          </div>
        </div>
        <div className="card-body" style={{padding:0}}>
          <div style={{padding:'16px 24px', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:10}}>
            <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
              <input className="search-input" placeholder="Search by confirmation #, type, address, or name..." value={search} onChange={e => setSearch(e.target.value)} style={{flex:1, minWidth:220}} />
              <input className="search-input" placeholder="🔍 Search description, comments, notes..." value={descSearch} onChange={e => setDescSearch(e.target.value)} style={{flex:1, minWidth:200}} />
              <select className="search-input" value={quickFilter} onChange={e => applyQuickFilter(e.target.value)} style={{width:160, cursor:'pointer'}}>
                <option value="today">📅 Today</option>
                <option value="week">📅 This Week</option>
                <option value="month">📅 This Month</option>
                <option value="year">📅 This Year</option>
                <option value="all">📅 All Time</option>
                <option value="custom">📅 Custom Range</option>
              </select>
            </div>
            <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
              <span style={{fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap'}}>Date range:</span>
              <input type="date" className="search-input" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setQuickFilter('custom'); }} style={{width:160}} />
              <span style={{fontSize:12, color:'var(--text-muted)'}}>→</span>
              <input type="date" className="search-input" value={dateTo} onChange={e => { setDateTo(e.target.value); setQuickFilter('custom'); }} style={{width:160}} />
              <div style={{display:'flex', gap:6, alignItems:'center'}}>
                <span style={{fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap'}}>Done:</span>
                <button className={`filter-btn ${doneFilter==='ALL'?'active':''}`} onClick={() => setDoneFilter('ALL')} style={{padding:'5px 10px', fontSize:12}}>All</button>
                <button className={`filter-btn ${doneFilter==='NOT_DONE'?'active':''}`} onClick={() => setDoneFilter('NOT_DONE')} style={{padding:'5px 10px', fontSize:12}}>☐ Not Done</button>
                <button className={`filter-btn ${doneFilter==='DONE'?'active':''}`} onClick={() => setDoneFilter('DONE')} style={{padding:'5px 10px', fontSize:12}}>☑ Done</button>
              </div>
              <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:12, fontWeight:600, color:'var(--accent-light)', background:'var(--accent-dim)', padding:'4px 12px', borderRadius:20, border:'1px solid var(--accent)30', whiteSpace:'nowrap'}}>
                  {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                </span>
                <button onClick={exportCSV} style={{padding:'6px 12px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all 0.15s'}} onMouseOver={e=>e.currentTarget.style.borderColor='var(--accent)'} onMouseOut={e=>e.currentTarget.style.borderColor='var(--border-light)'}>📥 Export CSV</button>
                <button onClick={() => window.print()} style={{padding:'6px 12px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all 0.15s'}} onMouseOver={e=>e.currentTarget.style.borderColor='var(--accent)'} onMouseOut={e=>e.currentTarget.style.borderColor='var(--border-light)'}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="empty">
              {submissions.length === 0 ? 'No submissions yet — they\'ll appear here as residents use CivicBot.' : 'No submissions match your search.'}
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>Confirmation #</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Submitted By</th>
                    <th>Delivery</th>
                    {user?.isCmmsPlan !== false && <th>CMMS Status</th>}
                    <th>Date</th>
                    <th style={{textAlign:'center'}}>Done</th>
                    <th>Notes</th>
                    <th style={{textAlign:'center'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => {
                    const cwStatus = s.cw_status || '';
                    const cwLower = cwStatus.toLowerCase();
                    const cwClosed = /closed|complete/.test(cwLower);
                    const cwCancelled = /cancel|void/.test(cwLower);
                    const cwInProgress = /progress|assigned/.test(cwLower);
                    const cwBadgeColor = cwClosed ? '#10B981' : cwCancelled ? '#94A3B8' : cwInProgress ? '#F59E0B' : '#3B82F6';
                    return (
                    <tr key={s.id} className={s.completed ? 'completed-row' : ''} style={s.review_status === 'rejected' ? { background: '#EF444408' } : undefined}>
                      <td><span style={{fontFamily:'monospace', fontSize:12, color:'var(--accent-light)'}}>{String(s.confirmation_number || '').replace(/\.0+$/, '')}</span>{s.cityworks_sr_id ? <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'monospace',marginTop:2}}>CMMS #{String(s.cityworks_sr_id).replace(/\.0+$/, '')}</div> : null}</td>
                      <td style={{maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{s.sr_type || '—'}</td>
                      <td style={{maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'var(--text-dim)', fontSize:12}}>{s.geo_address || s.address || '—'}</td>
                      <td style={{fontSize:12, color:'var(--text-dim)'}}>{s.caller_name && s.caller_name !== 'Anonymous' ? s.caller_name : <span style={{color:'var(--text-muted)'}}>Anonymous</span>}</td>
                      <td>
                        <span className={`delivery-badge ${s.delivery_method}`}>
                          {s.delivery_method === 'email' ? '📧 Email' : '🔗 CMMS'}
                        </span>
                      </td>
                      {user?.isCmmsPlan !== false && (
                        <td>
                          {s.review_status === 'rejected' ? (
                            <div>
                              <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:0.3,background:'#EF444425',color:'#EF4444',border:'1px solid #EF444460'}}>✕ Rejected in CB</span>
                              {s.rejected_reason && <div style={{fontSize:10,color:'#FCA5A5',marginTop:2,maxWidth:180,whiteSpace:'normal'}} title={s.rejected_reason}>Reason: {s.rejected_reason}</div>}
                            </div>
                          ) : s.review_status === 'pending' ? (
                            <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:0.3,background:'#F59E0B25',color:'#F59E0B',border:'1px solid #F59E0B60'}}>📥 Pending Review</span>
                          ) : cwStatus ? (
                            <div>
                              <span style={{display:'inline-block',padding:'2px 8px',borderRadius:10,fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:0.3,background:cwBadgeColor+'20',color:cwBadgeColor,border:'1px solid '+cwBadgeColor+'40'}}>{cwStatus}</span>
                              {s.status_polled_at && <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}} title="When CivicBot last checked the CMMS for this SR">{formatDate(s.status_polled_at)}</div>}
                            </div>
                          ) : <span style={{fontSize:11,color:'var(--text-muted)'}}>—</span>}
                        </td>
                      )}
                      <td style={{fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap'}}>{formatDate(s.submitted_at)}</td>
                      <td style={{textAlign:'center', padding:'12px 8px'}}>
                        <input type="checkbox" className="sub-check" checked={!!s.completed} onChange={e => updateSubmission(s.id, {completed: e.target.checked ? 1 : 0})} />
                      </td>
                      <td style={{padding:'8px 12px', minWidth:160}}>
                        <input className="notes-input" defaultValue={s.internal_notes || ''} maxLength={250} placeholder="Add note..." onBlur={e => { if (e.target.value !== (s.internal_notes || '')) updateSubmission(s.id, {internal_notes: e.target.value}); }} />
                      </td>
                      <td style={{textAlign:'center', padding:'8px'}}>
                        <button title="Delete from CivicBot list" onClick={() => deleteSubmission(s)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',fontSize:14,padding:'4px 6px',borderRadius:5,lineHeight:1}} onMouseEnter={e => { e.currentTarget.style.color='#EF4444'; e.currentTarget.style.background='#EF444415'; }} onMouseLeave={e => { e.currentTarget.style.color='var(--text-muted)'; e.currentTarget.style.background='none'; }}>🗑</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Billing Page ──────────────────────────────────────────────────────────────
function BillingPage({ user, showToast, onAnswersEnabled }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('annual');
  const [upgrading, setUpgrading] = useState(null);
  const [addingAnswers, setAddingAnswers] = useState(false);

  async function loadBillingData() {
    try {
      const r = await apiFetch(`/api/billing/status/${user.cityId}`);
      const data = await r.json();
      if (data.success) setBilling(data);
    } catch (e) { /* ignore */ }
  }

  useEffect(() => {
    if (!user?.cityId) { setLoading(false); return; }
    apiFetch(`/api/billing/status/${user.cityId}`)
      .then(r => r.json())
      .then(data => { if (data.success) setBilling(data); })
      .catch(() => showToast('Failed to load billing info', 'error'))
      .finally(() => setLoading(false));
  }, [user, showToast]);

  async function openPortal() {
    try {
      const res = await apiFetch('/api/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, returnUrl: window.location.href }),
      });
      const data = await res.json();
      if (data.success) window.open(data.url, '_blank');
      else showToast('Could not open billing portal', 'error');
    } catch { showToast('Could not open billing portal', 'error'); }
  }

  async function handleUpgrade(priceId, planName) {
    if (!window.confirm('Switch to ' + planName + '? This will take effect immediately.')) return;
    setUpgrading(priceId);
    try {
      const res = await apiFetch('/api/billing/upgrade', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, priceId }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Plan updated to ' + data.planName);
        setBilling(prev => ({ ...prev, currentPriceId: priceId, planName: data.planName }));
      } else if (data.isDowngradeBlock) {
        showToast(data.error, 'error');
      } else {
        showToast(data.error || 'Upgrade failed', 'error');
      }
    } catch { showToast('Upgrade failed — try again', 'error'); }
    setUpgrading(null);
  }

  async function handleAddAnswers() {
    const onTrial = billing?.status === 'trialing';
    const msg = onTrial
      ? 'Add CivicBot Answers to your trial? Answers pricing will apply when your trial converts — no charge until then.'
      : 'Add CivicBot Answers to your subscription? This will be billed immediately at the prorated rate for your current billing period.';
    if (!window.confirm(msg)) return;
    setAddingAnswers(true);
    try {
      const res = await apiFetch('/api/billing/add-answers', { method: 'POST', body: JSON.stringify({ cityId: user.cityId }) });
      const data = await res.json();
      if (data.success) {
        await loadBillingData();
        if (onAnswersEnabled) onAnswersEnabled();
        showToast('CivicBot Answers enabled! Navigate to CivicBot Answers in the left menu to set up your knowledge base.');
      } else {
        showToast(data.error || 'Failed to add Answers', 'error');
        await loadBillingData();
      }
    } catch (e) { showToast('Failed to add Answers — check connection and try again', 'error'); }
    setAddingAnswers(false);
  }

  async function handleStartTrial(priceId, planName) {
    setUpgrading(priceId);
    try {
      const res = await apiFetch('/api/billing/start-trial', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, email: user.email, cityName: user.cityName, isCityworks: billing?.isCityworks }),
      });
      const data = await res.json();
      if (!data.success) { showToast(data.error || 'Could not start trial', 'error'); setUpgrading(null); return; }
      // If they picked a non-Professional plan, upgrade immediately (trial, no charge)
      const defaultPriceId = billing?.isCityworks ? billing?.availablePlans?.monthly?.professional : billing?.availablePlans?.monthly?.professional;
      if (priceId && priceId !== defaultPriceId) {
        await apiFetch('/api/billing/upgrade', { method: 'POST', body: JSON.stringify({ cityId: user.cityId, priceId }) });
      }
      showToast(`${planName} trial started — 30 days free, no credit card required`);
      // Reload billing status
      const statusRes = await apiFetch(`/api/billing/status/${user.cityId}`);
      const statusData = await statusRes.json();
      if (statusData.success) setBilling(statusData);
    } catch { showToast('Could not start trial — try again', 'error'); }
    setUpgrading(null);
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading billing info...</div>;

  // Determine state
  const status = billing?.status;
  const isNoTrial = !billing || status === 'no_trial';
  const isTrialing = status === 'trialing';
  const isActive = status === 'active';
  const isPastDue = status === 'past_due';
  const isPaused = status === 'paused';
  const isCanceled = status === 'canceled';
  const isManual = billing?.manuallyActivated;
  const hasSubscription = !!billing?.subscriptionId;

  const daysLeft = billing?.trialDaysRemaining || 0;
  const trialEnd = billing?.trialEndsAt ? new Date(billing.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const licenseExpiry = billing?.paidThrough ? new Date(billing.paidThrough) : null;
  const licenseExpired = licenseExpiry && licenseExpiry < new Date();
  const licenseDaysLeft = licenseExpiry ? Math.ceil((licenseExpiry - new Date()) / 86400000) : null;

  const plans = billing?.availablePlans || {};
  const planList = billing?.isCityworks ? [
    { key: 'starter', name: 'Starter', monthlyPrice: '$250', annualPrice: '$2,500', monthlyPriceId: plans.monthly?.starter, annualPriceId: plans.annual?.starter, subs: 'Up to 100/mo' },
    { key: 'growth', name: 'Growth', monthlyPrice: '$500', annualPrice: '$5,000', monthlyPriceId: plans.monthly?.growth, annualPriceId: plans.annual?.growth, subs: 'Up to 250/mo' },
    { key: 'professional', name: 'Professional', monthlyPrice: '$750', annualPrice: '$7,500', monthlyPriceId: plans.monthly?.professional, annualPriceId: plans.annual?.professional, subs: 'Up to 500/mo', recommended: true },
  ] : [
    { key: 'starter', name: 'Starter', monthlyPrice: '$200', annualPrice: '$2,000', monthlyPriceId: plans.monthly?.starter, annualPriceId: plans.annual?.starter, subs: 'Up to 100/mo' },
    { key: 'growth', name: 'Growth', monthlyPrice: '$400', annualPrice: '$4,000', monthlyPriceId: plans.monthly?.growth, annualPriceId: plans.annual?.growth, subs: 'Up to 250/mo' },
    { key: 'professional', name: 'Professional', monthlyPrice: '$600', annualPrice: '$6,000', monthlyPriceId: plans.monthly?.professional, annualPriceId: plans.annual?.professional, subs: 'Up to 500/mo', recommended: true },
  ];

  // Answers add-on delta — the incremental cost above the base plan (same delta for CW and non-CW)
  const answersAddOnPricing = {
    starter:      { monthly: '+$150/mo', annual: '+$1,500/yr' },
    growth:       { monthly: '+$200/mo', annual: '+$2,000/yr' },
    professional: { monthly: '+$300/mo', annual: '+$3,000/yr' },
  };

  // Combined totals (base plan + Answers delta) — the actual Stripe Answers price IDs represent these totals
  const answersWithBaseTotals = billing?.isCityworks
    ? { starter: { monthly: '$400/mo', annual: '$4,000/yr' }, growth: { monthly: '$700/mo', annual: '$7,000/yr' }, professional: { monthly: '$1,050/mo', annual: '$10,500/yr' } }
    : { starter: { monthly: '$350/mo', annual: '$3,500/yr' }, growth: { monthly: '$600/mo', annual: '$6,000/yr' }, professional: { monthly: '$900/mo', annual: '$9,000/yr' } };

  const currentManualTier = isManual && billing?.planName
    ? (billing.planName.toLowerCase().includes('starter') ? 'starter'
      : billing.planName.toLowerCase().includes('growth') ? 'growth'
      : 'professional')
    : null;

  // Status banner config
  const statusBanner = isManual
    ? { emoji: licenseExpired ? '⚠️' : '✅', label: licenseExpired ? 'License Expired' : `${billing?.planName ? billing.planName + ' — ' : ''}Licensed through ${licenseExpiry ? licenseExpiry.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : 'No expiry set'}`, sub: licenseExpired ? 'Contact Centricity GIS to renew your license.' : licenseDaysLeft !== null ? `${licenseDaysLeft} days remaining on your current license.` : '', color: licenseExpired ? 'var(--danger)' : licenseDaysLeft <= 30 ? '#F59E0B' : 'var(--success)' }
    : isTrialing ? { emoji: '🎉', label: `Free Trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`, sub: `Trial ends ${trialEnd}. No charge until then — no credit card required.`, color: daysLeft <= 3 ? 'var(--danger)' : daysLeft <= 7 ? '#F59E0B' : 'var(--success)' }
    : isActive ? { emoji: '✅', label: `Active — ${billing?.planName || 'CivicBot Plan'}`, sub: 'Your subscription is active.', color: 'var(--success)' }
    : isPastDue ? { emoji: '⚠️', label: 'Payment Past Due', sub: 'Your payment method failed. Update it to avoid service interruption.', color: '#F59E0B' }
    : isPaused ? { emoji: '🔴', label: 'Subscription Paused', sub: 'Your trial ended without a payment method. Add one to resume.', color: 'var(--danger)' }
    : isCanceled ? { emoji: '✕', label: 'Subscription Canceled', sub: 'Your subscription has been canceled.', color: 'var(--danger)' }
    : { emoji: '💳', label: `Start your ${billing?.trialDays || 30}-day free trial`, sub: 'No credit card required. Full access during trial.', color: 'var(--accent-light)' };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Billing</div>
        <div className="page-subtitle">Manage your CivicBot subscription</div>
      </div>

      {/* Status banner */}
      <div className="billing-card" style={{marginBottom:20}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
              <span style={{fontSize:26}}>{statusBanner.emoji}</span>
              <span style={{fontSize:18,fontWeight:700,color:statusBanner.color}}>{statusBanner.label}</span>
            </div>
            {statusBanner.sub && <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.5,maxWidth:480}}>{statusBanner.sub}</div>}
            {isTrialing && daysLeft <= 7 && (
              <div style={{marginTop:10,background:'#F59E0B15',border:'1px solid #F59E0B40',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#F59E0B',fontWeight:600}}>
                ⏰ Add a payment method now to avoid interruption — use "Activate Plan" below.
              </div>
            )}
          </div>
          {hasSubscription && (
            <button className="save-btn" onClick={openPortal} style={{flexShrink:0}}>
              {isTrialing ? '🚀 Activate Plan (Selected Below)' : '💳 Manage Billing & Payment'}
            </button>
          )}
        </div>
      </div>

      {/* Plan grid — always visible */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header">
          <div className="card-title">{isManual ? 'Your Licensed Plan' : isNoTrial ? 'Choose Your Plan' : 'Available Plans'}</div>
          <span style={{fontSize:12, color:'var(--text-muted)'}}>
            {billing?.isCityworks ? 'Cityworks/Trimble Unity integration' : 'Email delivery'}
          </span>
        </div>
        <div className="card-body">
          {isNoTrial && !isManual && (
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#1E40AF',lineHeight:1.6}}>
              🎉 <strong>Start your {billing?.trialDays || 30}-day free trial</strong> — pick the plan that fits your city. No credit card required. You can switch plans anytime during your trial.
            </div>
          )}
          {isManual ? (
            <div style={{fontSize:12,color:'var(--text-muted)',textAlign:'center',marginBottom:20,padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
              Your plan is managed by Centricity GIS via invoice or purchase order. Contact us to make changes.
            </div>
          ) : (
            <div style={{display:'flex',justifyContent:'center',marginBottom:20}}>
              <div style={{display:'flex',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:3,gap:3}}>
                <button onClick={() => setBillingInterval('monthly')} style={{padding:'6px 20px',borderRadius:6,border:'none',background: billingInterval === 'monthly' ? 'var(--accent)' : 'transparent',color: billingInterval === 'monthly' ? '#fff' : 'var(--text-muted)',fontWeight:600,fontSize:13,cursor:'pointer',transition:'all 0.15s'}}>Monthly</button>
                <button onClick={() => setBillingInterval('annual')} style={{padding:'6px 20px',borderRadius:6,border:'none',background: billingInterval === 'annual' ? 'var(--accent)' : 'transparent',color: billingInterval === 'annual' ? '#fff' : 'var(--text-muted)',fontWeight:600,fontSize:13,cursor:'pointer',transition:'all 0.15s'}}>
                  Annual <span style={{fontSize:10,background:'#10B981',color:'#fff',padding:'1px 5px',borderRadius:4,marginLeft:4}}>Save 17%</span>
                </button>
              </div>
            </div>
          )}
          <div className="billing-plans-grid">
            {planList.map(plan => {
              const priceId = billingInterval === 'annual' ? plan.annualPriceId : plan.monthlyPriceId;
              const isCurrent = billing?.currentPriceId === priceId || (isManual && currentManualTier === plan.key);
              const isProcessing = upgrading === priceId;
              return (
                <div key={plan.key} className={`billing-plan-option ${isCurrent ? 'current' : ''}`} style={{position:'relative'}}>
                  {plan.recommended && !isCurrent && <div style={{position:'absolute',top:-10,left:'50%',transform:'translateX(-50%)',background:'var(--accent)',color:'#fff',fontSize:10,fontWeight:700,padding:'2px 10px',borderRadius:10,whiteSpace:'nowrap'}}>MOST POPULAR</div>}
                  <div className="billing-plan-price">
                    {isManual ? (billing?.billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice) : (billingInterval === 'annual' ? plan.annualPrice : plan.monthlyPrice)}
                    <span style={{fontSize:12,fontWeight:400,color:'var(--text-muted)'}}>{(isManual ? billing?.billingPeriod === 'monthly' : billingInterval === 'monthly') ? '/mo' : '/yr'}</span>
                  </div>
                  <div className="billing-plan-name-sm">{plan.name}</div>
                  <div className="billing-plan-subs">{plan.subs}</div>
                  {!isManual && billingInterval === 'annual' && <div style={{fontSize:10,color:'#10B981',marginTop:2}}>Billed annually — save 17%</div>}
                  <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid var(--border)',fontSize:11,color:'var(--text-muted)'}}>
                    🤖 Add Answers Q&A: <span style={{color:'var(--accent-light)',fontWeight:600}}>{answersAddOnPricing[plan.key]?.[isManual ? (billing?.billingPeriod || 'annual') : billingInterval]}</span>
                    <span style={{fontSize:10,color:'var(--text-muted)',display:'block',marginTop:2}}>({answersWithBaseTotals[plan.key]?.[isManual ? (billing?.billingPeriod || 'annual') : billingInterval]} with Answers)</span>
                  </div>
                  <div style={{marginTop:12}}>
                    {isCurrent ? (
                      <div style={{fontSize:11,color:'var(--accent-light)',fontWeight:600,textAlign:'center'}}>{isManual ? '✓ Licensed' : '✓ Current Plan'}</div>
                    ) : isManual ? (
                      <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'center'}}>—</div>
                    ) : (isNoTrial && !isManual) ? (
                      <button
                        onClick={() => handleStartTrial(priceId, plan.name + (billingInterval === 'annual' ? ' Annual' : ' Monthly'))}
                        disabled={isProcessing || !!upgrading}
                        style={{width:'100%',padding:'8px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor: isProcessing || upgrading ? 'not-allowed' : 'pointer',opacity: isProcessing || upgrading ? 0.6 : 1}}
                      >
                        {isProcessing ? 'Starting trial...' : 'Start Free Trial'}
                      </button>
                    ) : hasSubscription ? (
                      <button
                        onClick={() => handleUpgrade(priceId, plan.name + (billingInterval === 'annual' ? ' Annual' : ' Monthly'))}
                        disabled={isProcessing || (isPaused || isCanceled)}
                        style={{width:'100%',padding:'8px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor: isProcessing ? 'not-allowed' : 'pointer',opacity: isProcessing ? 0.6 : 1}}
                      >
                        {isProcessing ? 'Switching...' : 'Select Plan'}
                      </button>
                    ) : (
                      <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'center'}}>—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:16, fontSize:12, color:'var(--text-muted)', textAlign:'center'}}>
            Annual plans lock in for 12 months. Monthly plans can be changed anytime. Enterprise plans available — <a href="mailto:info@centricitygis.com" style={{color:'var(--accent-light)'}}>contact us</a>.
          </div>
        </div>
      </div>

      {/* CivicBot Answers add-on card */}
      {(() => {
        // Already licensed for Answers
        if (billing?.isAnswersPlan) {
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header"><div className="card-title">🤖 CivicBot Answers</div></div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{fontSize:28}}>✅</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:15}}>CivicBot Answers is active</div>
                    <div style={{fontSize:13,color:'var(--text-muted)',marginTop:2}}>Residents can ask natural language questions and get grounded answers from your city website.</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Manually licensed base plan — show add-on with "contact us" CTA
        if (isManual) {
          const tier = currentManualTier || 'professional';
          const period = billing?.billingPeriod || 'annual';
          const addOnLabel = answersAddOnPricing[tier]?.[period] || '';
          const totalLabel = answersWithBaseTotals[tier]?.[period] || '';
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:220}}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>Supercharge your widget with resident Q&A</div>
                    <ul style={{margin:'0 0 12px 0',paddingLeft:18,fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
                      <li>AI answers grounded in your city website content</li>
                      <li>Every answer includes source links residents can verify</li>
                      <li>Unanswered questions logged so you can fill gaps</li>
                      <li>Automatic weekly knowledge base refresh</li>
                    </ul>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,minWidth:170,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>Add-on cost</div>
                    <div style={{fontSize:22,fontWeight:700,color:'var(--accent-light)'}}>{addOnLabel}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)',paddingTop:6,width:'100%'}}>{tier.charAt(0).toUpperCase()+tier.slice(1)} plan + Answers = <strong style={{color:'var(--text)'}}>{totalLabel}</strong></div>
                    <a
                      href="mailto:info@centricitygis.com?subject=CivicBot%20Answers%20Add-on&body=Please%20add%20CivicBot%20Answers%20to%20our%20account."
                      style={{display:'block',textAlign:'center',padding:'9px 16px',background:'var(--accent)',borderRadius:8,color:'white',fontSize:12,fontWeight:600,textDecoration:'none',width:'100%',boxSizing:'border-box',marginTop:4}}
                    >
                      Contact Us to Add Answers
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Active subscription or active trial with plan selected — show matched tier with self-serve button
        if ((isActive || isPastDue || isTrialing) && billing?.currentPriceId) {
          const rank = billing.allPrices?.[billing.currentPriceId]?.rank;
          const tier = rank === 1 ? 'starter' : rank === 2 ? 'growth' : 'professional';
          const period = billing.billingPeriod || 'annual';
          const addOnLabel = answersAddOnPricing[tier]?.[period] || '';
          const totalLabel = answersWithBaseTotals[tier]?.[period] || '';
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:220}}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>Supercharge your widget with resident Q&A</div>
                    <ul style={{margin:'0 0 12px 0',paddingLeft:18,fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
                      <li>AI answers grounded in your city website content</li>
                      <li>Every answer includes source links residents can verify</li>
                      <li>Unanswered questions logged so you can fill gaps</li>
                      <li>Automatic weekly knowledge base refresh</li>
                    </ul>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,minWidth:170,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>Add-on cost</div>
                    <div style={{fontSize:22,fontWeight:700,color:'var(--accent-light)'}}>{addOnLabel}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)',paddingTop:6,width:'100%'}}>{tier.charAt(0).toUpperCase()+tier.slice(1)} plan + Answers = <strong style={{color:'var(--text)'}}>{totalLabel}</strong></div>
                    <button
                      onClick={handleAddAnswers}
                      disabled={addingAnswers}
                      style={{padding:'9px 24px',background:'var(--accent)',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',cursor: addingAnswers ? 'not-allowed' : 'pointer',opacity: addingAnswers ? 0.7 : 1,width:'100%',marginTop:4}}
                    >
                      {addingAnswers ? 'Processing...' : 'Add Answers'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Trial or no subscription yet — teaser only, no pricing shown until plan is confirmed
        if (isTrialing || isNoTrial) {
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                  <div style={{fontSize:40,flexShrink:0}}>🤖</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>CivicBot Answers is available as an add-on</div>
                    <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>Add AI-powered Q&A grounded in your city website. Pricing matches your plan tier — confirm your subscription above to see your Answers add-on price and activate it.</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Contact / Support card */}
      <div className="card" style={{marginTop:20}}>
        <div className="card-header">
          <div className="card-title">Need Help?</div>
        </div>
        <div className="card-body" style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
          <div style={{fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
            Questions about your plan, billing, or getting set up? Our team is here to help.
          </div>
          <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
            <a
              href="mailto:info@centricitygis.com"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text)',fontSize:13,fontWeight:500,textDecoration:'none'}}
            >
              ✉️ info@centricitygis.com
            </a>
            <a
              href="https://civicbot.centricitygis.com"
              target="_blank"
              rel="noreferrer"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',background:'var(--accent)',border:'none',borderRadius:8,color:'#fff',fontSize:13,fontWeight:500,textDecoration:'none'}}
            >
              🌐 civicbot.centricitygis.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Email SR Types Editor ────────────────────────────────────────────────────
function EmailSRTypesEditor({ showToast }) {
  const DEFAULTS = ['Pothole Report', 'Street Light Outage', 'Abandoned Vehicle'];
  const [srTypes, setSrTypes] = useState(() => {
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    const saved = localStorage.getItem(`civicbot_sr_types_${cityId}`);
    return saved ? JSON.parse(saved) : DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;

  function addType() { setSrTypes(prev => [...prev, '']); setSaved(false); }
  function removeType(i) { setSrTypes(prev => prev.filter((_, idx) => idx !== i)); setSaved(false); }
  function updateType(i, val) { setSrTypes(prev => prev.map((t, idx) => idx === i ? val : t)); setSaved(false); }

  async function handleSave() {
    const cleaned = srTypes.map(t => t.trim()).filter(Boolean);
    if (cleaned.length === 0) { showToast('Add at least one SR type', 'error'); return; }

    // Save to localStorage
    localStorage.setItem(`civicbot_sr_types_${cityId}`, JSON.stringify(cleaned));

    // Save to backend — load existing config first to avoid wiping other fields
    try {
      const cfgRes = await apiFetch(`/api/config/${cityId}`).then(r => r.json());
      const existing = cfgRes.success ? cfgRes.config : {};
      const saveRes = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId,
          cityName: existing.cityName,
          greeting: existing.greeting,
          primaryColor: existing.primaryColor,
          state: existing.state,
          wkid: existing.wkid,
          emailDelivery: true,
          notificationEmails: existing.notificationEmails,
          geofenceEnabled: existing.geofenceEnabled,
          geofenceMessage: existing.geofenceMessage,
          selectedTemplates: existing.selectedTemplates || [],
          aliases: existing.aliases || {},
          srTypes: cleaned,
        }),
      });
      const saveData = await saveRes.json();
      if (saveData.success) {
        setSrTypes(cleaned);
        setSaved(true);
        showToast(`Saved ${cleaned.length} service request types`);
        setTimeout(() => setSaved(false), 3000);
      } else {
        showToast(saveData.error || 'Failed to save', 'error');
      }
    } catch { showToast('Failed to save — check your connection', 'error'); }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Service Request Types</div>
        <div className="page-subtitle">Define the issue types residents can report through CivicBot</div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">SR Types Configured</div>
          <div className="stat-value" style={{color:'var(--accent-light)'}}>{srTypes.filter(t=>t.trim()).length}</div>
          <div className="stat-sub">Residents can choose from these</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Delivery Method</div>
          <div className="stat-value" style={{fontSize:18, marginTop:10}}>📧</div>
          <div className="stat-sub">Email delivery</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Widget Status</div>
          <div className="stat-value" style={{fontSize:18, marginTop:10, color:'var(--success)'}}>✅</div>
          <div className="stat-sub">Ready to embed</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Configure SR Types</div>
          <span style={{fontSize:12, color:'var(--text-muted)'}}>These appear as options in your CivicBot chat widget</span>
        </div>
        <div className="card-body">
          <div style={{display:'flex', flexDirection:'column', gap:8, marginBottom:16}}>
            {srTypes.map((type, i) => (
              <div key={i} style={{display:'flex', gap:8, alignItems:'center'}}>
                <div style={{width:28, height:28, borderRadius:6, background:'var(--surface2)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'var(--text-muted)', flexShrink:0}}>{i + 1}</div>
                <input
                  className="setting-input"
                  style={{flex:1}}
                  value={type}
                  onChange={e => updateType(i, e.target.value)}
                  placeholder="e.g. Pothole Report"
                  autoFocus={i === srTypes.length - 1 && type === ''}
                />
                <button
                  onClick={() => removeType(i)}
                  style={{width:32, height:32, background:'none', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s'}}
                  onMouseOver={e => { e.target.style.borderColor='var(--danger)'; e.target.style.color='var(--danger)'; }}
                  onMouseOut={e => { e.target.style.borderColor='var(--border-light)'; e.target.style.color='var(--text-muted)'; }}
                  title="Remove"
                >✕</button>
              </div>
            ))}
          </div>

          <button
            onClick={addType}
            style={{display:'flex', alignItems:'center', gap:8, padding:'9px 16px', background:'none', border:'1px dashed var(--border-light)', borderRadius:8, color:'var(--accent-light)', fontSize:13, fontFamily:'inherit', cursor:'pointer', transition:'all 0.15s', width:'100%', justifyContent:'center'}}
            onMouseOver={e => e.currentTarget.style.borderColor='var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor='var(--border-light)'}
          >
            <span style={{fontSize:16, fontWeight:600}}>+</span> Add Service Request Type
          </button>

          <div style={{marginTop:16, background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)', lineHeight:1.6}}>
            💡 <strong style={{color:'var(--text-dim)'}}>Tip:</strong> Be specific but simple. "Pothole Report" works better than "Road Surface Damage Issue". Residents will see exactly these names in the chatbot.
          </div>
        </div>
      </div>

      {!saved && (
        <div className="save-bar">
          <span className="save-bar-text"><strong>{srTypes.filter(t=>t.trim()).length} SR types</strong> configured</span>
          <button className="save-btn" onClick={handleSave}>Save SR Types</button>
        </div>
      )}
    </div>
  );
}

// ─── Templates Page ────────────────────────────────────────────────────────────
function TemplatesPage({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [aliases, setAliases] = useState({});
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [keywords, setKeywords] = useState({}); // problemSid -> "garbage, trash" (#4)
  const [editingKw, setEditingKw] = useState(null);
  const [kwInput, setKwInput] = useState('');
  const [groups, setGroups] = useState({}); // problemSid -> "Parks" — Browse category hierarchy
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupInput, setGroupInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  // Starts true so the save bar only appears once something actually changes —
  // a fresh page load with no edits should not show "Save Configuration"
  const [saved, setSaved] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isEmailOnly, setIsEmailOnly] = useState(false);
  const [needsCWSetup, setNeedsCWSetup] = useState(false);

  useEffect(() => {
    // Load city config first to determine type
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { setLoading(false); return; }

    // Aliases come from backend config (the source of truth). Seed only from a
    // CITY-SCOPED localStorage key — never the old global `civicbot_aliases`, which
    // bled one city's aliases onto another city opened in the same browser (e.g.
    // Lincoln's "Code Enforcement: Graffiti" alias appearing on Imperial by ProblemSid).
    setAliases(JSON.parse(localStorage.getItem(`civicbot_aliases_${cityId}`) || '{}'));

    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(async cfg => {
        if (!cfg.success) { setLoading(false); return; }
        const config = cfg.config;
        if (config.aliases && Object.keys(config.aliases).length) setAliases(config.aliases);
        if (config.templateKeywords) setKeywords(config.templateKeywords);
        if (config.templateCategories) setGroups(config.templateCategories);

        if (config.emailDelivery) {
          // Email-only city — use their configured SR types, no Cityworks
          const srTypes = JSON.parse(localStorage.getItem('civicbot_sr_types') || '[]');
          setIsEmailOnly(true);
          setLoading(false);
          return;
        }

        // Cityworks city — check if THIS city has CW credentials configured
        const hasCWCredentials = config.cwBaseUrl && config.cwUser;

        if (!hasCWCredentials) {
          setNeedsCWSetup(true);
          setLoading(false);
          return;
        }

        // Load Cityworks templates
        const cfgSelected = config.selectedTemplates || [];
        apiFetch('/api/sr-templates?cityId=' + cityId)
          .then(r => r.json())
          .then(data => {
            if (data.success) {
              setTemplates(data.templates);
              // Backend config is the source of truth (what the widget actually shows).
              // localStorage fallback is CITY-SCOPED so one city's selection can't bleed
              // into another opened in the same browser.
              const savedIds = cfgSelected.length > 0 ? cfgSelected : JSON.parse(localStorage.getItem(`civicbot_selected_templates_${cityId}`) || '[]');
              if (savedIds.length > 0) setSelected(new Set(savedIds));
              else setSelected(new Set(data.templates.filter(t => t.ForPublicSite).map(t => t.ProblemSid)));
            }
          })
          .catch(() => showToast('Failed to load templates', 'error'))
          .finally(() => setLoading(false));
      })
      .catch(() => { setLoading(false); showToast('Failed to load city config', 'error'); });
  }, [showToast]);

  const categories = ['ALL', ...new Set(templates.map(t => t.ProbCategory))];
  const filtered = templates.filter(t => {
    const displayName = aliases[t.ProblemSid] || t.Description;
    const matchSearch = displayName.toLowerCase().includes(search.toLowerCase()) || t.ProblemCode.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'ALL' || t.ProbCategory === catFilter;
    return matchSearch && matchCat;
  });

  function toggle(sid) { setSelected(prev => { const next = new Set(prev); next.has(sid) ? next.delete(sid) : next.add(sid); return next; }); setSaved(false); }
  function selectAll() { setSelected(new Set(filtered.map(t => t.ProblemSid))); setSaved(false); }
  function selectNone() { setSelected(prev => { const next = new Set(prev); filtered.forEach(t => next.delete(t.ProblemSid)); return next; }); setSaved(false); }
  function startEditAlias(sid) { setEditingAlias(sid); setAliasInput(aliases[sid] || ''); }
  function saveAlias(sid) {
    const trimmed = aliasInput.trim();
    setAliases(prev => { const next = { ...prev }; if (trimmed) next[sid] = trimmed; else delete next[sid]; return next; });
    setEditingAlias(null); setSaved(false);
  }
  function clearAlias(sid) { setAliases(prev => { const next = { ...prev }; delete next[sid]; return next; }); setSaved(false); }
  function startEditKw(sid) { setEditingKw(sid); setKwInput(keywords[sid] || ''); }
  function saveKw(sid) {
    const trimmed = kwInput.trim();
    setKeywords(prev => { const next = { ...prev }; if (trimmed) next[sid] = trimmed; else delete next[sid]; return next; });
    setEditingKw(null); setSaved(false);
  }
  function clearKw(sid) { setKeywords(prev => { const next = { ...prev }; delete next[sid]; return next; }); setSaved(false); }
  function startEditGroup(sid) { setEditingGroup(sid); setGroupInput(groups[sid] || ''); }
  function saveGroup(sid) {
    const trimmed = groupInput.trim();
    setGroups(prev => { const next = { ...prev }; if (trimmed) next[sid] = trimmed; else delete next[sid]; return next; });
    setEditingGroup(null); setSaved(false);
  }
  function clearGroup(sid) { setGroups(prev => { const next = { ...prev }; delete next[sid]; return next; }); setSaved(false); }
  async function handleSave() {
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    // CITY-SCOPED localStorage cache — never the old global keys, which let one city's
    // selection/aliases bleed onto another city in the same browser.
    if (cityId) {
      localStorage.setItem(`civicbot_selected_templates_${cityId}`, JSON.stringify([...selected]));
      localStorage.setItem(`civicbot_aliases_${cityId}`, JSON.stringify(aliases));
    }
    // Also persist to backend so the widget picks them up
    try {
      if (cityId) {
        const cfgRes = await apiFetch('/api/config/' + cityId).then(r => r.json());
        const existing = (cfgRes && cfgRes.config) || {};
        await apiFetch('/api/config', {
          method: 'POST',
          body: JSON.stringify({
            cityId: cityId,
            cityName: existing.cityName || '',
            slug: existing.slug || '',
            greeting: existing.greeting || '',
            primaryColor: existing.primaryColor || '#3B82F6',
            logoUrl: existing.logoUrl || '',
            cwBaseUrl: existing.cwBaseUrl || '',
            cwUser: existing.cwUser || '',
            cwPassword: '',
            state: existing.state || '',
            wkid: existing.wkid || 4326,
            zoneName: existing.zoneName || '',
            selectedTemplates: [...selected],
            aliases: aliases,
            templateKeywords: keywords,
            templateCategories: groups,
            geofenceEnabled: existing.geofenceEnabled || false,
            geofenceMessage: existing.geofenceMessage || '',
            geofenceLat: existing.geofenceLat || null,
            geofenceLng: existing.geofenceLng || null,
            geofenceCityName: existing.geofenceCityName || '',
            emailDelivery: existing.emailDelivery || false,
            notificationEmails: existing.notificationEmails || '',
            srTypes: existing.srTypes || [],
            assignedSalesRep: existing.assignedSalesRep || '',
            sendSubmitterConfirmation: existing.sendSubmitterConfirmation !== false,
          }),
        });
      }
    } catch (e) {
      console.error('Backend save failed:', e);
      showToast('Local saved, but backend sync failed', 'error');
      return;
    }
    setSaved(true);
    showToast('Saved ' + selected.size + ' SR types with ' + Object.keys(aliases).length + ' aliases');
  }

  const embedCode = generateEmbedCode(templates.filter(t => selected.has(t.ProblemSid)));
  function copyEmbed() { navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 2000); showToast('Embed code copied!'); }

  if (loading) return <div className="loading"><div className="spinner" />Loading SR templates...</div>;

  if (isEmailOnly) {
    return <EmailSRTypesEditor showToast={showToast} />;
  }

  if (needsCWSetup) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Service Request Templates</div>
          <div className="page-subtitle">Configure your Cityworks connection to load SR types</div>
        </div>
        <div className="card">
          <div className="card-body" style={{textAlign:'center', padding:48}}>
            <div style={{fontSize:32, marginBottom:16}}>🔗</div>
            <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Cityworks Connection Required</div>
            <div style={{fontSize:13, color:'var(--text-muted)', lineHeight:1.6, marginBottom:24}}>
              To load your SR types, first configure your Cityworks/Trimble Unity connection in Settings.<br/>
              You'll need your <strong>Cityworks Base URL</strong> and <strong>API credentials</strong>.
            </div>
            <button className="save-btn" onClick={() => window.dispatchEvent(new CustomEvent('navigate', {detail:'settings'}))}>
              Go to Settings →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Service Request Templates</div>
        <div className="page-subtitle">Select SR types and set friendly display names for the chatbot</div>
      </div>
      <div className="stats-row">
        <div className="stat-card"><div className="stat-label">Total Templates</div><div className="stat-value">{templates.length}</div><div className="stat-sub">In Trimble Unity</div></div>
        <div className="stat-card"><div className="stat-label">Enabled for CivicBot</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{selected.size}</div><div className="stat-sub">Residents can submit these</div></div>
        <div className="stat-card"><div className="stat-label">Custom Aliases</div><div className="stat-value" style={{color:'#8B5CF6'}}>{Object.keys(aliases).length}</div><div className="stat-sub">Renamed for residents</div></div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Configure SR Types</div>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>{selected.size} of {templates.length} selected</span>
            <button onClick={async () => { setLoading(true); try { const cityId2 = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
const data = await apiFetch('/api/sr-templates' + (cityId2 ? '?cityId=' + cityId2 : '')).then(r=>r.json()); if (data.success) { setTemplates(data.templates); showToast('SR types synced from Cityworks (' + data.templates.length + ' loaded)'); } else { showToast(data.error || 'Sync failed', 'error'); } } catch { showToast('Sync failed', 'error'); } setLoading(false); }} style={{padding:'6px 14px',background:'var(--surface2)',border:'1px solid var(--border-light)',borderRadius:6,color:'var(--accent-light)',fontSize:12,fontFamily:'inherit',cursor:'pointer',fontWeight:600}}>🔄 Sync from Cityworks</button>
            <span style={{fontSize:11,color:'var(--text-muted)'}}>Only pulls templates marked "For Public Website" in Cityworks</span>
          </div>
        </div>
        <div className="card-body">
          <div className="filter-row">
            <input className="search-input" placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} />
            {categories.map(cat => <button key={cat} className={`filter-btn ${catFilter===cat?'active':''}`} onClick={() => setCatFilter(cat)}>{cat==='ALL'?'All':getCat(cat).label}</button>)}
          </div>
          <div className="select-all-row">
            <span>{filtered.length} templates shown</span>
            <div style={{display:'flex',gap:12}}>
              <button className="select-all-btn" onClick={selectAll}>Select All</button>
              <span style={{color:'var(--border-light)'}}>|</span>
              <button className="select-all-btn" onClick={selectNone}>Deselect All</button>
            </div>
          </div>
          <div className="sr-list">
            {filtered.map(t => {
              const cat = getCat(t.ProbCategory);
              const isSelected = selected.has(t.ProblemSid);
              const hasAlias = !!aliases[t.ProblemSid];
              const isEditing = editingAlias === t.ProblemSid;
              const hasKw = !!keywords[t.ProblemSid];
              const isEditingKw = editingKw === t.ProblemSid;
              return (
                <div key={t.ProblemSid} className={`sr-item ${isSelected?'selected':''}`} onClick={e => { if (!e.target.closest('.alias-area')) toggle(t.ProblemSid); }}>
                  <div className="sr-checkbox">{isSelected && <span className="sr-checkbox-check">✓</span>}</div>
                  <div className="sr-info" style={{flex:1}}>
                    <div className="sr-name">{t.Description}</div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4,flexWrap:'wrap'}}>
                      <div className="sr-code">{t.ProblemCode}</div>
                      <div className="alias-area" onClick={e => e.stopPropagation()}>
                        {isEditing ? (
                          <div style={{display:'flex',gap:4,alignItems:'center'}}>
                            <input autoFocus value={aliasInput} onChange={e => setAliasInput(e.target.value)} onKeyDown={e => { if(e.key==='Enter') saveAlias(t.ProblemSid); if(e.key==='Escape') setEditingAlias(null); }} placeholder={t.Description} style={{width:180,padding:'3px 8px',background:'var(--surface3)',border:'1px solid var(--accent)',borderRadius:5,color:'var(--text)',fontSize:11,fontFamily:'inherit',outline:'none'}} />
                            <button onClick={() => saveAlias(t.ProblemSid)} style={{background:'var(--accent)',border:'none',borderRadius:4,color:'white',padding:'3px 8px',cursor:'pointer',fontSize:11}}>✓</button>
                            <button onClick={() => setEditingAlias(null)} style={{background:'var(--surface3)',border:'1px solid var(--border-light)',borderRadius:4,color:'var(--text-muted)',padding:'3px 6px',cursor:'pointer',fontSize:11}}>✕</button>
                          </div>
                        ) : hasAlias ? (
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <span style={{fontSize:11,color:'#8B5CF6',background:'#8B5CF615',padding:'2px 7px',borderRadius:4,border:'1px solid #8B5CF630'}}>→ {aliases[t.ProblemSid]}</span>
                            <button onClick={() => startEditAlias(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:'0 2px'}}>✏️</button>
                            <button onClick={() => clearAlias(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:10,padding:'0 2px'}}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => startEditAlias(t.ProblemSid)} style={{background:'none',border:'1px dashed var(--border-light)',borderRadius:4,color:'var(--text-muted)',fontSize:10,padding:'2px 8px',cursor:'pointer',fontFamily:'inherit'}}>+ alias</button>
                        )}
                      </div>
                      <div className="alias-area" onClick={e => e.stopPropagation()}>
                        {isEditingKw ? (
                          <div style={{display:'flex',gap:4,alignItems:'center'}}>
                            <input autoFocus value={kwInput} onChange={e => setKwInput(e.target.value)} onKeyDown={e => { if(e.key==='Enter') saveKw(t.ProblemSid); if(e.key==='Escape') setEditingKw(null); }} placeholder="garbage, trash, refuse" style={{width:220,padding:'3px 8px',background:'var(--surface3)',border:'1px solid #10B981',borderRadius:5,color:'var(--text)',fontSize:11,fontFamily:'inherit',outline:'none'}} />
                            <button onClick={() => saveKw(t.ProblemSid)} style={{background:'#10B981',border:'none',borderRadius:4,color:'white',padding:'3px 8px',cursor:'pointer',fontSize:11}}>✓</button>
                            <button onClick={() => setEditingKw(null)} style={{background:'var(--surface3)',border:'1px solid var(--border-light)',borderRadius:4,color:'var(--text-muted)',padding:'3px 6px',cursor:'pointer',fontSize:11}}>✕</button>
                          </div>
                        ) : hasKw ? (
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <span title="Words residents might use that should match this category" style={{fontSize:11,color:'#10B981',background:'#10B98115',padding:'2px 7px',borderRadius:4,border:'1px solid #10B98130'}}>🔎 {keywords[t.ProblemSid]}</span>
                            <button onClick={() => startEditKw(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:'0 2px'}}>✏️</button>
                            <button onClick={() => clearKw(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:10,padding:'0 2px'}}>✕</button>
                          </div>
                        ) : (
                          <button title="Add words residents might type that should match this category" onClick={() => startEditKw(t.ProblemSid)} style={{background:'none',border:'1px dashed var(--border-light)',borderRadius:4,color:'var(--text-muted)',fontSize:10,padding:'2px 8px',cursor:'pointer',fontFamily:'inherit'}}>+ keywords</button>
                        )}
                      </div>
                      <div className="alias-area" onClick={e => e.stopPropagation()}>
                        {editingGroup === t.ProblemSid ? (
                          <div style={{display:'flex',gap:4,alignItems:'center'}}>
                            <input autoFocus list="cb-group-list" value={groupInput} onChange={e => setGroupInput(e.target.value)} onKeyDown={e => { if(e.key==='Enter') saveGroup(t.ProblemSid); if(e.key==='Escape') setEditingGroup(null); }} placeholder="e.g. Parks, Streets, Utilities" style={{width:180,padding:'3px 8px',background:'var(--surface3)',border:'1px solid #F59E0B',borderRadius:5,color:'var(--text)',fontSize:11,fontFamily:'inherit',outline:'none'}} />
                            <datalist id="cb-group-list">{[...new Set(Object.values(groups))].map(g => <option key={g} value={g} />)}</datalist>
                            <button onClick={() => saveGroup(t.ProblemSid)} style={{background:'#F59E0B',border:'none',borderRadius:4,color:'white',padding:'3px 8px',cursor:'pointer',fontSize:11}}>✓</button>
                            <button onClick={() => setEditingGroup(null)} style={{background:'var(--surface3)',border:'1px solid var(--border-light)',borderRadius:4,color:'var(--text-muted)',padding:'3px 6px',cursor:'pointer',fontSize:11}}>✕</button>
                          </div>
                        ) : groups[t.ProblemSid] ? (
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <span title="Browse category — residents pick this group first, then see its request types" style={{fontSize:11,color:'#F59E0B',background:'#F59E0B15',padding:'2px 7px',borderRadius:4,border:'1px solid #F59E0B30'}}>📂 {groups[t.ProblemSid]}</span>
                            <button onClick={() => startEditGroup(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:'0 2px'}}>✏️</button>
                            <button onClick={() => clearGroup(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:10,padding:'0 2px'}}>✕</button>
                          </div>
                        ) : (
                          <button title="Assign a Browse category — residents pick a group first instead of scrolling 50+ types" onClick={() => startEditGroup(t.ProblemSid)} style={{background:'none',border:'1px dashed var(--border-light)',borderRadius:4,color:'var(--text-muted)',fontSize:10,padding:'2px 8px',cursor:'pointer',fontFamily:'inherit'}}>+ group</button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:4,alignItems:'center',flexShrink:0}}>
                    <span className="sr-badge" style={{background:cat.bg,color:cat.color}}>{cat.label}</span>
                    {t.ForPublicSite && <span className="sr-public-badge">Public</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="card" style={{marginTop:20}}>
        <div className="card-header"><div className="card-title">Your Embed Code</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Paste this on your city website</span></div>
        <div className="card-body">
          <div className="embed-box">
            <pre className="embed-code">{embedCode}</pre>
            <button className={`copy-btn ${copied?'copied':''}`} onClick={copyEmbed}>{copied?'✓ Copied':'Copy'}</button>
          </div>
        </div>
      </div>
      {!saved && (
        <div className="save-bar">
          <span className="save-bar-text"><strong>{selected.size} SR types</strong> · <strong>{Object.keys(aliases).length} aliases</strong></span>
          <button className="save-btn" onClick={handleSave}>Save Configuration</button>
        </div>
      )}
    </div>
  );
}

// ─── Embed Page ────────────────────────────────────────────────────────────────
function EmbedPage({ user }) {
  const [copied, setCopied] = useState(false);
  const [copiedStandalone, setCopiedStandalone] = useState(false);
  const cityId = user?.cityId || 'your-city-id';
  // Read color from backend config (most up to date)
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  useEffect(() => {
    if (!cityId || cityId === 'your-city-id') return;
    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(data => { if (data.success && data.config?.primaryColor) setPrimaryColor(data.config.primaryColor); })
      .catch(() => {
        const saved = JSON.parse(localStorage.getItem('civicbot_settings') || '{}');
        if (saved.primaryColor) setPrimaryColor(saved.primaryColor);
      });
  }, [cityId]);
  const standaloneUrl = `https://civicbot.centricitygis.com/submit.html?city=${cityId}`;
  const code = `<script src="https://civicbot.centricitygis.com/widget.js"\n  data-city-id="${cityId}"\n  data-color="${primaryColor}"\n  async>\n</script>`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(standaloneUrl)}&color=ffffff&bgcolor=111827`;

  function copy() { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  function copyStandalone() { navigator.clipboard.writeText(standaloneUrl); setCopiedStandalone(true); setTimeout(() => setCopiedStandalone(false), 2000); }

  return (
    <div>
      <div className="page-header"><div className="page-title">Embed Code</div><div className="page-subtitle">Add CivicBot to your city website — or share a direct link with residents</div></div>

      {/* Website Widget */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">🌐 Website Widget</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Embed on your city website</span></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>Paste this script tag just before the closing <code style={{background:'var(--surface3)',padding:'2px 6px',borderRadius:4,fontSize:12}}>&lt;/body&gt;</code> tag on your city website. The chat bubble will appear in the bottom-right corner.</p>
          <div className="embed-box">
            <pre className="embed-code">{code}</pre>
            <button className={`copy-btn ${copied?'copied':''}`} onClick={copy}>{copied?'✓ Copied':'Copy'}</button>
          </div>
          <div className="widget-preview">
            <div className="widget-preview-title">Live Preview</div>
            <div className="widget-chat"><div className="widget-chat-name">CivicBot</div>Hi! I'm CivicBot. I can help you submit a service request to the city. What issue would you like to report today?</div>
            <div className="widget-bubble" style={{background:`linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`}}>{user?.cityLogo ? <img src={user.cityLogo} alt="logo" style={{width:28,height:28,borderRadius:6,objectFit:"contain"}} /> : <svg width="22" height="22" viewBox="0 0 36 36" fill="none"><rect x="5" y="16" width="5" height="15" rx="1" fill="white"/><rect x="12" y="11" width="5" height="20" rx="1" fill="white"/><rect x="19" y="6" width="5" height="25" rx="1" fill="white"/><rect x="26" y="13" width="4" height="18" rx="1" fill="#93c5fd"/></svg>}</div>
          </div>
        </div>
      </div>

      {/* Standalone Page + QR Code */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📱 Standalone Resident Page</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Share directly — no website needed</span></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>
            Share this link via email, social media, or print the QR code for city signage, flyers, and bulletins. Residents go straight to your CivicBot without needing your city website.
          </p>
          <div className="embed-box" style={{marginBottom:20}}>
            <pre className="embed-code">{standaloneUrl}</pre>
            <button className={`copy-btn ${copiedStandalone?'copied':''}`} onClick={copyStandalone}>{copiedStandalone?'✓ Copied':'Copy'}</button>
          </div>

          <div style={{display:'flex',gap:24,alignItems:'flex-start',flexWrap:'wrap'}}>
            {/* QR Code */}
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:12}}>QR Code</div>
              <div style={{background:'#111827',border:'1px solid var(--border)',borderRadius:12,padding:16,display:'inline-block'}}>
                <img src={qrUrl} alt="QR Code" width={160} height={160} style={{display:'block',borderRadius:4}} />
              </div>
              <div style={{marginTop:10,display:'flex',gap:8,justifyContent:'center'}}>
                <a href={qrUrl} download={`civicbot-qr-${cityId}.png`} style={{padding:'6px 14px',background:'var(--surface2)',border:'1px solid var(--border-light)',borderRadius:6,color:'var(--text-dim)',fontSize:11,textDecoration:'none',transition:'all 0.15s'}}
                  onMouseOver={e=>e.target.style.borderColor='var(--accent)'}
                  onMouseOut={e=>e.target.style.borderColor='var(--border-light)'}>
                  ⬇ Download PNG
                </a>
              </div>
            </div>

            {/* Usage tips */}
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:12}}>Ways to share</div>
              {[
                ['📧', 'Email blast', 'Include the link or QR code in your city newsletter'],
                ['🏛️', 'City signage', 'Print and post QR codes at city hall, public works buildings'],
                ['📱', 'Social media', "Post the link on your city's Facebook, Nextdoor, or X page"],
                ['📰', 'City website', 'Add the link or use the embed code on your website'],
                ['🖨️', 'Flyers', 'Include on utility bills, door hangers, or community flyers'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{display:'flex',gap:10,marginBottom:10,alignItems:'flex-start'}}>
                  <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>{title}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.4}}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Test your widget */}
      <div className="card">
        <div className="card-header"><div className="card-title">🧪 Test Your Widget</div></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>Open your standalone page to test the full submission flow before sharing with residents.</p>
          <a href={standaloneUrl} target="_blank" rel="noreferrer" style={{display:'inline-block',padding:'10px 20px',background:'var(--accent)',borderRadius:8,color:'white',fontSize:13,fontWeight:600,textDecoration:'none',transition:'background 0.2s'}}
            onMouseOver={e=>e.currentTarget.style.background='#2563EB'}
            onMouseOut={e=>e.currentTarget.style.background='var(--accent)'}>
            Open My CivicBot Page →
          </a>
        </div>
      </div>
    </div>
  );
}


// ─── Geofence Validator Component ────────────────────────────────────────────
function GeofenceValidator({ cityName, state, onChange, onVerified }) {
  const [status, setStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid' | 'error'
  const [validatedName, setValidatedName] = useState('');
  const [matchedName, setMatchedName] = useState('');

  async function verify() {
    if (!cityName.trim() || !state) return;
    setStatus('checking');
    try {
      const res = await apiFetch(`/api/verify-place?name=${encodeURIComponent(cityName.trim())}&state=${encodeURIComponent(state)}`);
      const data = await res.json();
      if (data.found) {
        setMatchedName(data.matchedName);
        setStatus('valid');
        setValidatedName(cityName.trim());
        // Pass verified coordinates up so they can be saved with settings
        if (onVerified) onVerified({ lat: data.lat, lng: data.lng, matchedName: data.matchedName });
      } else {
        setStatus('invalid');
        setMatchedName('');
      }
    } catch (err) {
      console.error('Geofence verify error:', err);
      setStatus('error');
    }
  }



  const inputBorder = status === 'valid' ? '#10B981' : status === 'invalid' ? '#EF4444' : 'var(--border-light)';

  return (
    <div className="setting-field">
      <label className="setting-label">Census Place Name</label>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <input
          className="setting-input"
          value={cityName}
          onChange={e => { onChange(e.target.value); setStatus(null); }}
          placeholder="e.g. Highland"
          style={{flex:1, borderColor: inputBorder}}
        />
        <button
          onClick={verify}
          disabled={!cityName.trim() || status === 'checking'}
          style={{padding:'10px 16px',background:'var(--accent)',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',cursor:'pointer',whiteSpace:'nowrap',opacity:(!cityName.trim() || status === 'checking') ? 0.5 : 1}}
        >
          {status === 'checking' ? '⏳ Checking...' : '🔍 Verify'}
        </button>
      </div>

      {/* Status messages */}
      {status === 'valid' && (
        <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981',marginTop:8}}>
          ✅ <strong>Verified!</strong> Found "{matchedName}" in the Census database for {state}.
          {matchedName !== validatedName && <span style={{color:'#94A3B8'}}> (You entered "{validatedName}" — consider updating to match exactly: "{matchedName}")</span>}
        </div>
      )}
      {status === 'invalid' && (
        <div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#EF4444',marginTop:8}}>
          ❌ <strong>Not found.</strong> "{cityName}" was not found in the Census database for {state}. Check spelling or try without "City of" / "Town of" prefix. Visit <a href="https://www.census.gov/quickfacts/" target="_blank" style={{color:'#F87171'}}>census.gov/quickfacts</a> to find the exact name.
        </div>
      )}
      {status === 'error' && (
        <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B',marginTop:8}}>
          ⚠️ Could not reach Census database. Check your internet connection and try again.
        </div>
      )}
      {status === null && (
        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,lineHeight:1.5}}>
          Enter your city name and click Verify. We'll automatically look it up and confirm it exists in the Census database.
        </div>
      )}
    </div>
  );
}

// ─── Emergency Routing Page ────────────────────────────────────────────────────
const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function EmergencyRoutingPage({ user, showToast }) {
  const cityId = user?.cityId;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState({
    businessPhone: '', businessLabel: 'Business Hours',
    afterHoursPhone: '', afterHoursLabel: 'After Hours',
    businessHoursStart: '08:00', businessHoursEnd: '17:00',
    businessDays: [1,2,3,4,5], timezone: 'America/Denver',
  });
  const [templates, setTemplates] = useState([]);
  const [overrides, setOverrides] = useState({}); // problemSid -> { isEmergency, emergencyMessage }
  const [savingSid, setSavingSid] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!cityId) return;
    let alive = true;
    (async () => {
      try {
        const [cfgRes, tplRes, ovRes] = await Promise.all([
          apiFetch(`/api/config/${cityId}`).then(r => r.json()),
          apiFetch(`/api/sr-templates?cityId=${cityId}`).then(r => r.json()),
          apiFetch(`/api/admin/sr-template-overrides/${cityId}`).then(r => r.json()),
        ]);
        if (!alive) return;
        if (cfgRes?.config?.emergency) {
          const e = cfgRes.config.emergency;
          setContacts({
            businessPhone: e.businessPhone || '',
            businessLabel: e.businessLabel || 'Business Hours',
            afterHoursPhone: e.afterHoursPhone || '',
            afterHoursLabel: e.afterHoursLabel || 'After Hours',
            businessHoursStart: e.businessHoursStart || '08:00',
            businessHoursEnd: e.businessHoursEnd || '17:00',
            businessDays: Array.isArray(e.businessDays) ? e.businessDays : [1,2,3,4,5],
            timezone: e.timezone || 'America/Denver',
          });
        }
        const selArr = (cfgRes?.config?.selectedTemplates || []).map(String);
        const selectedSet = new Set(selArr);
        const aliases = cfgRes?.config?.aliases || {};
        // Show the templates the city has selected for the widget. An EMPTY selection
        // means "all public templates" (same rule the widget/backend use) — otherwise a
        // brand-new city that hasn't saved a selection yet sees an empty Emergency Routing
        // page until they re-save SR Templates.
        const allTpls = selArr.length > 0
          ? (tplRes?.templates || []).filter(t => selectedSet.has(String(t.ProblemSid)))
          : (tplRes?.templates || []);
        const tplsWithAlias = allTpls.map(t => ({ ...t, displayName: aliases[t.ProblemSid] || t.Description }));
        tplsWithAlias.sort((a,b) => (a.displayName || '').localeCompare(b.displayName || ''));
        setTemplates(tplsWithAlias);
        const ovMap = {};
        (ovRes?.overrides || []).forEach(o => { ovMap[String(o.problemSid)] = o; });
        setOverrides(ovMap);
      } catch (e) {
        console.error(e);
        showToast('Failed to load emergency settings', 'error');
      } finally {
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cityId, showToast]);

  async function saveContacts() {
    setSaving(true);
    try {
      const r = await apiFetch(`/api/admin/emergency-config/${cityId}`, {
        method: 'POST',
        body: JSON.stringify(contacts),
      });
      const d = await r.json();
      if (d.success) { showToast('Emergency contacts saved'); setSavedAt(Date.now()); }
      else showToast(d.error || 'Save failed', 'error');
    } catch { showToast('Save failed', 'error'); }
    setSaving(false);
  }

  async function saveOverride(problemSid, patch) {
    const current = overrides[problemSid] || { isEmergency: false, emergencyMessage: '' };
    const next = { ...current, ...patch };
    setOverrides(o => ({ ...o, [problemSid]: next }));
    setSavingSid(problemSid);
    try {
      const r = await apiFetch(`/api/admin/sr-template-overrides/${cityId}/${encodeURIComponent(problemSid)}`, {
        method: 'PATCH',
        body: JSON.stringify(next),
      });
      const d = await r.json();
      if (!d.success) showToast(d.error || 'Save failed', 'error');
    } catch { showToast('Save failed', 'error'); }
    setSavingSid(null);
  }

  function toggleDay(d) {
    setContacts(c => {
      const has = c.businessDays.includes(d);
      return { ...c, businessDays: has ? c.businessDays.filter(x => x !== d) : [...c.businessDays, d].sort() };
    });
  }

  const flaggedCount = Object.values(overrides).filter(o => (o.deflectionType && o.deflectionType !== 'none') || (!o.deflectionType && o.isEmergency)).length;

  if (!cityId) {
    return <div className="page-content"><div className="card"><div className="card-body" style={{padding:24,color:'var(--text-muted)'}}>Emergency Routing is per-city. Open a specific city to configure.</div></div></div>;
  }
  if (loading) return <div className="page-content"><div className="card"><div className="card-body" style={{padding:24,color:'var(--text-muted)'}}>Loading…</div></div></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">🚨 Emergency Routing</div>
          <div className="page-subtitle">Flag categories that need a phone call, not a form. Residents see a click-to-call card instead of the SR flow.</div>
        </div>
      </div>

      {/* Contacts card */}
      <div className="card">
        <div className="card-header"><div className="card-title">Emergency Phone Numbers</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Used for every flagged category</span></div>
        <div className="card-body">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            <div className="setting-field"><label className="setting-label">Business-Hours Phone</label><input className="setting-input" value={contacts.businessPhone} onChange={e => setContacts(c => ({...c, businessPhone: e.target.value}))} placeholder="(555) 123-4567" /></div>
            <div className="setting-field"><label className="setting-label">Business-Hours Label</label><input className="setting-input" value={contacts.businessLabel} onChange={e => setContacts(c => ({...c, businessLabel: e.target.value}))} placeholder="Public Works" /></div>
            <div className="setting-field"><label className="setting-label">After-Hours Phone</label><input className="setting-input" value={contacts.afterHoursPhone} onChange={e => setContacts(c => ({...c, afterHoursPhone: e.target.value}))} placeholder="(555) 987-6543" /></div>
            <div className="setting-field"><label className="setting-label">After-Hours Label</label><input className="setting-input" value={contacts.afterHoursLabel} onChange={e => setContacts(c => ({...c, afterHoursLabel: e.target.value}))} placeholder="Police Dispatch" /></div>
          </div>

          <div style={{marginTop:20,paddingTop:20,borderTop:'1px solid var(--border)'}}>
            <div style={{fontSize:12,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>Business Hours</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:12}}>
              <div className="setting-field"><label className="setting-label">Open</label><input className="setting-input" type="time" value={contacts.businessHoursStart} onChange={e => setContacts(c => ({...c, businessHoursStart: e.target.value}))} /></div>
              <div className="setting-field"><label className="setting-label">Close</label><input className="setting-input" type="time" value={contacts.businessHoursEnd} onChange={e => setContacts(c => ({...c, businessHoursEnd: e.target.value}))} /></div>
              <div className="setting-field"><label className="setting-label">Timezone</label><select className="setting-input" value={contacts.timezone} onChange={e => setContacts(c => ({...c, timezone: e.target.value}))}><option value="America/New_York">Eastern (ET)</option><option value="America/Chicago">Central (CT)</option><option value="America/Denver">Mountain (MT)</option><option value="America/Phoenix">Mountain – no DST (AZ)</option><option value="America/Los_Angeles">Pacific (PT)</option><option value="America/Anchorage">Alaska (AKT)</option><option value="Pacific/Honolulu">Hawaii (HT)</option></select></div>
            </div>
            <div className="setting-label">Open Days</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {DAY_LABELS.map((lbl, idx) => {
                const on = contacts.businessDays.includes(idx);
                return <button key={idx} onClick={() => toggleDay(idx)} style={{padding:'7px 14px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',border:`1px solid ${on?'var(--accent)':'var(--border-light)'}`,background:on?'var(--accent-dim)':'transparent',color:on?'var(--accent-light)':'var(--text-muted)'}}>{lbl}</button>;
              })}
            </div>
          </div>

          <div style={{marginTop:20,display:'flex',justifyContent:'flex-end',gap:10,alignItems:'center'}}>
            {savedAt && <span style={{fontSize:12,color:'var(--success)'}}>✓ Saved</span>}
            <button className="btn btn-primary" onClick={saveContacts} disabled={saving}>{saving ? 'Saving…' : 'Save Contacts'}</button>
          </div>
        </div>
      </div>

      {/* Per-template overrides */}
      <div className="card" style={{marginTop:20}}>
        <div className="card-header">
          <div className="card-title">Category Notices & Emergency Routing</div>
          <span style={{fontSize:12,color:'var(--text-muted)'}}>{flaggedCount} of {templates.length} configured · 🔴 Emergency = call-only · 🟡 Advisory = notice, can continue · ☑️ Acknowledge = must check a box to continue</span>
        </div>
        <div className="card-body">
          {templates.length === 0 ? (
            <div style={{padding:20,color:'var(--text-muted)',fontSize:13}}>No SR templates selected yet. Go to SR Templates and choose which ones the widget should offer first.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {templates.map(t => {
                const o = overrides[String(t.ProblemSid)] || { isEmergency: false, emergencyMessage: '' };
                // Effective tier: deflectionType wins; legacy rows derive from isEmergency
                const tier = o.deflectionType || (o.isEmergency ? 'emergency' : 'none');
                const on = tier !== 'none' && tier !== '';
                const isEmerg = tier === 'emergency';
                const accent = isEmerg ? '#EF4444' : '#F59E0B';
                const accentText = isEmerg ? '#FCA5A5' : '#FCD34D';
                const isSaving = savingSid === String(t.ProblemSid);
                const setTier = (nt) => saveOverride(String(t.ProblemSid), { deflectionType: nt, isEmergency: nt === 'emergency' });
                const TIERS = [ ['none','None'], ['emergency','🔴 Emergency'], ['advisory','🟡 Advisory'], ['acknowledge','☑️ Acknowledge'], ['conditional','❓ Conditional'] ];
                return (
                  <div key={t.ProblemSid} style={{border:`1px solid ${on?accent+'50':'var(--border)'}`,background:on?accent+'08':'var(--surface2)',borderRadius:10,padding:12}}>
                    <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                      <div style={{flex:1,minWidth:180}}>
                        <div style={{fontSize:14,fontWeight:600,color:on?accentText:'var(--text)'}}>{isEmerg && '🚨 '}{tier==='advisory' && '🟡 '}{tier==='acknowledge' && '☑️ '}{t.displayName}</div>
                        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{t.ProblemCode} · ProblemSid {t.ProblemSid}</div>
                      </div>
                      <div style={{display:'flex',gap:4}}>
                        {TIERS.map(([val,label]) => (
                          <button key={val} onClick={() => setTier(val)}
                            style={{padding:'5px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',
                              border:`1px solid ${tier===val ? (val==='emergency'?'#EF4444':val==='none'?'var(--border-light)':'#F59E0B') : 'var(--border)'}`,
                              background: tier===val ? (val==='emergency'?'#EF444420':val==='none'?'var(--surface3)':'#F59E0B20') : 'var(--surface2)',
                              color: tier===val ? (val==='emergency'?'#FCA5A5':val==='none'?'var(--text)':'#FCD34D') : 'var(--text-muted)'}}>
                            {label}
                          </button>
                        ))}
                      </div>
                      {isSaving && <span style={{fontSize:11,color:'var(--text-muted)'}}>Saving…</span>}
                    </div>
                    {on && (
                      <div style={{marginTop:12,paddingTop:12,borderTop:`1px dashed ${accent}30`}}>
                        {tier==='conditional' && (
                          <div style={{marginBottom:12}}>
                            <label className="setting-label" style={{color:accentText}}>Yes/No question asked before the request <span style={{color:'var(--text-muted)',fontWeight:400}}>(Yes → continue, No → show the notice below)</span></label>
                            <input className="setting-input"
                              value={o.gateQuestion || ''}
                              onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, gateQuestion: e.target.value } }))}
                              onBlur={() => saveOverride(String(t.ProblemSid), { gateQuestion: o.gateQuestion || '' })}
                              placeholder="Have you verified it's your pickup day?" />
                          </div>
                        )}
                        {(tier==='advisory' || tier==='acknowledge' || tier==='conditional') && (
                          <div style={{marginBottom:12}}>
                            <label className="setting-label" style={{color:accentText}}>Notice title</label>
                            <input className="setting-input"
                              value={o.noticeTitle || ''}
                              onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, noticeTitle: e.target.value } }))}
                              onBlur={() => saveOverride(String(t.ProblemSid), { noticeTitle: o.noticeTitle || '' })}
                              placeholder="The City Needs More Information" />
                          </div>
                        )}
                        <label className="setting-label" style={{color:accentText}}>{isEmerg ? 'Emergency Message (shown to resident)' : (tier==='conditional' ? 'Message shown if they answer No' : 'Notice message (shown to resident)')}</label>
                        <textarea
                          className="setting-input"
                          rows={2}
                          value={o.emergencyMessage}
                          onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, emergencyMessage: e.target.value } }))}
                          onBlur={() => saveOverride(String(t.ProblemSid), { emergencyMessage: o.emergencyMessage })}
                          placeholder={`Active ${t.displayName.toLowerCase()} needs immediate attention — please call us directly.`}
                          style={{resize:'vertical',fontFamily:'inherit'}}
                        />
                        {(tier==='advisory' || tier==='acknowledge' || tier==='conditional') && (
                          <div style={{marginTop:12,display:'grid',gridTemplateColumns:'2fr 1fr',gap:12}}>
                            <div>
                              <label className="setting-label" style={{color:accentText}}>Link URL <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional — e.g. trash schedule, fee page)</span></label>
                              <input className="setting-input"
                                value={o.noticeLinkUrl || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, noticeLinkUrl: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { noticeLinkUrl: o.noticeLinkUrl || '' })}
                                placeholder="https://www.yourcity.gov/trash-schedule" />
                            </div>
                            <div>
                              <label className="setting-label" style={{color:accentText}}>Link button label</label>
                              <input className="setting-input"
                                value={o.noticeLinkLabel || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, noticeLinkLabel: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { noticeLinkLabel: o.noticeLinkLabel || '' })}
                                placeholder="Check your pickup day" />
                            </div>
                          </div>
                        )}
                        {tier==='acknowledge' && (
                          <div style={{marginTop:12}}>
                            <label className="setting-label" style={{color:accentText}}>Acknowledgment checkbox text <span style={{color:'var(--text-muted)',fontWeight:400}}>(resident must check this to continue)</span></label>
                            <input className="setting-input"
                              value={o.ackText || ''}
                              onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, ackText: e.target.value } }))}
                              onBlur={() => saveOverride(String(t.ProblemSid), { ackText: o.ackText || '' })}
                              placeholder="I agree to pay the extra pickup fee and affirm I am the account holder" />
                          </div>
                        )}
                        <div style={{marginTop:12}}>
                          <label className="setting-label" style={{color:'#FCA5A5'}}>Custom phone numbers for this category <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional — routes to a specific team)</span></label>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:6}}>
                            <div>
                              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4}}>Business hours</div>
                              <input className="setting-input" style={{marginBottom:6}}
                                value={o.emergencyPhoneBusinessLabel || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, emergencyPhoneBusinessLabel: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { emergencyPhoneBusinessLabel: o.emergencyPhoneBusinessLabel || '' })}
                                placeholder={contacts.businessLabel || 'Business Hours'} />
                              <input className="setting-input"
                                value={o.emergencyPhoneBusiness || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, emergencyPhoneBusiness: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { emergencyPhoneBusiness: o.emergencyPhoneBusiness || '' })}
                                placeholder={contacts.businessPhone ? `Default: ${contacts.businessPhone}` : 'e.g. 916-555-0101'} />
                            </div>
                            <div>
                              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4}}>After hours</div>
                              <input className="setting-input" style={{marginBottom:6}}
                                value={o.emergencyPhoneAfterhoursLabel || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, emergencyPhoneAfterhoursLabel: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { emergencyPhoneAfterhoursLabel: o.emergencyPhoneAfterhoursLabel || '' })}
                                placeholder={contacts.afterHoursLabel || 'After Hours'} />
                              <input className="setting-input"
                                value={o.emergencyPhoneAfterhours || ''}
                                onChange={e => setOverrides(prev => ({ ...prev, [t.ProblemSid]: { ...o, emergencyPhoneAfterhours: e.target.value } }))}
                                onBlur={() => saveOverride(String(t.ProblemSid), { emergencyPhoneAfterhours: o.emergencyPhoneAfterhours || '' })}
                                placeholder={contacts.afterHoursPhone ? `Default: ${contacts.afterHoursPhone}` : 'e.g. 916-555-0199'} />
                            </div>
                          </div>
                          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6}}>Leave blank to use the city-wide emergency numbers above.</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Review Queue Page ─────────────────────────────────────────────────────────
function ReviewQueuePage({ user, showToast }) {
  const cityId = user?.cityId;
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [intakeMode, setIntakeMode] = useState('direct');
  const [requireContact, setRequireContact] = useState(false);
  const [contactRequirements, setContactRequirements] = useState({ name: true, phone: false, email: false });
  const [notifyApproval, setNotifyApproval] = useState(true);
  const [notifyRejection, setNotifyRejection] = useState(false);
  const [allowCmmsLookup, setAllowCmmsLookup] = useState(true);
  const [clarifyingQuestions, setClarifyingQuestions] = useState(true);
  const [pendingMsg, setPendingMsg] = useState('');
  const [pendingMsgDirty, setPendingMsgDirty] = useState(false);
  const [savingMsg, setSavingMsg] = useState(false);
  const [reviewEmails, setReviewEmails] = useState('');
  const [reviewEmailsDirty, setReviewEmailsDirty] = useState(false);
  const [savingReviewEmails, setSavingReviewEmails] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [isCmms, setIsCmms] = useState(true);

  async function loadAll() {
    if (!cityId) return;
    setLoading(true);
    try {
      const [qRes, cfgRes, intakeRes] = await Promise.all([
        apiFetch(`/api/admin/review-queue/${cityId}`).then(r => r.json()),
        apiFetch(`/api/config/${cityId}`).then(r => r.json()),
        // Staff notification emails live behind auth (never in the public config payload)
        apiFetch(`/api/admin/intake-config/${cityId}`).then(r => r.json()).catch(() => ({})),
      ]);
      setReviewEmails(intakeRes?.reviewNotificationEmails || '');
      setReviewEmailsDirty(false);
      setSubs(qRes.submissions || []);
      const cfg = cfgRes?.config || {};
      setIntakeMode(cfg.intakeMode || 'direct');
      setRequireContact(!!cfg.requireContactInfo);
      if (cfg.contactRequirements) {
        setContactRequirements(cfg.contactRequirements);
      } else {
        // No per-field config saved yet — derive from the legacy require_contact_info
        // flag and persist it once so the widget reads concrete per-field values
        // instead of falling back to "all required".
        const derived = { name: true, phone: !!cfg.requireContactInfo, email: false };
        setContactRequirements(derived);
        apiFetch(`/api/admin/intake-config/${cityId}`, {
          method: 'POST',
          body: JSON.stringify({
            intakeMode: cfg.intakeMode || 'direct',
            requireContactInfo: !!cfg.requireContactInfo,
            contactRequirements: derived,
            pendingReviewMessage: cfg.pendingReviewMessage,
            notifyOnApproval: cfg.notifyOnApproval !== false,
            notifyOnRejection: !!cfg.notifyOnRejection,
            allowCmmsStatusLookup: cfg.allowCmmsStatusLookup !== false,
          }),
        }).catch(() => {});
      }
      setNotifyApproval(cfg.notifyOnApproval !== false);
      setNotifyRejection(!!cfg.notifyOnRejection);
      setAllowCmmsLookup(cfg.allowCmmsStatusLookup !== false);
      setClarifyingQuestions(cfg.clarifyingQuestionsEnabled !== false);
      setPendingMsg(cfg.pendingReviewMessage || 'Your request has been received and is awaiting review by city staff. They will follow up shortly.');
      setPendingMsgDirty(false);
      // CMMS-backed (Cityworks today, others later) vs email-only
      setIsCmms(!(cfg.emailDelivery));
    } catch (e) {
      showToast('Failed to load review queue', 'error');
    }
    setLoading(false);
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [cityId]);

  async function saveCfg(patch) {
    const next = { intakeMode, requireContactInfo: requireContact, contactRequirements, pendingReviewMessage: pendingMsg, notifyOnApproval: notifyApproval, notifyOnRejection: notifyRejection, allowCmmsStatusLookup: allowCmmsLookup, clarifyingQuestionsEnabled: clarifyingQuestions, ...patch };
    setIntakeMode(next.intakeMode);
    setRequireContact(next.requireContactInfo);
    if (next.contactRequirements) setContactRequirements(next.contactRequirements);
    if (typeof next.pendingReviewMessage === 'string') setPendingMsg(next.pendingReviewMessage);
    if (typeof next.notifyOnApproval === 'boolean') setNotifyApproval(next.notifyOnApproval);
    if (typeof next.notifyOnRejection === 'boolean') setNotifyRejection(next.notifyOnRejection);
    if (typeof next.allowCmmsStatusLookup === 'boolean') setAllowCmmsLookup(next.allowCmmsStatusLookup);
    if (typeof next.clarifyingQuestionsEnabled === 'boolean') setClarifyingQuestions(next.clarifyingQuestionsEnabled);
    setSavingCfg(true);
    try {
      const r = await apiFetch(`/api/admin/intake-config/${cityId}`, { method: 'POST', body: JSON.stringify(next) });
      const d = await r.json();
      if (!d.success) showToast(d.error || 'Save failed', 'error');
      else if (patch.pendingReviewMessage !== undefined) setPendingMsgDirty(false);
    } catch { showToast('Save failed', 'error'); }
    setSavingCfg(false);
  }

  async function savePendingMsg() {
    setSavingMsg(true);
    await saveCfg({ pendingReviewMessage: pendingMsg });
    setSavingMsg(false);
    showToast('Message updated');
  }

  async function approve(id) {
    setActingId(id);
    try {
      const r = await apiFetch(`/api/admin/review-queue/${id}/approve`, { method: 'POST' });
      const d = await r.json();
      if (d.success) { showToast(`Approved — pushed to your CMMS${d.srId ? ` (SR ${d.srId})` : ''}`); loadAll(); }
      else showToast(d.error || 'Approve failed', 'error');
    } catch { showToast('Approve failed', 'error'); }
    setActingId(null);
  }

  async function bulkApprove() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} submission${ids.length===1?'':'s'} and push to your CMMS?`)) return;
    setBulkApproving(true);
    try {
      const r = await apiFetch(`/api/admin/review-queue/bulk-approve`, { method: 'POST', body: JSON.stringify({ ids }) });
      const d = await r.json();
      if (d.success) {
        showToast(`${d.okCount} approved${d.failCount ? `, ${d.failCount} failed` : ''}`, d.failCount ? 'error' : 'success');
        setSelectedIds(new Set());
        loadAll();
      } else {
        showToast(d.error || 'Bulk approve failed', 'error');
      }
    } catch { showToast('Bulk approve failed', 'error'); }
    setBulkApproving(false);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function selectAllVisible(visibleIds) {
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  }
  async function reject(id) {
    setActingId(id);
    try {
      const r = await apiFetch(`/api/admin/review-queue/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason: rejectReason }) });
      const d = await r.json();
      if (d.success) { showToast('Rejected'); setRejectingId(null); setRejectReason(''); loadAll(); }
      else showToast(d.error || 'Reject failed', 'error');
    } catch { showToast('Reject failed', 'error'); }
    setActingId(null);
  }

  if (!cityId) return <div className="page-content"><div className="card"><div className="card-body" style={{padding:24,color:'var(--text-muted)'}}>Open a specific city to manage its review queue.</div></div></div>;

  if (!loading && !isCmms) {
    return (
      <div className="page-content">
        <div className="page-header">
          <div>
            <div className="page-title">📥 Review Queue</div>
            <div className="page-subtitle">Triage resident submissions before they hit your CMMS.</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{padding:24}}>
            <div style={{fontSize:14,color:'var(--text)',marginBottom:8,fontWeight:600}}>Review Queue is for CMMS-integrated cities only.</div>
            <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.5}}>
              Your city currently uses <b>email delivery</b> for service requests. The Review Queue is designed to triage submissions before they're pushed to your CMMS (e.g. Cityworks, Trimble Unity). To use it, connect a CMMS in <b>Settings → Cityworks Integration</b>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filtered = subs.filter(s => filter === 'all' ? true : s.review_status === filter);
  const counts = { pending: subs.filter(s=>s.review_status==='pending').length, approved: subs.filter(s=>s.review_status==='approved').length, rejected: subs.filter(s=>s.review_status==='rejected').length };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">📥 Review Queue</div>
          <div className="page-subtitle">Triage resident submissions before they hit your CMMS.</div>
        </div>
      </div>

      {/* Intake settings are city-admin territory — review_admin only works the queue
          (backend returns 403 on intake-config for them anyway) */}
      {user?.role !== 'review_admin' && (
      <div className="card">
        <div className="card-header"><div className="card-title">Intake Mode</div></div>
        <div className="card-body">
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div onClick={() => saveCfg({ intakeMode: 'direct' })} style={{display:'flex',gap:12,alignItems:'flex-start',padding:14,border:`2px solid ${intakeMode==='direct'?'var(--accent)':'var(--border)'}`,borderRadius:10,cursor:'pointer',background:intakeMode==='direct'?'var(--accent-dim)':'var(--surface2)'}}>
              <div style={{fontSize:20,marginTop:-2}}>{intakeMode==='direct' ? '🔘' : '⚪'}</div>
              <div>
                <div style={{fontWeight:700,color:'var(--text)'}}>Direct to CMMS (default)</div>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>Submissions create SRs immediately. Use this for most cities.</div>
              </div>
            </div>
            <div onClick={() => saveCfg({ intakeMode: 'review_all' })} style={{display:'flex',gap:12,alignItems:'flex-start',padding:14,border:`2px solid ${intakeMode==='review_all'?'var(--accent)':'var(--border)'}`,borderRadius:10,cursor:'pointer',background:intakeMode==='review_all'?'var(--accent-dim)':'var(--surface2)'}}>
              <div style={{fontSize:20,marginTop:-2}}>{intakeMode==='review_all' ? '🔘' : '⚪'}</div>
              <div>
                <div style={{fontWeight:700,color:'var(--text)'}}>Pending Review (all submissions)</div>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>Every submission queues here for staff approval before going to your CMMS.</div>
              </div>
            </div>
          </div>
          {intakeMode === 'review_all' && (
            <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
              <div style={{fontSize:12,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,marginBottom:8}}>Message shown to resident</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:8}}>This appears in the chatbot's confirmation card after they submit. Keep it short and reassuring.</div>
              <textarea
                className="setting-input"
                rows={3}
                value={pendingMsg}
                onChange={e => { setPendingMsg(e.target.value); setPendingMsgDirty(true); }}
                placeholder="Your request has been received and is awaiting review by city staff. They will follow up shortly."
                style={{resize:'vertical',fontFamily:'inherit',width:'100%'}}
              />
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
                <div style={{fontSize:11,color:'var(--text-muted)'}}>
                  <span style={{opacity:0.7}}>Preview:</span>{' '}
                  <span style={{color:'#F59E0B'}}>📥 Pending review</span> <span style={{color:'var(--text)'}}>#CB-XXXX-XXXXX</span> — {pendingMsg || <i>(empty)</i>}
                </div>
                <button disabled={!pendingMsgDirty || savingMsg} onClick={savePendingMsg} style={{padding:'6px 16px',borderRadius:6,border:'none',background: pendingMsgDirty ? 'var(--accent)' : 'var(--surface3)',color: pendingMsgDirty ? '#fff' : 'var(--text-muted)',fontWeight:700,fontSize:12,cursor: pendingMsgDirty ? 'pointer' : 'default'}}>{savingMsg ? 'Saving…' : (pendingMsgDirty ? 'Save Message' : '✓ Saved')}</button>
              </div>
              {/* #6: staff alert recipients — emailed whenever a new submission hits the queue */}
              <div style={{marginTop:20,paddingTop:16,borderTop:'1px solid var(--border)'}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,marginBottom:8}}>Notify staff of new submissions</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:8}}>Everyone on this list gets a brief email (type, location, reference #) each time a resident submission lands in the review queue. Separate multiple addresses with commas. Leave blank to disable.</div>
                <input
                  className="setting-input"
                  value={reviewEmails}
                  onChange={e => { setReviewEmails(e.target.value); setReviewEmailsDirty(true); }}
                  placeholder="staff1@yourcity.gov, staff2@yourcity.gov"
                  style={{width:'100%'}}
                />
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
                  <button disabled={!reviewEmailsDirty || savingReviewEmails} onClick={async () => {
                    setSavingReviewEmails(true);
                    try {
                      const r = await apiFetch(`/api/admin/intake-config/${cityId}`, { method: 'POST', body: JSON.stringify({ reviewNotificationEmails: reviewEmails }) });
                      const d = await r.json();
                      if (d.success) { setReviewEmailsDirty(false); showToast('Staff notification list saved'); }
                      else showToast(d.error || 'Failed to save', 'error');
                    } catch { showToast('Failed to save — check connection', 'error'); }
                    setSavingReviewEmails(false);
                  }} style={{padding:'6px 16px',borderRadius:6,border:'none',background: reviewEmailsDirty ? 'var(--accent)' : 'var(--surface3)',color: reviewEmailsDirty ? '#fff' : 'var(--text-muted)',fontWeight:700,fontSize:12,cursor: reviewEmailsDirty ? 'pointer' : 'default'}}>{savingReviewEmails ? 'Saving…' : (reviewEmailsDirty ? 'Save List' : '✓ Saved')}</button>
                </div>
              </div>
            </div>
          )}
          <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
            <div style={{marginBottom:4,fontWeight:600,fontSize:13,color:'#334155'}}>Required Contact Fields</div>
            <div style={{fontSize:12,color:'#64748b',marginBottom:8}}>Choose which fields residents must provide. If all are off, anonymous submissions are allowed.</div>
            {[
              { key: 'name', label: 'Name', desc: 'Resident full name' },
              { key: 'phone', label: 'Phone', desc: 'Phone number (area code required)' },
              { key: 'email', label: 'Email', desc: 'Email address' },
            ].map(({ key, label, desc }) => (
              <div key={key} onClick={() => {
                const updated = { ...contactRequirements, [key]: !contactRequirements[key] };
                setContactRequirements(updated);
                saveCfg({ contactRequirements: updated, requireContactInfo: updated.name || updated.phone || updated.email });
              }} style={{display:'flex',gap:12,alignItems:'center',padding:'8px 0',cursor:'pointer',borderBottom:'1px solid #f1f5f9'}}>
                <button className={`toggle-switch ${contactRequirements[key]?'on':''}`} style={{flexShrink:0}}><div className="toggle-thumb" /></button>
                <div>
                  <div style={{fontWeight:500,fontSize:13}}>{label}</div>
                  <div style={{fontSize:11,color:'#94a3b8'}}>{desc}</div>
                </div>
              </div>
            ))}
            {isCmms && (
              <div onClick={() => saveCfg({ allowCmmsStatusLookup: !allowCmmsLookup })} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 0',cursor:'pointer'}}>
                <button className={`toggle-switch ${allowCmmsLookup?'on':''}`}><div className="toggle-thumb" /></button>
                <div>
                  <div style={{fontWeight:600,color:'var(--text)',fontSize:13}}>Let residents look up CMMS Service Request status by number</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>When on, residents can type any of your CMMS Service Request numbers (e.g. <code>2654</code>) into the chatbot to see Open / In Progress / Closed status — even Service Requests created by staff after a phone call. Turn off if you don't want this exposed.</div>
                </div>
              </div>
            )}
            <div onClick={() => saveCfg({ clarifyingQuestionsEnabled: !clarifyingQuestions })} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 0',cursor:'pointer'}}>
              <button className={`toggle-switch ${clarifyingQuestions?'on':''}`}><div className="toggle-thumb" /></button>
              <div>
                <div style={{fontWeight:600,color:'var(--text)',fontSize:13}}>Smart clarifying questions</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>When a resident's report is vague, the chatbot asks one AI follow-up question to gather details (e.g. "Which cart?"). Recommended.</div>
              </div>
            </div>
            {intakeMode === 'review_all' && (
              <>
                <div onClick={() => saveCfg({ notifyOnApproval: !notifyApproval })} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 0',cursor:'pointer'}}>
                  <button className={`toggle-switch ${notifyApproval?'on':''}`}><div className="toggle-thumb" /></button>
                  <div>
                    <div style={{fontWeight:600,color:'var(--text)',fontSize:13}}>Email the resident when their request is approved</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Closed-loop confirmation builds trust. Includes the CMMS work-order number.</div>
                  </div>
                </div>
                <div onClick={() => saveCfg({ notifyOnRejection: !notifyRejection })} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 0',cursor:'pointer'}}>
                  <button className={`toggle-switch ${notifyRejection?'on':''}`}><div className="toggle-thumb" /></button>
                  <div>
                    <div style={{fontWeight:600,color:'var(--text)',fontSize:13}}>Email the resident when their request is rejected</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Optional. Some cities prefer transparency; others avoid this to reduce callbacks.</div>
                  </div>
                </div>
              </>
            )}
          </div>
          {savingCfg && <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>Saving…</div>}
        </div>
      </div>
      )}

      <div className="card" style={{marginTop:20}}>
        <div className="card-header" style={{display:'flex',gap:12,alignItems:'center',justifyContent:'space-between',flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:12,alignItems:'center'}}>
            <div className="card-title">Submissions</div>
            <div style={{display:'flex',gap:6}}>
              {[['pending', 'Pending', counts.pending], ['approved', 'Approved', counts.approved], ['rejected', 'Rejected', counts.rejected], ['all', 'All', subs.length]].map(([k, lbl, n]) => (
                <button key={k} onClick={() => { setFilter(k); setSelectedIds(new Set()); }} style={{padding:'5px 12px',borderRadius:14,fontSize:11,fontWeight:700,border:`1px solid ${filter===k?'var(--accent)':'var(--border-light)'}`,background:filter===k?'var(--accent-dim)':'transparent',color:filter===k?'var(--accent-light)':'var(--text-muted)',cursor:'pointer'}}>{lbl} ({n})</button>
              ))}
            </div>
          </div>
          {selectedIds.size > 0 && (
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:12,color:'var(--text-muted)'}}>{selectedIds.size} selected</span>
              <button onClick={bulkApprove} disabled={bulkApproving} style={{padding:'6px 14px',borderRadius:6,border:'none',background:'var(--success)',color:'#fff',fontWeight:700,fontSize:12,cursor:bulkApproving?'default':'pointer',opacity:bulkApproving?0.6:1}}>{bulkApproving ? 'Approving…' : `✓ Approve ${selectedIds.size} → CMMS`}</button>
              <button onClick={() => setSelectedIds(new Set())} style={{padding:'6px 10px',borderRadius:6,border:'1px solid var(--border-light)',background:'transparent',color:'var(--text-muted)',fontSize:11,cursor:'pointer'}}>Clear</button>
            </div>
          )}
        </div>
        <div className="card-body" style={{padding:0}}>
          {loading ? <div style={{padding:20,color:'var(--text-muted)'}}>Loading…</div> :
            filtered.length === 0 ? <div style={{padding:20,color:'var(--text-muted)',fontSize:13}}>{intakeMode==='direct' ? 'No queued submissions. Switch to "Pending Review" intake mode above to start queueing.' : 'No submissions match this filter yet.'}</div> :
            <div>
              {(() => {
                const pendingVisibleIds = filtered.filter(s => s.review_status === 'pending').map(s => s.id);
                if (pendingVisibleIds.length === 0) return null;
                const allSel = pendingVisibleIds.every(id => selectedIds.has(id));
                const someSel = !allSel && pendingVisibleIds.some(id => selectedIds.has(id));
                return (
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderBottom:'1px solid var(--border)',background:'var(--surface2)'}}>
                    <input type="checkbox" checked={allSel} ref={el => { if (el) el.indeterminate = someSel; }} onChange={() => selectAllVisible(pendingVisibleIds)} style={{width:16,height:16,cursor:'pointer',accentColor:'var(--accent)'}} />
                    <span style={{fontSize:12,color:'var(--text-muted)'}}>Select all {pendingVisibleIds.length} pending</span>
                  </div>
                );
              })()}
              {filtered.map(s => (
                <div key={s.id} style={{borderBottom:'1px solid var(--border)',padding:'14px 16px',background:selectedIds.has(s.id)?'var(--accent-dim)':'transparent'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12}}>
                    {s.review_status === 'pending' && (
                      <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} style={{width:16,height:16,cursor:'pointer',accentColor:'var(--accent)',marginTop:3,flexShrink:0}} />
                    )}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:4,flexWrap:'wrap'}}>
                        <span style={{fontWeight:700,color:'var(--text)',fontSize:13}}>{s.sr_type}</span>
                        <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,fontWeight:700,textTransform:'uppercase',letterSpacing:0.3,background:s.review_status==='pending'?'#F59E0B30':s.review_status==='approved'?'#10B98130':'#EF444430',color:s.review_status==='pending'?'#F59E0B':s.review_status==='approved'?'#10B981':'#EF4444'}}>{s.review_status}</span>
                        <span style={{fontSize:10,color:'var(--accent-light)',fontFamily:'monospace'}}>{s.confirmation_number}</span>
                        {s.cityworks_sr_id ? <span style={{fontSize:10,color:'var(--text-muted)',fontFamily:'monospace'}}>↔ CMMS #{s.cityworks_sr_id}</span> : null}
                        <span style={{fontSize:10,color:'var(--text-muted)'}}>{new Date((s.created_at || '').includes('T') ? s.created_at : (s.created_at || '').replace(' ', 'T') + 'Z').toLocaleString()}</span>
                      </div>
                      <div style={{fontSize:13,color:'var(--text)',marginBottom:4}}>{s.description || <em style={{color:'var(--text-muted)'}}>(no description)</em>}</div>
                      <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:4}}>📍 {s.address || '—'}</div>
                      <div style={{fontSize:12,color:'var(--text-muted)'}}>
                        {(s.caller_name || 'Anonymous')}{s.caller_email ? ` · ${s.caller_email}` : ''}{s.caller_phone ? ` · ${s.caller_phone}` : ''}
                      </div>
                      {s.rejected_reason && <div style={{marginTop:6,padding:'6px 10px',background:'#EF444415',borderRadius:6,fontSize:11,color:'#FCA5A5'}}>Reason: {s.rejected_reason}</div>}
                    </div>
                    {s.review_status === 'pending' && (
                      <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
                        <button onClick={() => approve(s.id)} disabled={actingId===s.id} style={{padding:'6px 12px',borderRadius:6,border:'none',background:'var(--success)',color:'#fff',fontWeight:700,fontSize:11,cursor:'pointer'}}>✓ Approve → CMMS</button>
                        <button onClick={() => { setRejectingId(s.id); setRejectReason(''); }} style={{padding:'6px 12px',borderRadius:6,border:'1px solid var(--danger)',background:'transparent',color:'var(--danger)',fontWeight:700,fontSize:11,cursor:'pointer'}}>✕ Reject</button>
                      </div>
                    )}
                  </div>
                  {rejectingId === s.id && (
                    <div style={{marginTop:10,padding:10,background:'var(--surface3)',borderRadius:6}}>
                      <input className="setting-input" placeholder="Reason (e.g. duplicate, spam, missing info)…" value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{marginBottom:8}} />
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={() => reject(s.id)} disabled={actingId===s.id} style={{padding:'5px 12px',borderRadius:5,border:'none',background:'var(--danger)',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>Confirm Reject</button>
                        <button onClick={() => setRejectingId(null)} style={{padding:'5px 12px',borderRadius:5,border:'1px solid var(--border-light)',background:'transparent',color:'var(--text-muted)',fontSize:11,cursor:'pointer'}}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Team Page ─────────────────────────────────────────────────────────────────
function TeamPage({ user, showToast }) {
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showInvite, setShowInvite] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteName, setInviteName] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('city_admin');
  const [inviting, setInviting] = React.useState(false);
  const [removing, setRemoving] = React.useState(null);

  const cityId = user?.cityId;

  function load() {
    const url = '/api/admin/team' + (user?.role === 'super_admin' && cityId ? '?cityId=' + cityId : '');
    apiFetch(url).then(r => r.json()).then(d => {
      if (d.success) setMembers(d.users || []);
    }).catch(() => showToast('Failed to load team', 'error')).finally(() => setLoading(false));
  }

  React.useEffect(() => { load(); }, []);

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const body = { email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole };
      if (user?.role === 'super_admin' && cityId) body.cityId = cityId;
      const r = await apiFetch('/api/admin/team/invite', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json();
      if (d.success) {
        showToast('Invite sent — they\'ll receive a link to set their password', 'success');
        setShowInvite(false);
        setInviteEmail('');
        setInviteName('');
        setInviteRole('city_admin');
        load();
      } else {
        showToast(d.error || 'Invite failed', 'error');
      }
    } catch { showToast('Invite failed', 'error'); }
    finally { setInviting(false); }
  }

  async function handleRemove(memberId) {
    if (!window.confirm('Remove this team member? They will lose access immediately.')) return;
    setRemoving(memberId);
    try {
      const qs = user?.role === 'super_admin' && cityId ? '?cityId=' + cityId : '';
      const r = await apiFetch('/api/admin/team/' + memberId + qs, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) { showToast('Team member removed', 'success'); load(); }
      else showToast(d.error || 'Remove failed', 'error');
    } catch { showToast('Remove failed', 'error'); }
    finally { setRemoving(null); }
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 className="page-title">Team</h1>
          <div className="page-subtitle">Manage who has admin access to this portal</div>
        </div>
        <button onClick={() => setShowInvite(true)} style={{ background: '#1E3A5F', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          + Invite Team Member
        </button>
      </div>

      {showInvite && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', color: 'var(--text)' }}>Invite a Team Member</h3>
          <form onSubmit={handleInvite}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>Name</label>
                <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Jane Smith" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>Email *</label>
                <input type="email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="jane@city.gov" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted)' }}>Access Level</label>
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }}>
                <option value="city_admin">Full City Admin — all pages and settings</option>
                <option value="review_admin">Review Queue Only — can approve/reject submissions, nothing else</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={inviting} style={{ background: '#1E3A5F', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {inviting ? 'Sending…' : 'Send Invite'}
              </button>
              <button type="button" onClick={() => { setShowInvite(false); setInviteEmail(''); setInviteName(''); }} style={{ background: 'var(--surface2)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 48 }}>Loading…</div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          {members.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>No team members yet</div>
          ) : members.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#1E3A5F22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#1E3A5F', flexShrink: 0 }}>
                {(m.name || m.email).charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{m.name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No name set</span>}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{m.email}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, flexShrink: 0, background: m.role === 'review_admin' ? '#F59E0B22' : '#3B82F622', color: m.role === 'review_admin' ? '#F59E0B' : '#60A5FA', border: `1px solid ${m.role === 'review_admin' ? '#F59E0B44' : '#3B82F644'}` }}>
                {m.role === 'review_admin' ? '📥 Review Only' : 'Full Admin'}
              </span>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>
                Joined {m.created_at ? new Date(m.created_at.includes('T') ? m.created_at : m.created_at + 'Z').toLocaleDateString() : '—'}
              </div>
              {m.id !== user?.userId && (
                <button onClick={() => handleRemove(m.id)} disabled={removing === m.id} style={{ background: 'none', border: '1px solid #EF444466', color: '#EF4444', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                  {removing === m.id ? '…' : 'Remove'}
                </button>
              )}
              {m.id === user?.userId && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: '5px 12px', flexShrink: 0 }}>You</span>
              )}
            </div>
          ))}
        </div>
      )}
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
        Invited users receive an email with a link to set their password. Full Admins can access every page; Review Queue Only users can approve and reject submissions but see nothing else.
      </p>
    </div>
  );
}

// ─── Settings Page ─────────────────────────────────────────────────────────────
function SettingsPage({ showToast }) {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('civicbot_settings');
    // Always use defaults — API load will populate from DB
    return {
      cityName: '', greeting: 'Hi! I can help you submit a service request. What issue would you like to report?',
      primaryColor: '#3B82F6', logoUrl: '',
      cwBaseUrl: '', cwUser: '', cwPassword: '',
      state: '', wkid: 4326, zoneName: '',
      geofenceEnabled: false, geofenceMessage: 'This address appears to be outside city limits. Please verify the address or contact the city directly for assistance.', geofenceCityName: '',
      emailDelivery: false, notificationEmails: '',
      sendSubmitterConfirmation: true, notifyUnansweredQA: false, isAnswersPlan: false,
      adminEmail: '',
      publicContactPhone: '', publicContactWebsite: '',
      submissionNotice: '',
      escalationFormUrl: '', escalationMessage: '',
      geofenceSource: 'census', geofenceBoundaryUrl: '',
    };
  });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [boundaryTest, setBoundaryTest] = useState(null); // null | {status:'testing'|'ok'|'error', msg}

  // Load city config from backend on mount
  useEffect(() => {
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { setSettingsLoading(false); return; }
    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.config) {
          const cfg = data.config;
          const defaults = {
            greeting: 'Hi! I can help you submit a service request. What issue would you like to report?',
            primaryColor: '#3B82F6',
          };
          setSettings(prev => ({
            ...prev,
            cityName: cfg.cityName || prev.cityName,
            greeting: cfg.greeting || defaults.greeting,
            primaryColor: cfg.primaryColor || defaults.primaryColor,
            logoUrl: cfg.logoUrl || '',
            emailDelivery: !!cfg.emailDelivery,
            notificationEmails: cfg.notificationEmails || prev.notificationEmails,
            geofenceEnabled: !!cfg.geofenceEnabled,
            geofenceMessage: cfg.geofenceMessage || prev.geofenceMessage,
            geofenceLat: cfg.geofenceLat || null,
            geofenceLng: cfg.geofenceLng || null,
            geofenceCityName: cfg.geofenceCityName || '',
            state: cfg.state || prev.state,
            wkid: cfg.wkid || prev.wkid,
            zoneName: cfg.zoneName || prev.zoneName,
            cwBaseUrl: cfg.cwBaseUrl || prev.cwBaseUrl || '',
            cwUser: cfg.cwUser || prev.cwUser || '',
            sendSubmitterConfirmation: cfg.send_submitter_confirmation !== undefined ? !!cfg.send_submitter_confirmation : (cfg.sendSubmitterConfirmation !== undefined ? !!cfg.sendSubmitterConfirmation : true),
            notifyUnansweredQA: !!cfg.notifyUnansweredQA,
            isAnswersPlan: !!cfg.isAnswersPlan,
            adminEmail: cfg.adminEmail || '',
            publicContactPhone: cfg.publicContactPhone || '',
            publicContactWebsite: cfg.publicContactWebsite || '',
            submissionNotice: cfg.submissionNotice || '',
            escalationFormUrl: cfg.escalationFormUrl || '',
            escalationMessage: cfg.escalationMessage || '',
            geofenceSource: cfg.geofenceSource || 'census',
            geofenceBoundaryUrl: cfg.geofenceBoundaryUrl || '',
            geofenceLat: cfg.geofenceLat || null,
            geofenceLng: cfg.geofenceLng || null,
            geofenceCityName: cfg.geofenceCityName || prev.geofenceCityName || '',
            geofenceEnabled: cfg.geofenceEnabled !== undefined ? !!cfg.geofenceEnabled : prev.geofenceEnabled,
            geofenceMessage: cfg.geofenceMessage || prev.geofenceMessage,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
  }, []);
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const US_STATES = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ];

  useEffect(() => {
    if (!settings.state) { setZones([]); return; }
    setLoadingZones(true);
    apiFetch(`/api/state-plane-zones?state=${settings.state}`)
      .then(r => r.json())
      .then(data => { if (data.success) setZones(data.zones); })
      .finally(() => setLoadingZones(false));
  }, [settings.state]);

  async function save() {
    // Validate State Plane Zone is set for Cityworks clients
    if (!settings.emailDelivery && (!settings.wkid || settings.wkid === 4326)) {
      showToast('⚠️ State Plane Zone is required for Cityworks clients — please select your zone before saving.', 'error');
      return;
    }
    localStorage.setItem('civicbot_settings', JSON.stringify(settings));
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { showToast('Settings saved locally'); return; }

    try {
      // Load existing config to preserve fields we don't manage on Settings page
      const cfgRes = await apiFetch(`/api/config/${cityId}`).then(r => r.json());
      const existing = cfgRes.success ? cfgRes.config : {};

      const res = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId,
          cityName: settings.cityName || existing.cityName,
          greeting: settings.greeting,
          primaryColor: settings.primaryColor,
          logoUrl: settings.logoUrl,
          state: settings.state,
          wkid: settings.wkid,
          zoneName: settings.zoneName,
          cwBaseUrl: settings.cwBaseUrl,
          cwUser: settings.cwUser,
          cwPassword: settings.cwPassword,
          emailDelivery: settings.emailDelivery,
          notificationEmails: settings.notificationEmails,
          geofenceEnabled: settings.geofenceEnabled,
          geofenceMessage: settings.geofenceMessage,
          geofenceCityName: settings.geofenceCityName || '',
          geofenceLat: settings.geofenceLat || null,
          geofenceLng: settings.geofenceLng || null,
          selectedTemplates: existing.selectedTemplates || [],
          aliases: existing.aliases || {},
          srTypes: existing.srTypes || [],
          sendSubmitterConfirmation: settings.sendSubmitterConfirmation,
          notifyUnansweredQA: settings.notifyUnansweredQA,
          adminEmail: settings.adminEmail,
          submissionNotice: settings.submissionNotice,
          escalationFormUrl: settings.escalationFormUrl || '',
          escalationMessage: settings.escalationMessage || '',
          geofenceSource: settings.geofenceSource || 'census',
          geofenceBoundaryUrl: settings.geofenceBoundaryUrl || '',
          publicContactPhone: settings.publicContactPhone,
          publicContactWebsite: settings.publicContactWebsite,
          templateKeywords: existing.templateKeywords || {},
        }),
      });
      const data = await res.json();
      if (data.success) showToast('Settings saved');
      else showToast(data.error || 'Failed to save settings', 'error');
    } catch (err) {
      showToast('Could not reach server — saved locally only', 'error');
    }
  }

  return (
    <div>
      <div className="page-header"><div className="page-title">Settings</div><div className="page-subtitle">Configure your CivicBot instance</div></div>

      {/* Branding */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Branding</div></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field"><label className="setting-label">City Name</label><input className="setting-input" value={settings.cityName} onChange={e => setSettings({...settings,cityName:e.target.value})} placeholder="City of Springfield" /></div>
            <div className="setting-field"><label className="setting-label">Admin Contact Email</label><input className="setting-input" type="email" value={settings.adminEmail} onChange={e => setSettings({...settings,adminEmail:e.target.value})} placeholder="admin@yourcity.gov" /><span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Used for unanswered Q&A notifications and account communications.</span></div>
            <div className="setting-field"><label className="setting-label">Public Phone Number <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label><input className="setting-input" value={settings.publicContactPhone} onChange={e => setSettings({...settings,publicContactPhone:e.target.value})} placeholder="916-555-1234" /><span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Shown to residents in confirmation and notification emails.</span></div>
            <div className="setting-field"><label className="setting-label">City Website URL <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label><input className="setting-input" value={settings.publicContactWebsite} onChange={e => setSettings({...settings,publicContactWebsite:e.target.value})} placeholder="https://www.yourcity.gov" /><span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Shown to residents in confirmation and notification emails.</span></div>
            <div className="setting-field setting-full">
              <label className="setting-label">City Logo <span style={{color:"var(--text-muted)",fontWeight:400}}>(recommended: 200x200 PNG/SVG, under 2MB)</span></label>
              <div style={{display:"flex",alignItems:"center",gap:16,marginTop:4}}>
                {settings.logoUrl
                  ? <img src={settings.logoUrl} alt="City logo" className="logo-preview" />
                  : <div style={{width:80,height:80,borderRadius:12,background:"var(--surface2)",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>🏙️</div>
                }
                <div style={{flex:1}}>
                  <label className="logo-upload-box" style={{display:"block"}}>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { showToast("Logo must be under 2MB", "error"); return; }
                      const cityId = JSON.parse(localStorage.getItem("civicbot_user")||"{}").cityId;
                      const formData = new FormData();
                      formData.append("logo", file);
                      if (cityId) formData.append("cityId", cityId);
                      try {
                        const token = localStorage.getItem("civicbot_token");
                        const res = await fetch(API_BASE + "/api/upload-logo", { method: "POST", headers: { Authorization: "Bearer " + token }, body: formData });
                        const data = await res.json();
                        if (data.success) {
                          setSettings(s => ({...s, logoUrl: data.logoUrl}));
                          const u = JSON.parse(localStorage.getItem("civicbot_user")||"{}");
                          u.cityLogo = data.logoUrl;
                          localStorage.setItem("civicbot_user", JSON.stringify(u));
                          showToast("Logo uploaded successfully!");
                        } else { showToast(data.error || "Upload failed", "error"); }
                      } catch { showToast("Upload failed", "error"); }
                    }} />
                    <div style={{fontSize:13,color:"var(--accent-light)",fontWeight:600}}>📁 Click to upload logo</div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>JPG, PNG, SVG or WebP - Max 2MB</div>
                  </label>
                  {settings.logoUrl && <button onClick={() => setSettings(s=>({...s,logoUrl:""}))} style={{marginTop:8,padding:"4px 10px",background:"none",border:"1px solid var(--border-light)",borderRadius:6,color:"var(--text-muted)",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove logo</button>}
                </div>
              </div>
            </div>
            <div className="setting-field"><label className="setting-label">Primary Color</label><div style={{display:'flex',gap:8,alignItems:'center'}}><input className="setting-input" value={settings.primaryColor} onChange={e => setSettings({...settings,primaryColor:e.target.value})} style={{flex:1}} /><input type="color" value={settings.primaryColor} onChange={e => setSettings({...settings,primaryColor:e.target.value})} style={{width:36,height:36,borderRadius:8,border:'1px solid var(--border-light)',background:'none',cursor:'pointer',padding:2,flexShrink:0}} title="Pick a color" /></div></div>
            <div className="setting-field setting-full"><label className="setting-label">Welcome Message</label><input className="setting-input" value={settings.greeting} onChange={e => setSettings({...settings,greeting:e.target.value})} /></div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Location & Coordinate System</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Required for accurate map placement in Cityworks</span></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field">
              <label className="setting-label">State</label>
              <select className="setting-input" value={settings.state} onChange={e => setSettings(s => ({...s,state:e.target.value,wkid:4326,zoneName:''}))} style={{cursor:'pointer'}}>
                <option value="">Select state...</option>
                {US_STATES.map(([code,name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
            <div className="setting-field">
              <label className="setting-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{display:'flex',alignItems:'center'}}>State Plane Zone {loadingZones && <span style={{color:'var(--text-muted)',fontSize:10}}> Loading...</span>}<Tooltip text="A State Plane Zone (WKID) tells CivicBot how to convert GPS coordinates into your Cityworks map projection. Without the correct zone, SR locations will appear in the wrong place on the map. Ask your GIS department if unsure — they will know immediately." /></span>
                <a href="https://www.ngs.noaa.gov/SPCS/index.shtml" target="_blank" rel="noreferrer" style={{fontSize:10,color:'var(--accent-light)',fontWeight:400,textDecoration:'none'}}>Help finding your zone</a>
              </label>
              <select className="setting-input" value={settings.wkid} onChange={e => { const zone=zones.find(z=>z.wkid===parseInt(e.target.value)); setSettings(s=>({...s,wkid:parseInt(e.target.value),zoneName:zone?.name||''})); }} disabled={zones.length===0} style={{cursor:zones.length>0?'pointer':'not-allowed'}}>
                <option value={4326}>{zones.length===0?'Select state first...':'Select zone...'}</option>
                {zones.map(z => <option key={z.wkid} value={z.wkid}>{z.name} (WKID: {z.wkid})</option>)}
              </select>
            </div>
            {settings.wkid && settings.wkid !== 4326 ? (
              <div className="setting-field setting-full"><div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#10B981',display:'flex',alignItems:'center',gap:8}}>✓ Using <strong>{settings.zoneName}</strong> — WKID {settings.wkid}</div></div>
            ) : !settings.emailDelivery && (
              <div className="setting-field setting-full"><div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#EF4444',display:'flex',alignItems:'center',gap:8}}>⚠️ <strong>State Plane Zone is required</strong> for Cityworks clients. Without it, map pins will be placed incorrectly. Please select your state and zone above.</div></div>
            )}
            <div className="setting-field setting-full">
              <label className="setting-label">Or enter WKID manually <span style={{color:'var(--text-muted)',fontWeight:400}}>(advanced)</span></label>
              <input className="setting-input" type="number" value={settings.wkid} onChange={e => setSettings({...settings,wkid:parseInt(e.target.value),zoneName:'Custom'})} placeholder="e.g. 102100" />
            </div>
          </div>
        </div>
      </div>

      {/* Geofencing */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title" style={{display:'flex',alignItems:'center'}}>🗺️ City Limits Geofencing<Tooltip text="Geofencing prevents residents from submitting requests for addresses outside your city. It uses U.S. Census Bureau boundary data. You must click Verify City Location after enabling — without that step, geofencing will not activate." /></div><span style={{fontSize:12,color:'var(--text-muted)'}}>Optional — powered by US Census TIGERweb</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info"><div className="toggle-row-label">Restrict submissions to city limits</div><div className="toggle-row-sub">Verify submitted addresses fall within your city boundary. Addresses outside city limits will be rejected with a friendly message.</div></div>
            <button className={`toggle-switch ${settings.geofenceEnabled?'on':''}`} onClick={() => setSettings(s=>({...s,geofenceEnabled:!s.geofenceEnabled}))}><div className="toggle-thumb" /></button>
          </div>
          {/* #0: boundary source — Census (default) or the city's own ArcGIS REST layer */}
          {settings.geofenceEnabled && (
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setSettings(s=>({...s,geofenceSource:'census'}))}
                style={{flex:1,padding:'12px 14px',borderRadius:10,border:`2px solid ${(settings.geofenceSource||'census')==='census'?'var(--accent)':'var(--border-light)'}`,background:(settings.geofenceSource||'census')==='census'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'left'}}>
                <div style={{fontSize:13,fontWeight:600,color:(settings.geofenceSource||'census')==='census'?'var(--accent-light)':'var(--text)'}}>US Census boundary (default)</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>Uses TIGERweb incorporated-place data. Updated annually by the Census Bureau.</div>
              </button>
              <button onClick={() => setSettings(s=>({...s,geofenceSource:'custom'}))}
                style={{flex:1,padding:'12px 14px',borderRadius:10,border:`2px solid ${settings.geofenceSource==='custom'?'var(--accent)':'var(--border-light)'}`,background:settings.geofenceSource==='custom'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'left'}}>
                <div style={{fontSize:13,fontWeight:600,color:settings.geofenceSource==='custom'?'var(--accent-light)':'var(--text)'}}>My city's GIS boundary layer</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>Point CivicBot at your own authoritative ArcGIS REST polygon layer — always current.</div>
              </button>
            </div>
          )}
          {settings.geofenceEnabled && settings.geofenceSource === 'custom' && (
            <div className="setting-field">
              <label className="setting-label">Boundary layer URL (ArcGIS REST)</label>
              <div style={{display:'flex',gap:8}}>
                <input className="setting-input" style={{flex:1}} value={settings.geofenceBoundaryUrl||''} onChange={e => { setSettings({...settings,geofenceBoundaryUrl:e.target.value}); setBoundaryTest(null); }} placeholder="https://services.arcgis.com/…/FeatureServer/0" />
                <button onClick={async () => {
                  setBoundaryTest({status:'testing', msg:'Testing layer…'});
                  try {
                    const r = await apiFetch('/api/admin/test-boundary', { method:'POST', body: JSON.stringify({ url: settings.geofenceBoundaryUrl, lat: settings.geofenceLat, lng: settings.geofenceLng }) });
                    const d = await r.json();
                    if (d.success) {
                      const pt = d.pointTest ? (d.pointTest.inBounds ? ' City center point test: ✅ inside boundary.' : ' ⚠️ City center point tested OUTSIDE this boundary — double-check the layer.') : '';
                      setBoundaryTest({status:'ok', msg:`✓ Layer "${d.layerName || 'boundary'}" verified (polygon).${pt}`});
                    } else setBoundaryTest({status:'error', msg:d.error || 'Test failed'});
                  } catch { setBoundaryTest({status:'error', msg:'Could not reach server'}); }
                }} disabled={!(settings.geofenceBoundaryUrl||'').trim() || boundaryTest?.status==='testing'}
                  style={{padding:'8px 16px',background:'var(--surface2)',border:'1px solid var(--border-light)',borderRadius:8,color:'var(--text-muted)',fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
                  {boundaryTest?.status==='testing' ? 'Testing…' : 'Test Layer'}
                </button>
              </div>
              <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Must be a public ArcGIS REST <strong>polygon feature layer</strong> URL (ends in a layer number, e.g. /FeatureServer/0). Submitted addresses are checked against this boundary instead of Census data.</span>
              {boundaryTest && boundaryTest.status!=='testing' && (
                <div style={{marginTop:8,background:boundaryTest.status==='ok'?'#10B98115':'#EF444415',border:`1px solid ${boundaryTest.status==='ok'?'#10B98130':'#EF444430'}`,borderRadius:8,padding:'8px 12px',fontSize:12,color:boundaryTest.status==='ok'?'#10B981':'#EF4444'}}>{boundaryTest.msg}</div>
              )}
            </div>
          )}
          {settings.geofenceEnabled && (settings.geofenceSource||'census')==='census' && !settings.state && <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B'}}>⚠️ Please select your State above — required for city boundary lookup.</div>}
          {settings.geofenceEnabled && (settings.geofenceSource||'census')==='census' && settings.state && (
            <GeofenceValidator
              cityName={settings.geofenceCityName || ''}
              state={settings.state}
              onChange={val => setSettings(s => ({...s, geofenceCityName: val}))}
              onVerified={({lat, lng, matchedName}) => setSettings(s => ({...s, geofenceLat: lat, geofenceLng: lng, geofenceCityName: matchedName}))}
            />
          )}
          {settings.geofenceEnabled && (settings.geofenceSource||'census')==='census' && settings.state && !settings.geofenceCityName && <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B'}}>⚠️ Enter your Census Place Name above to activate geofencing.</div>}
          {settings.geofenceEnabled && <div className="setting-field"><label className="setting-label">Out-of-bounds message</label><input className="setting-input" value={settings.geofenceMessage} onChange={e => setSettings({...settings,geofenceMessage:e.target.value})} /></div>}
        </div></div>
      </div>

      {/* Email Delivery */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">✉️ Email Delivery</div><span style={{fontSize:12,color:'var(--text-muted)'}}>For organizations without Cityworks or Trimble Unity</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info"><div className="toggle-row-label">Enable email delivery</div><div className="toggle-row-sub">Service requests are packaged and emailed to your team. A CB-YYYY-XXXXX confirmation number is generated for each submission.</div></div>
            {(JSON.parse(localStorage.getItem('civicbot_user')||'{}').role==='super_admin' || !!localStorage.getItem('civicbot_super_token'))
              ? <button className={`toggle-switch ${settings.emailDelivery?'on':''}`} onClick={() => setSettings(s=>({...s,emailDelivery:!s.emailDelivery}))}><div className="toggle-thumb" /></button>
              : <button className={`toggle-switch ${settings.emailDelivery?'on':''}`} disabled title="🔒 Managed by CivicBot — contact us to change your delivery method" style={{opacity:0.5,cursor:'not-allowed'}}><div className="toggle-thumb" /></button>}
          </div>
          {settings.emailDelivery && <>
            <div className="setting-field">
              <label className="setting-label" style={{display:'flex',alignItems:'center'}}>Notification Email Addresses<Tooltip text="All email addresses listed here will receive a formatted notification for every service request submitted. Separate multiple addresses with commas. Consider using a shared inbox (e.g. 311@yourcity.gov) rather than individual staff emails." /></label>
              <input className="setting-input" value={settings.notificationEmails} onChange={e => setSettings({...settings,notificationEmails:e.target.value})} placeholder="dispatcher@city.gov, publicworks@city.gov" />
              <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Separate multiple addresses with commas.</span>
            </div>

            <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>✓ Email delivery active — submissions sent to <strong>{settings.notificationEmails||'your configured addresses'}</strong></div>
          </>}
        </div></div>
      </div>

      {/* Submitter Confirmation Email */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📧 Submitter Confirmation Email</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Send a receipt to residents who provide their email</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info">
              <div className="toggle-row-label">Send confirmation email to submitter</div>
              <div className="toggle-row-sub">When a resident includes their email address, they will automatically receive a confirmation with their request details and confirmation number.</div>
            </div>
            <button className={`toggle-switch ${settings.sendSubmitterConfirmation?'on':''}`} onClick={() => setSettings(s=>({...s,sendSubmitterConfirmation:!s.sendSubmitterConfirmation}))}><div className="toggle-thumb" /></button>
          </div>
          {settings.sendSubmitterConfirmation && (
            <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>
              Residents who provide their email will receive a confirmation after submitting.
            </div>
          )}
        </div></div>
      </div>

      {/* Submission Notice — free-text shown on the confirmation screen before submit */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📌 Submission Notice</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Shown to residents on the confirmation screen, right before they submit</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:10}}>
          <textarea className="setting-input" rows={2} value={settings.submissionNotice||''} onChange={e => setSettings({...settings,submissionNotice:e.target.value})} placeholder="e.g. Service requests can take up to 10 business days to be addressed." style={{resize:'vertical',fontFamily:'inherit'}} />
          <span style={{fontSize:11,color:'var(--text-muted)'}}>Leave blank for no notice. Appears above the “Submit” confirmation in the resident widget.</span>
        </div></div>
      </div>

      {/* CivicBot Answers — Unanswered Q notification (only for Answers cities) */}
      {settings.isAnswersPlan && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><div className="card-title">🤖 CivicBot Answers Notifications</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Get notified when a resident question can't be answered</span></div>
          <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
            <div className="toggle-row">
              <div className="toggle-row-info">
                <div className="toggle-row-label">Notify me when a question can't be answered</div>
                <div className="toggle-row-sub">When CivicBot can't find an answer in your knowledge base, you'll receive an email with the resident's question so you can decide whether to add that information to your website.</div>
              </div>
              <button className={`toggle-switch ${settings.notifyUnansweredQA?'on':''}`} onClick={() => setSettings(s=>({...s,notifyUnansweredQA:!s.notifyUnansweredQA}))}><div className="toggle-thumb" /></button>
            </div>
            {settings.notifyUnansweredQA && (
              <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>
                Unanswered questions will be emailed to your city admin account.
              </div>
            )}
          </div></div>
        </div>
      )}

      {/* CivicBot Answers — escalation to a contact form when a question can't be answered */}
      {settings.isAnswersPlan && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><div className="card-title">🙋 Escalate to Staff</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Where residents go when CivicBot can't answer their question</span></div>
          <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
            <div className="setting-field setting-full">
              <label className="setting-label">Contact form link <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label>
              <input className="setting-input" type="url" value={settings.escalationFormUrl||''} onChange={e => setSettings({...settings,escalationFormUrl:e.target.value})} placeholder="https://yourcity.gov/contact-us" />
            </div>
            <div className="setting-field setting-full">
              <label className="setting-label">Message shown with the link <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label>
              <textarea className="setting-input" rows={3} maxLength={500} value={settings.escalationMessage||''} onChange={e => setSettings({...settings,escalationMessage:e.target.value})} placeholder="If you would like staff to review your question, please complete this form and include the question you were trying to ask." />
            </div>
            <span style={{fontSize:11,color:'var(--text-muted)'}}>When CivicBot can't answer a question, this message and a link to your form appear right below its reply, along with the resident's question so they can copy it into the form. Leave the link blank to turn this off. Must be a full web address starting with https://.</span>
          </div></div>
        </div>
      )}

      {/* Delivery Method Toggle */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Delivery Method</div><span style={{fontSize:12,color:'var(--text-muted)'}}>How service requests are delivered to your team</span></div>
        <div className="card-body">
          {(() => {
            // Delivery method (Email vs Cityworks) is a plan/provisioning decision — only
            // Centricity staff (super_admin, or a super admin impersonating the city) can
            // change it. City admins see it read-only so a stray click can't flip their
            // plan and silently stop routing SRs to Cityworks. Server enforces this too.
            const _u = JSON.parse(localStorage.getItem('civicbot_user') || '{}');
            const canEditDelivery = _u.role === 'super_admin' || !!localStorage.getItem('civicbot_super_token');
            if (!canEditDelivery) {
              return (
                <div style={{padding:'14px 16px',borderRadius:10,border:'1px solid var(--border-light)',background:'var(--surface2)'}}>
                  <div style={{fontSize:20,marginBottom:6}}>{settings.emailDelivery?'📧':'🔗'}</div>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{settings.emailDelivery?'Email Delivery':'Cityworks / Trimble Unity'}</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,lineHeight:1.4}}>🔒 Managed by CivicBot. Contact us to change how your service requests are delivered.</div>
                </div>
              );
            }
            return (
          <div style={{display:'flex',gap:10}}>
            <button
              onClick={() => setSettings(s=>({...s,emailDelivery:true}))}
              style={{flex:1,padding:'14px 16px',borderRadius:10,border:`2px solid ${settings.emailDelivery?'var(--accent)':'var(--border-light)'}`,background:settings.emailDelivery?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',transition:'all 0.15s',textAlign:'left'}}>
              <div style={{fontSize:20,marginBottom:6}}>📧</div>
              <div style={{fontSize:13,fontWeight:600,color:settings.emailDelivery?'var(--accent-light)':'var(--text)'}}>Email Delivery</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>SRs emailed to your team. No Cityworks required.</div>
            </button>
            <button
              onClick={() => setSettings(s=>({...s,emailDelivery:false}))}
              style={{flex:1,padding:'14px 16px',borderRadius:10,border:`2px solid ${!settings.emailDelivery?'var(--accent)':'var(--border-light)'}`,background:!settings.emailDelivery?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',transition:'all 0.15s',textAlign:'left'}}>
              <div style={{fontSize:20,marginBottom:6}}>🔗</div>
              <div style={{fontSize:13,fontWeight:600,color:!settings.emailDelivery?'var(--accent-light)':'var(--text)'}}>Cityworks / Trimble Unity</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>SRs submitted directly into your Cityworks instance.</div>
            </button>
          </div>
            );
          })()}
        </div>
      </div>

      {/* Trimble Unity - only show when Cityworks selected */}
      {!settings.emailDelivery && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><div className="card-title">Trimble Unity / Cityworks Connection</div></div>
          <div className="card-body">
            <div className="settings-grid">
              <div className="setting-field setting-full"><label className="setting-label" style={{display:'flex',alignItems:'center'}}>Cityworks Base URL<Tooltip text="Enter the root URL of your Cityworks or Trimble Unity instance. Example: https://partners.maintain.unity.trimble.com/YOUR_ORG — no trailing slash. For on-premise Cityworks, this is typically your internal server URL." /></label><input className="setting-input" value={settings.cwBaseUrl} onChange={e => setSettings({...settings,cwBaseUrl:e.target.value})} placeholder="https://partners.maintain.unity.trimble.com/YOUR_ORG" /></div>
              <div className="setting-field"><label className="setting-label">API Username</label><input className="setting-input" value={settings.cwUser} onChange={e => setSettings({...settings,cwUser:e.target.value})} placeholder="APIUser" /></div>
              <div className="setting-field"><label className="setting-label">API Password</label><input className="setting-input" type="password" value={settings.cwPassword||''} onChange={e => setSettings({...settings,cwPassword:e.target.value})} placeholder="••••••••••" /></div>
            </div>
            <CWVerifyButton cwBaseUrl={settings.cwBaseUrl} cwUser={settings.cwUser} cwPassword={settings.cwPassword} cityId={JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId} />
            <div style={{marginTop:16,background:'#3B82F615',border:'1px solid #3B82F630',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-dim)'}}>
              💡 After saving your Cityworks credentials, go to <strong>SR Templates</strong> to load and configure your SR types.
            </div>
          </div>
        </div>
      )}

      <button className="save-btn" style={{padding:'11px 24px'}} onClick={save}>Save Settings</button>
    </div>
  );
}


// CW Verify Button Component
// When `cityId` is provided, the server falls back to the city's STORED (encrypted)
// creds for any field left blank — so a super admin can test a city's connection
// without impersonating, and the Settings test works even though the password field
// always loads blank for security.
function CWVerifyButton({ cwBaseUrl, cwUser, cwPassword, cityId }) {
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');

  async function verify() {
    // Without a cityId we have no stored creds to fall back on — require all three.
    if (!cityId && (!cwBaseUrl || !cwUser || !cwPassword)) {
      setStatus('error'); setMessage('Please fill in Base URL, username, and password first'); return;
    }
    setStatus('checking'); setMessage('');
    try {
      const res = await apiFetch('/api/verify-cw', {
        method: 'POST',
        body: JSON.stringify({ cwBaseUrl, cwUser, cwPassword, ...(cityId ? { cityId } : {}) }),
      });
      const data = await res.json();
      if (data.success) { setStatus('success'); setMessage(data.message || 'Connection successful!'); }
      else { setStatus('error'); setMessage(data.error || 'Connection failed'); }
    } catch { setStatus('error'); setMessage('Could not reach server'); }
  }

  const colors = { success: { bg:'#10B98115', border:'#10B98130', text:'#10B981' }, error: { bg:'#EF444415', border:'#EF444430', text:'#EF4444' } };
  const c = colors[status] || {};

  return (
    <div style={{marginTop:12, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
      <button onClick={verify} disabled={status==='checking'} style={{padding:'8px 16px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:8, color:'var(--text-muted)', fontSize:12, fontFamily:'inherit', cursor:'pointer', fontWeight:600, transition:'all 0.15s', opacity:status==='checking'?0.6:1}}>
        {status==='checking' ? 'Testing connection...' : 'Test Connection'}
      </button>
      {status && status !== 'checking' && (
        <div style={{background:c.bg, border:'1px solid '+c.border, borderRadius:8, padding:'7px 14px', fontSize:12, color:c.text, fontWeight:500}}>
          {message}
        </div>
      )}
    </div>
  );
}

// ─── Cities Page (Super Admin) ────────────────────────────────────────────────
function CitiesPage({ user, showToast, setImpersonating }) {
  const [apiCost, setApiCost] = useState(null);
  React.useEffect(() => {
    apiFetch('/api/admin/api-cost-portfolio')
      .then(r => r.json())
      .then(data => { if (data.success) setApiCost(data); })
      .catch(() => {});
  }, []);

  const [deleteCity, setDeleteCity] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [resetPwdCity, setResetPwdCity] = useState(null);
  const [resetPwdVal, setResetPwdVal] = useState('');
  const [resetPwdConfirm, setResetPwdConfirm] = useState('');
  const [resetPwdMsg, setResetPwdMsg] = useState('');
  const [resetPwdSaving, setResetPwdSaving] = useState(false);

  async function handleResetPwd(e) {
    e.preventDefault();
    setResetPwdMsg('');
    if (resetPwdVal !== resetPwdConfirm) { setResetPwdMsg('Passwords do not match'); return; }
    if (resetPwdVal.length < 8) { setResetPwdMsg('Password must be at least 8 characters'); return; }
    setResetPwdSaving(true);
    try {
      const r = await apiFetch(`/api/admin/users/${resetPwdCity.id}/reset-password`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ newPassword: resetPwdVal }) });
      const data = await r.json();
      if (data.success) { setResetPwdMsg('✅ Password reset successfully'); setTimeout(() => { setResetPwdCity(null); setResetPwdVal(''); setResetPwdConfirm(''); setResetPwdMsg(''); }, 1500); }
      else setResetPwdMsg(data.error || 'Failed');
    } catch { setResetPwdMsg('Network error'); }
    setResetPwdSaving(false);
  }

  async function handleDeleteCity() {
    if (deleteConfirmText !== deleteCity.name) return;
    setDeleting(true);
    try {
      const res = await apiFetch('/api/admin/cities/' + deleteCity.id, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        showToast('City permanently deleted');
        setDeleteCity(null);
        setDeleteConfirmText('');
        loadCities();
      } else {
        showToast(data.error || 'Delete failed', 'error');
      }
    } catch { showToast('Delete failed', 'error'); }
    setDeleting(false);
  }
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCity, setEditCity] = useState(null);
  const [viewCity, setViewCity] = useState(null);

  const [activateCity, setActivateCity] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function sortIndicator(field) {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  function getExpiry(city) {
    if (city.manually_activated === 1 && city.paid_through) return new Date(city.paid_through);
    if (city.paid_through) return new Date(city.paid_through);
    if (city.trial_ends_at) return new Date(city.trial_ends_at);
    return null;
  }


  function loadCities() {
    setLoading(true);
    apiFetch('/api/admin/cities')
      .then(r => r.json())
      .then(data => { if (data.success) setCities(data.cities); })
      .catch(() => showToast('Failed to load cities', 'error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadCities(); }, []);

  const filtered = cities.filter(c =>
    !search ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.admin_email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.state || '').toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    let av, bv;
    if (sortField === 'name') { av = a.name || ''; bv = b.name || ''; }
    else if (sortField === 'status') { av = a.billing_status || ''; bv = b.billing_status || ''; }
    else if (sortField === 'plan') { av = a.plan_name || ''; bv = b.plan_name || ''; }
    else if (sortField === 'submissions') { av = a.submission_count || 0; bv = b.submission_count || 0; }
    else if (sortField === 'expiry') {
      av = a.manually_activated ? (a.paid_through || '') : (a.trial_ends_at || '');
      bv = b.manually_activated ? (b.paid_through || '') : (b.trial_ends_at || '');
    }
    else if (sortField === 'created_at') { av = a.created_at || ''; bv = b.created_at || ''; }
    else if (sortField === 'referral') { av = a.referral_source || ''; bv = b.referral_source || ''; }
    else if (sortField === 'salesrep') { av = a.assigned_sales_rep || ''; bv = b.assigned_sales_rep || ''; }
    else { av = ''; bv = ''; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function statusBadge(city) {
    const s = city.billing_status || 'no_trial';
    if (city.manually_activated === 1) {
      const expired = city.paid_through && new Date(city.paid_through) < new Date();
      return <span className={`status-badge ${expired ? 'past_due' : 'active'}`}>{expired ? '⚠️ Manual Expired' : '✅ Manual'}</span>;
    }
    const labels = { trialing: '🎉 Trial', active: '✅ Active', past_due: '⚠️ Past Due', paused: '⏸ Paused', no_trial: '— No Trial' };
    return <span className={`status-badge ${s}`}>{labels[s] || s}</span>;
  }

  function trialDays(city) {
    if (!city.trial_ends_at) return '—';
    const days = Math.max(0, Math.ceil((new Date(city.trial_ends_at) - new Date()) / 86400000));
    return days > 0 ? `${days}d left` : 'Expired';
  }

  async function openStripePortal(city) {
    if (!city.stripe_customer_id) { showToast('No billing account for this city', 'error'); return; }
    try {
      const res = await apiFetch('/api/billing/portal', { method: 'POST', body: JSON.stringify({ cityId: city.id, returnUrl: window.location.href }) });
      const data = await res.json();
      if (data.success) window.open(data.url, '_blank');
      else showToast('Could not open billing portal', 'error');
    } catch { showToast('Could not open billing portal', 'error'); }
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading cities...</div>;

  async function handleImpersonate(city) {
    if (!window.confirm('Enter admin panel as ' + city.name + '? Your session will be saved and restored on exit.')) return;
    try {
      const res = await apiFetch('/api/admin/impersonate/' + city.id, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      // Save super admin session, swap in city token
      localStorage.setItem('civicbot_super_token', localStorage.getItem('civicbot_token'));
      localStorage.setItem('civicbot_super_user', localStorage.getItem('civicbot_user'));
      localStorage.setItem('civicbot_token', data.token);
      localStorage.setItem('civicbot_impersonating', city.name);
      localStorage.setItem('civicbot_user', JSON.stringify({ cityId: city.id, email: data.email, role: 'city_admin', cityName: city.name, isAnswersPlan: city.is_answers_plan === 1 }));
      setImpersonating(city.name);
      window.location.reload();
    } catch (err) {
      showToast('Impersonation failed: ' + err.message);
    }
  }

  function exportCitiesCSV() {
    const headers = ['City','Slug','State','Plan','Billing Status','Trial Ends','Submissions','Created'];
    const rows = cities.map(c => [
      c.name || '', c.slug || '', c.state || '',
      c.plan_name || '', c.billing_status || '',
      c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString() : '',
      c.submission_count || 0,
      c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g,'""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'civicbot-cities.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const totalCities = cities.length;
  const activeCities = cities.filter(c => c.billing_status === 'trialing' || c.billing_status === 'active').length;
  const totalSubs = cities.reduce((a, c) => a + (c.submission_count || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="page-title">All Cities</div>
            <div className="page-subtitle">Manage all CivicBot customer accounts</div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary" onClick={exportCitiesCSV}>📥 Export CSV</button>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ Create City</button>
          </div>
        </div>
      </div>

      {/* AI Cost Widget — super-admin portfolio view, not visible to city admins */}
      {apiCost && (
        <div style={{background:'linear-gradient(135deg,#0F1A2A 0%,#1A2235 100%)',border:'1px solid #3B82F640',borderRadius:12,padding:'18px 22px',marginBottom:20,display:'flex',alignItems:'center',gap:24,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{fontSize:34}}>🧮</div>
            <div>
              <div style={{fontSize:30,fontWeight:800,color:'#60A5FA',lineHeight:1}}>${apiCost.thisMonth.costUsd.toFixed(2)}</div>
              <div style={{fontSize:11,color:'#3B82F6',textTransform:'uppercase',letterSpacing:0.5,fontWeight:600,marginTop:2}}>AI Cost This Month — All Cities</div>
              <div style={{fontSize:11,color:'#9CA3AF',marginTop:2}}>{apiCost.thisMonth.calls.toLocaleString()} model calls · {Math.round((apiCost.thisMonth.inputTokens + apiCost.thisMonth.outputTokens)/1000).toLocaleString()}K tokens</div>
            </div>
          </div>
          {apiCost.byEndpoint && apiCost.byEndpoint.length > 0 && (
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:10,fontWeight:700,color:'#3B82F6',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>By Endpoint</div>
              {apiCost.byEndpoint.slice(0,4).map((r, i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#CBD5E1',padding:'2px 0'}}>
                  <span>{r.endpoint} <span style={{color:'#64748B'}}>· {r.calls}</span></span>
                  <span style={{fontWeight:700,color:'#60A5FA'}}>${r.costUsd.toFixed(3)}</span>
                </div>
              ))}
            </div>
          )}
          {apiCost.byCity && apiCost.byCity.length > 0 && (
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:10,fontWeight:700,color:'#3B82F6',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>Top Cities</div>
              {apiCost.byCity.slice(0,4).map((r, i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#CBD5E1',padding:'2px 0'}}>
                  <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:160}}>{r.cityName} <span style={{color:'#64748B'}}>· {r.calls}</span></span>
                  <span style={{fontWeight:700,color:'#60A5FA'}}>${r.costUsd.toFixed(3)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="stats-row">
        <div className="stat-card"><div className="stat-label">Total Cities</div><div className="stat-value">{totalCities}</div><div className="stat-sub">All accounts</div></div>
        <div className="stat-card"><div className="stat-label">Active / Trial</div><div className="stat-value" style={{color:'var(--success)'}}>{activeCities}</div><div className="stat-sub">Live customers</div></div>
        <div className="stat-card"><div className="stat-label">Total Submissions</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{totalSubs}</div><div className="stat-sub">All time, all cities</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Customer Cities</div>
          <input className="search-input" placeholder="Search by name, email, or state..." value={search} onChange={e => setSearch(e.target.value)} style={{width:280}} />
        </div>
        <div style={{overflowX:'auto'}}>
          {filtered.length === 0 ? (
            <div className="empty">No cities yet. Click "Create City" to onboard your first customer.</div>
          ) : (
            <table className="cities-table">
              <thead>
                <tr>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('name')}>City{sortIndicator('name')}</th>
                  <th>Admin Email</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('plan')}>Plan{sortIndicator('plan')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('expiry')}>Expiry{sortIndicator('expiry')}</th>
                  <th>Billing</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('submissions')}>Submissions{sortIndicator('submissions')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('referral')}>Referral{sortIndicator('referral')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('salesrep')}>Sales Rep{sortIndicator('salesrep')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(city => (
                  <tr key={city.id}>
                    <td>
                      <div style={{fontWeight:600}}>{city.name}</div>
                      <div style={{fontSize:11, color:'var(--text-muted)'}}>{city.state || '—'} · {city.email_delivery ? '📧 Email' : '🔗 Cityworks'}</div>
                    </td>
                    <td style={{fontSize:12, color:'var(--text-dim)'}}>{city.admin_email || '—'}</td>
                    <td style={{fontSize:12}}>{city.plan_name ? city.plan_name.replace('CivicBot ', '') : <span style={{color:'var(--text-muted)'}}>No plan</span>}</td>
                    <td>{statusBadge(city)}</td>
                    <td style={{fontSize:12}}>
                      {(() => {
                        const exp = getExpiry(city);
                        if (!exp) return <span style={{color:'var(--text-muted)'}}>—</span>;
                        const expired = exp < new Date();
                        const days = Math.ceil((exp - new Date()) / 86400000);
                        return <div>
                          <div style={{color: expired ? 'var(--danger)' : days <= 30 ? '#F59E0B' : 'var(--success)', fontWeight:600}}>{expired ? '⚠️ Expired' : days + 'd left'}</div>
                          <div style={{color:'var(--text-muted)',fontSize:11}}>{exp.toLocaleDateString()}</div>
                        </div>;
                      })()}
                    </td>
                    <td style={{fontSize:12}}>
                      {city.manually_activated === 1 ? (
                        <div>
                          <div style={{fontWeight:600, color:'var(--text)'}}>{city.billing_period === 'monthly' ? '📅 Monthly' : '📆 Annual'}</div>
                          <div style={{color: city.paid_through && new Date(city.paid_through) < new Date() ? 'var(--danger)' : 'var(--text-muted)'}}>
                            {city.paid_through ? 'Until ' + new Date(city.paid_through).toLocaleDateString() : '—'}
                          </div>
                        </div>
                      ) : city.stripe_subscription_id ? (
                        <div>
                          <div style={{fontWeight:600, color:'var(--text)'}}>💳 Stripe</div>
                          <div style={{color:'var(--text-muted)'}}>{city.billing_period === 'monthly' ? 'Monthly' : 'Annual'}</div>
                        </div>
                      ) : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                    <td>
                      <div style={{fontSize:13, fontWeight:600}}>{city.submission_count || 0}</div>
                      <div style={{fontSize:11, color:'var(--text-muted)'}}>all time</div>
                    </td>
                    <td style={{fontSize:12}}>
                      {city.referral_source ? (
                        <div>
                          <div style={{color:'var(--text)'}}>{city.referral_source}</div>
                          {city.referral_detail && <div style={{color:'var(--text-muted)',fontSize:11}}>{city.referral_detail}</div>}
                        </div>
                      ) : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                    <td style={{fontSize:12, color:'var(--text)'}}>{city.assigned_sales_rep ? city.assigned_sales_rep : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td>
                      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                        <button className="action-btn" onClick={() => setViewCity(city)}>📋 Submissions</button>
                        <button className="action-btn" onClick={() => setEditCity(city)}>✏️ Edit</button>
                        <button className="action-btn" onClick={() => handleImpersonate(city)} style={{background:'var(--accent)',color:'#fff',border:'none'}}>👁 Login As</button>
                        <button className="action-btn" onClick={() => { setResetPwdCity(city); setResetPwdVal(''); setResetPwdConfirm(''); setResetPwdMsg(''); }}>🔑 Reset Pwd</button>
                        {city.stripe_customer_id && <button className="action-btn" onClick={() => openStripePortal(city)}>💳 Billing</button>}
                        <button className="action-btn" onClick={() => setActivateCity(city)}>{city.manually_activated ? '🔴 Deactivate' : '✅ Activate'}</button>
                        <button className="action-btn" onClick={() => setDeleteCity(city)} style={{background:'var(--danger)',color:'#fff',border:'none'}}>🗑 Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateCityModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadCities(); showToast('City created and welcome email sent!'); }}
          showToast={showToast}
        />
      )}

      {editCity && (
        <EditCityModal
          city={editCity}
          onClose={() => setEditCity(null)}
          onSaved={() => { setEditCity(null); loadCities(); showToast('City updated successfully'); }}
          showToast={showToast}
        />
      )}

      {viewCity && (
        <CitySubmissionsModal
          city={viewCity}
          onClose={() => setViewCity(null)}
        />
      )}
      {activateCity && (
        <ManualActivationModal
          city={activateCity}
          onClose={() => setActivateCity(null)}
          onSaved={() => { setActivateCity(null); loadCities(); showToast(activateCity.manually_activated ? 'City deactivated' : 'City manually activated!'); }}
          showToast={showToast}
        />
      )}

      {deleteCity && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteCity(null)}>
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-header">
              <div className="modal-title" style={{color:'var(--danger)'}}>🗑 Delete City — Permanent Action</div>
              <button className="modal-close" onClick={() => { setDeleteCity(null); setDeleteConfirmText(''); }}>✕</button>
            </div>
            <div style={{padding:'24px 28px'}}>
              <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#FCA5A5'}}>
                ⚠️ This will permanently delete <strong>{deleteCity.name}</strong> and all associated submissions, settings, and user accounts. This cannot be undone.
              </div>
              <div style={{marginBottom:8,fontSize:13,color:'var(--text-muted)'}}>Type <strong style={{color:'var(--text)'}}>{deleteCity.name}</strong> to confirm:</div>
              <input
                className="setting-input"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={deleteCity.name}
                autoFocus
                style={{width:'100%',marginBottom:20}}
              />
              <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={() => { setDeleteCity(null); setDeleteConfirmText(''); }}>Cancel</button>
                <button
                  onClick={handleDeleteCity}
                  disabled={deleteConfirmText !== deleteCity.name || deleting}
                  style={{padding:'8px 20px',background: deleteConfirmText === deleteCity.name ? 'var(--danger)' : 'var(--surface2)',color: deleteConfirmText === deleteCity.name ? '#fff' : 'var(--text-muted)',border:'none',borderRadius:6,fontWeight:600,fontSize:13,cursor: deleteConfirmText === deleteCity.name ? 'pointer' : 'not-allowed',transition:'all 0.15s'}}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetPwdCity && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setResetPwdCity(null)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <div className="modal-title">Reset Password — {resetPwdCity.name}</div>
              <button className="modal-close" onClick={() => setResetPwdCity(null)}>✕</button>
            </div>
            <form onSubmit={handleResetPwd} style={{padding:'20px 24px'}}>
              <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16}}>Set a new password for the admin account of <strong>{resetPwdCity.name}</strong>. The city admin will be signed out of any active sessions on next refresh.</p>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-input" value={resetPwdVal} onChange={e => setResetPwdVal(e.target.value)} required minLength={8} placeholder="Min. 8 characters" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input type="password" className="form-input" value={resetPwdConfirm} onChange={e => setResetPwdConfirm(e.target.value)} required />
              </div>
              {resetPwdMsg && <div style={{padding:'8px 12px',borderRadius:6,fontSize:13,background:resetPwdMsg.startsWith('✅')?'#10B98120':'#EF444420',color:resetPwdMsg.startsWith('✅')?'#10B981':'#EF4444',border:`1px solid ${resetPwdMsg.startsWith('✅')?'#10B981':'#EF4444'}`}} style={{marginBottom:12}}>{resetPwdMsg}</div>}
              <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-secondary" onClick={() => setResetPwdCity(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={resetPwdSaving}>{resetPwdSaving ? 'Saving…' : 'Reset Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create City Modal ────────────────────────────────────────────────────────
function ManualActivationModal({ city, onClose, onSaved, showToast }) {
  const isActive = city.manually_activated === 1;
  const oneYearFromNow = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    planName: city.plan_name || 'CivicBot Professional',
    isCwPlan: city.is_cw_plan === 1,
    isAnswersPlan: city.is_answers_plan === 1,
    billingPeriod: city.billing_period || 'annual',
    paidThrough: city.paid_through ? city.paid_through.slice(0, 10) : oneYearFromNow,
    subscriptionAmount: city.subscription_amount ? String(city.subscription_amount) : '',
    notes: city.manual_activation_notes || '',
  });
  const [saving, setSaving] = useState(false);

  async function handleActivate() {
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/activate-city', { method: 'POST', body: JSON.stringify({ cityId: city.id, ...form }) });
      const data = await res.json();
      if (data.success) onSaved();
      else showToast(data.error || 'Failed to activate', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  async function handleDeactivate() {
    if (!window.confirm('Deactivate ' + city.name + '? This will block new submissions.')) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/deactivate-city', { method: 'POST', body: JSON.stringify({ cityId: city.id, notes: form.notes }) });
      const data = await res.json();
      if (data.success) onSaved();
      else showToast(data.error || 'Failed to deactivate', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isActive ? '🔴 Deactivate City' : '✅ Manual Activation'} — {city.name}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {isActive && (
            <div style={{background:'var(--success)15',border:'1px solid var(--success)30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--success)',marginBottom:16}}>
              ✅ Currently manually activated — paid through {city.paid_through ? new Date(city.paid_through).toLocaleDateString() : 'no expiry set'}
            </div>
          )}
          <div className="setting-field">
            <label className="setting-label">Plan</label>
            <select className="setting-input" value={form.planName} onChange={e => setForm({...form, planName: e.target.value})} style={{cursor:'pointer'}}>
              <option value="CivicBot Starter">Starter</option>
              <option value="CivicBot Growth">Growth</option>
              <option value="CivicBot Professional">Professional</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">Plan Type</label>
            <select className="setting-input" value={form.isCwPlan ? 'cw' : 'email'} onChange={e => setForm({...form, isCwPlan: e.target.value === 'cw'})} style={{cursor:'pointer'}}>
              <option value="email">Email Delivery</option>
              <option value="cw">Cityworks / Trimble Unity</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">Add-ons</label>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
              <input type="checkbox" checked={form.isAnswersPlan} onChange={e => setForm({...form, isAnswersPlan: e.target.checked})} />
              Include CivicBot Answers
            </label>
          </div>
          <div className="setting-field">
            <label className="setting-label">Billing Period</label>
            <select className="setting-input" value={form.billingPeriod} onChange={e => {
              const period = e.target.value;
              const days = period === 'monthly' ? 30 : 365;
              const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
              setForm({...form, billingPeriod: period, paidThrough: expiry});
            }} style={{cursor:'pointer'}}>
              <option value="annual">Annual (+365 days)</option>
              <option value="monthly">Monthly (+30 days)</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">License Expiration Date</label>
            <input className="setting-input" type="date" value={form.paidThrough} onChange={e => setForm({...form, paidThrough: e.target.value})} />
            <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4,display:'block'}}>Auto-calculated from billing period. You can override manually if needed.</span>
          </div>
          <div className="setting-field">
            <label className="setting-label">Subscription Amount ($)</label>
            <input className="setting-input" type="number" min="0" value={form.subscriptionAmount} onChange={e => setForm({...form, subscriptionAmount: e.target.value})} placeholder="e.g. 2500" />
            <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4,display:'block'}}>Total billed per {form.billingPeriod === 'monthly' ? 'month' : 'year'} — used for ARR tracking in the sales portal.</span>
          </div>
          <div className="setting-field">
            <label className="setting-label">Notes / Memo</label>
            <input className="setting-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="e.g. Check #1234 received 4/28/2026" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {isActive && <button className="btn" style={{background:'var(--danger)',color:'white'}} onClick={handleDeactivate} disabled={saving}>{saving ? 'Deactivating...' : '🔴 Deactivate'}</button>}
          <button className="btn btn-primary" onClick={handleActivate} disabled={saving}>{saving ? 'Saving...' : '✅ Activate'}</button>
        </div>
      </div>
    </div>
  );
}

function CreateCityModal({ onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ cityName: '', state: '', email: '', isCityworks: true, notificationEmails: '', referralSource: '', referralDetail: '' });
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (!form.cityName || !form.email) { showToast('City name and email are required', 'error'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/create-city', { method: 'POST', body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) onCreated();
      else showToast(data.error || 'Failed to create city', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Create New City</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="setting-field">
            <label className="setting-label">City Name *</label>
            <input className="setting-input" value={form.cityName} onChange={e => setForm({...form, cityName: e.target.value})} placeholder="City of Springfield" autoFocus />
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">State</label>
              <input className="setting-input" value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="UT" maxLength={2} />
            </div>
            <div className="setting-field">
              <label className="setting-label">Type</label>
              <select className="setting-input" value={form.isCityworks ? 'cw' : 'email'} onChange={e => setForm({...form, isCityworks: e.target.value === 'cw'})} style={{cursor:'pointer'}}>
                <option value="cw">Cityworks / Trimble Unity</option>
                <option value="email">Email Delivery</option>
              </select>
            </div>
          </div>
          <div className="setting-field">
            <label className="setting-label">Admin Email * <span style={{color:'var(--text-muted)', fontWeight:400}}>(welcome email sent here)</span></label>
            <input className="setting-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="admin@cityname.gov" />
          </div>
          {!form.isCityworks && (
            <div className="setting-field">
              <label className="setting-label">Notification Emails</label>
              <input className="setting-input" value={form.notificationEmails} onChange={e => setForm({...form, notificationEmails: e.target.value})} placeholder="dispatch@city.gov, pw@city.gov" />
              <span style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>SR emails will be sent here. Separate multiple with commas.</span>
            </div>
          )}
          <div className="setting-field">
            <label className="setting-label">How did they hear about CivicBot? <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label>
            <select className="setting-input" value={form.referralSource} onChange={e => { setForm({...form, referralSource: e.target.value, referralDetail: ''}); }} style={{cursor:'pointer'}}>
              <option value="">Select an option...</option>
              <option value="Web Search">Web Search</option>
              <option value="AI Tool">AI Tool (ChatGPT, Claude, etc.)</option>
              <option value="Cityworks / Trimble">Cityworks / Trimble</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Conference / Trade Show">Conference / Trade Show</option>
              <option value="Existing Customer Referral">Existing Customer Referral</option>
              <option value="Centricity Sales Rep">Centricity Sales Rep</option>
              <option value="Other Referral">Other Referral</option>
              <option value="Cold Outreach">Cold Outreach</option>
              <option value="Other">Other</option>
            </select>
            {(form.referralSource === 'Centricity Sales Rep' || form.referralSource === 'Other Referral') && (
              <input className="setting-input" style={{marginTop:8}} value={form.referralDetail} onChange={e => setForm({...form, referralDetail: e.target.value})} placeholder={form.referralSource === 'Centricity Sales Rep' ? 'Which rep?' : 'Referred by (name or organization)'} />
            )}
          </div>
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)', lineHeight:1.6}}>
            📧 A welcome email with a "Set Your Password" link will be sent to the admin email. The link expires in 48 hours.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create City & Send Welcome Email'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit City Modal ──────────────────────────────────────────────────────────
function EditCityModal({ city, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    cityId: city.id,
    cityName: city.name || '',
    state: city.state || '',
    emailDelivery: city.email_delivery === 1,
    notificationEmails: city.notification_emails || '',
    geofenceEnabled: city.geofence_enabled === 1,
    geofenceMessage: city.geofence_message || '',
    srTypes: JSON.parse(city.sr_types || '[]'),
    planLimit: city.plan_limit || 500,
    auditEmail: city.audit_email || '',
    assignedSalesRep: city.assigned_sales_rep || '',
    noCommission: city.no_commission === 1,
    isAnswersPlan: city.is_answers_plan === 1,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      // Update city config
      const res = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId: form.cityId,
          cityName: form.cityName,
          state: form.state,
          emailDelivery: form.emailDelivery,
          notificationEmails: form.notificationEmails,
          geofenceEnabled: form.geofenceEnabled,
          geofenceMessage: form.geofenceMessage,
          srTypes: form.srTypes,
          auditEmail: form.auditEmail,
          assignedSalesRep: form.assignedSalesRep,
          noCommission: form.noCommission,
          // preserve existing values
          greeting: city.greeting,
          primaryColor: city.primary_color,
          wkid: city.wkid,
          selectedTemplates: JSON.parse(city.selected_templates || '[]'),
          aliases: JSON.parse(city.aliases || '{}'),
        }),
      });
      const data = await res.json();

      // Update plan limit separately if changed
      if (form.planLimit !== (city.plan_limit || 500)) {
        await apiFetch('/api/admin/set-plan-limit', {
          method: 'POST',
          body: JSON.stringify({ cityId: form.cityId, planLimit: parseInt(form.planLimit) }),
        });
      }

      // Update Answers plan flag if changed
      if (form.isAnswersPlan !== (city.is_answers_plan === 1)) {
        await apiFetch('/api/admin/set-answers-plan', {
          method: 'POST',
          body: JSON.stringify({ cityId: form.cityId, isAnswersPlan: form.isAnswersPlan }),
        });
      }

      if (data.success) onSaved();
      else showToast(data.error || 'Failed to save', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit — {city.name}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">City Name</label>
              <input className="setting-input" value={form.cityName} onChange={e => setForm({...form, cityName: e.target.value})} />
            </div>
            <div className="setting-field">
              <label className="setting-label">State</label>
              <input className="setting-input" value={form.state} onChange={e => setForm({...form, state: e.target.value})} maxLength={2} />
            </div>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">Monthly Submission Limit</label>
              <select className="setting-input" value={form.planLimit} onChange={e => setForm({...form, planLimit: parseInt(e.target.value)})} style={{cursor:'pointer'}}>
                <option value={100}>100 — Starter</option>
                <option value={250}>250 — Growth</option>
                <option value={500}>500 — Professional</option>
                <option value={9999}>Unlimited — Enterprise</option>
              </select>
            </div>
            <div className="setting-field">
              <label className="setting-label">Assigned Sales Rep</label></div><input className="setting-input" value={form.assignedSalesRep} onChange={e => setForm({...form, assignedSalesRep: e.target.value})} placeholder="e.g. John Smith" />
            <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px', marginTop:8}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <div>
                  <div style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>No Commission</div>
                  <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Flag this customer as ineligible for sales rep commission (e.g. self-serve, inbound, no rep responsible)</div>
                </div>
                <button className={`toggle-switch ${form.noCommission?'on':''}`} onClick={() => setForm(f=>({...f, noCommission:!f.noCommission}))}><div className="toggle-thumb" /></button>
              </div>
            </div>
            <div className="setting-field"><label className="setting-label">Audit Email</label>
              <input className="setting-input" value={form.auditEmail} onChange={e => setForm({...form, auditEmail: e.target.value})} placeholder="Same as admin email" />
              <span style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>Daily audit emails go here. Leave blank to use admin email.</span>
            </div>
          </div>

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>Email Delivery</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>SRs emailed to staff instead of Cityworks</div>
              </div>
              <button className={`toggle-switch ${form.emailDelivery?'on':''}`} onClick={() => setForm(f=>({...f, emailDelivery:!f.emailDelivery}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          {form.emailDelivery && (
            <div className="setting-field">
              <label className="setting-label">Notification Emails</label>
              <input className="setting-input" value={form.notificationEmails} onChange={e => setForm({...form, notificationEmails: e.target.value})} placeholder="dispatch@city.gov, pw@city.gov" />
            </div>
          )}

          {form.emailDelivery && (
            <div className="setting-field">
              <label className="setting-label">SR Types <span style={{color:'var(--text-muted)', fontWeight:400}}>(one per line)</span></label>
              <textarea className="setting-input" rows={5} value={form.srTypes.join('\n')} onChange={e => setForm({...form, srTypes: e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)})} placeholder={"Pothole Report\nStreet Light Outage\nGraffiti"} style={{resize:'vertical', lineHeight:1.6}} />
            </div>
          )}

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>City Limits Geofencing</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Restrict submissions to city boundary</div>
              </div>
              <button className={`toggle-switch ${form.geofenceEnabled?'on':''}`} onClick={() => setForm(f=>({...f, geofenceEnabled:!f.geofenceEnabled}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          {form.geofenceEnabled && (
            <div className="setting-field">
              <label className="setting-label">Out-of-bounds message</label>
              <input className="setting-input" value={form.geofenceMessage} onChange={e => setForm({...form, geofenceMessage: e.target.value})} placeholder="This address appears to be outside city limits..." />
            </div>
          )}

          {/* CivicBot Answers toggle */}
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:8}}>
                  🤖 CivicBot Answers
                  {form.isAnswersPlan && <span style={{fontSize:10,fontWeight:700,background:'#8B5CF620',color:'#8B5CF6',border:'1px solid #8B5CF630',borderRadius:4,padding:'2px 7px'}}>ENABLED</span>}
                </div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Unlocks AI-powered resident Q&A add-on (crawl + embeddings)</div>
              </div>
              <button className={'toggle-switch ' + (form.isAnswersPlan ? 'on' : '')} onClick={() => setForm(f => ({...f, isAnswersPlan: !f.isAnswersPlan}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)'}}>
            <strong style={{color:'var(--text-dim)'}}>City ID:</strong> <span style={{fontFamily:'monospace'}}>{city.id}</span><br/>
            <strong style={{color:'var(--text-dim)'}}>Admin:</strong> {city.admin_email || '—'}<br/>
            <strong style={{color:'var(--text-dim)'}}>Created:</strong> {new Date(city.created_at).toLocaleDateString()}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── City Submissions Modal ───────────────────────────────────────────────────
function CitySubmissionsModal({ city, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthCount, setMonthCount] = useState(0);

  useEffect(() => {
    apiFetch(`/api/submissions?cityId=${city.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) { setSubmissions(data.submissions); setMonthCount(data.monthCount); }
      })
      .finally(() => setLoading(false));
  }, [city.id]);

  function formatDate(dt) {
    if (!dt) return '—';
    const normalized = dt.includes('T') ? dt : dt.replace(' ', 'T') + 'Z';
    return new Date(normalized).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <div>
            <div className="modal-title">📋 {city.name} — Submissions</div>
            <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>{monthCount} this month · {submissions.length} total loaded</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:'0', maxHeight:'60vh', overflowY:'auto'}}>
          {loading ? (
            <div className="loading"><div className="spinner" />Loading...</div>
          ) : submissions.length === 0 ? (
            <div className="empty">No submissions yet for {city.name}.</div>
          ) : (
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>Confirmation #</th>
                  <th>SR Type</th>
                  <th>Address</th>
                  <th>Contact</th>
                  <th>Via</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(s => (
                  <tr key={s.id}>
                    <td><span style={{fontFamily:'monospace', fontSize:11, color:'var(--accent-light)'}}>{s.confirmation_number}</span></td>
                    <td style={{maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:12}}>{s.sr_type || '—'}</td>
                    <td style={{maxWidth:180, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:11, color:'var(--text-dim)'}}>{s.geo_address || s.address || '—'}</td>
                    <td style={{fontSize:11, color:'var(--text-dim)'}}>{s.caller_name && s.caller_name !== 'Anonymous' ? s.caller_name : <span style={{color:'var(--text-muted)'}}>Anon</span>}</td>
                    <td><span className={`delivery-badge ${s.delivery_method}`}>{s.delivery_method === 'email' ? '📧' : '🔗'}</span></td>
                    <td style={{fontSize:11, color:'var(--text-muted)', whiteSpace:'nowrap'}}>{formatDate(s.submitted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Answers Page ─────────────────────────────────────────────────────────────
function AnswersPage({ user, showToast, impersonating }) {
  const cityId = user?.cityId;
  // Manual crawl is staff-only (it spends Firecrawl credits). Super admins —
  // including while impersonating a city — keep the button; real city admins
  // rely on the weekly auto-crawl.
  const canManualCrawl = !!impersonating || user?.role === 'super_admin';
  const [status, setStatus] = React.useState(null);
  const [config, setConfig] = React.useState({ startUrl: '', maxPages: 500, maxPagesLocked: false });
  const [crawlConfirm, setCrawlConfirm] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [crawling, setCrawling] = React.useState(false);
  const [qaLog, setQaLog] = React.useState([]);
  const [clearConfirm, setClearConfirm] = React.useState(false);
  const [billingStatus, setBillingStatus] = React.useState(null);

  React.useEffect(() => {
    if (!cityId) return;
    Promise.all([loadStatus(), loadConfig(), loadQaLog()]).finally(() => setLoading(false));
  }, [cityId]);

  async function loadStatus() {
    try {
      const res = await apiFetch('/api/admin/crawl/status/' + cityId);
      const data = await res.json();
      setStatus(data);
      if (data.lastRun && data.lastRun.status === 'running') setCrawling(true);
      else setCrawling(false);
    } catch (e) { console.error('[Answers] Status fetch failed', e); }
  }

  async function loadConfig() {
    try {
      const res = await apiFetch('/api/admin/crawl/config/' + cityId);
      const data = await res.json();
      if (data.config) {
        setConfig({
          startUrl: data.config.start_url || '',
          maxPages: data.config.max_pages || 500,
          maxPagesLocked: data.config.max_pages_locked === 1,
        });
      }
      if (data.billingStatus) setBillingStatus(data.billingStatus);
    } catch (e) { console.error('[Answers] Config fetch failed', e); }
  }

  // Poll every 10s while crawl is running
  React.useEffect(() => {
    if (!crawling) return;
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [crawling]);

  async function saveConfig() {
    if (!config.startUrl.trim()) { showToast('Start URL is required', 'error'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/crawl/config/' + cityId, {
        method: 'POST',
        body: JSON.stringify({ startUrl: config.startUrl, maxPages: config.maxPages, crawlFrequency: 'weekly', ...(canManualCrawl ? { maxPagesLocked: config.maxPagesLocked } : {}) }),
      });
      const data = await res.json();
      if (data.success) showToast('Configuration saved');
      else showToast(data.error || 'Save failed', 'error');
    } catch (e) { showToast('Save failed — check connection', 'error'); }
    setSaving(false);
  }

  async function startCrawl() {
    if (!config.startUrl.trim()) { showToast('Save a Start URL first', 'error'); return; }
    if (!crawlConfirm) { setCrawlConfirm(true); return; }
    setCrawlConfirm(false);
    setCrawling(true);
    try {
      const res = await apiFetch('/api/admin/crawl/start/' + cityId, { method: 'POST' });
      const data = await res.json();
      if (data.success) showToast('Crawl started — status updates every 10 seconds');
      else { showToast(data.error || 'Failed to start crawl', 'error'); setCrawling(false); }
    } catch (e) { showToast('Failed to start crawl', 'error'); setCrawling(false); }
  }

  async function loadQaLog() {
    try {
      const res = await apiFetch('/api/admin/qa-log/' + cityId);
      const data = await res.json();
      if (data.success) setQaLog(data.log);
    } catch (e) { console.error('[Answers] QA log fetch failed', e); }
  }

  async function resolveQaEntry(id, resolved) {
    try {
      await apiFetch('/api/admin/qa-log/' + id, { method: 'PATCH', body: JSON.stringify({ resolved }) });
      setQaLog(prev => prev.map(r => r.id === id ? { ...r, resolved: resolved ? 1 : 0, resolved_at: resolved ? new Date().toISOString().slice(0, 10) : null } : r));
    } catch (e) { showToast('Failed to update entry', 'error'); }
  }

  async function clearQaLog() {
    if (!clearConfirm) { setClearConfirm(true); return; }
    try {
      await apiFetch('/api/admin/qa-log/' + cityId, { method: 'DELETE' });
      setQaLog([]);
      setClearConfirm(false);
      showToast('Q&A log cleared');
    } catch (e) { showToast('Failed to clear log', 'error'); }
  }

  function formatDate(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  const lastRun = status?.lastRun;
  const runStatus = lastRun?.status;
  const statusColor = runStatus === 'success' ? 'var(--success)' : runStatus === 'running' ? 'var(--warning)' : runStatus === 'failed' ? 'var(--danger)' : 'var(--text-muted)';
  const statusLabel = runStatus === 'success' ? '✅ Success' : runStatus === 'running' ? '⏳ Running' : runStatus === 'failed' ? '❌ Failed' : '— Never run';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🤖 CivicBot Answers</div>
          <div className="page-subtitle">Manage the knowledge base that powers resident Q&A for {user?.cityName}</div>
        </div>
        {crawling && (
          <span style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--warning)',background:'#F59E0B15',border:'1px solid #F59E0B30',padding:'8px 14px',borderRadius:8}}>
            <div className="spinner" style={{width:14,height:14,borderWidth:2}} />Crawl in progress...
          </span>
        )}
      </div>

      {/* Status Card */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📊 Knowledge Base Status</div></div>
        <div className="card-body">
          {status?.demoChunkCount > 0 && (
            <div style={{background:'#F59E0B10',border:'1px solid #F59E0B40',borderRadius:8,padding:'14px 16px',marginBottom:16}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:10}}>
                <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
                <div style={{fontSize:12,color:'#F59E0B'}}>
                  <strong>Trial Demo Data Active</strong> — {status.demoChunkCount} generic municipal sample chunks are loaded so you can test CivicBot Answers right now. All responses are prefixed with "Trial Data — Not real city data." When your trial converts to a paid plan, this sample data is cleared and your actual city website is crawled automatically.
                </div>
              </div>
              <div style={{fontSize:12,color:'var(--text-dim)',marginLeft:26}}>
                <div style={{fontWeight:600,marginBottom:6,color:'var(--text-dim)'}}>Try these sample questions in your widget to see CivicBot Answers in action:</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px 16px'}}>
                  {[
                    'How do I report a pothole?',
                    'What are City Hall hours?',
                    'How do I apply for a building permit?',
                    'How do I set up utility service?',
                    'What parks programs are available?',
                    'How do I report illegal dumping?',
                    'What trash and recycling services are available?',
                  ].map(q => (
                    <span key={q} style={{background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,padding:'3px 8px',fontSize:11,color:'var(--text-dim)',fontStyle:'italic'}}>"{q}"</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:16,marginBottom:16}}>
            {[
              { label: 'Pages Indexed', value: status?.pageCount ?? 0, color: 'var(--accent-light)' },
              { label: 'Real Chunks', value: status?.chunkCount ?? 0, color: '#8B5CF6' },
              { label: 'Last Run Status', value: statusLabel, color: statusColor, small: true },
              { label: 'Last Crawled', value: lastRun ? formatDate(lastRun.started_at) : 'Never', color: 'var(--text)', small: true },
            ].map((card, i) => (
              <div key={i} style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>{card.label}</div>
                <div style={{fontSize:card.small?13:24,fontWeight:700,color:card.color,lineHeight:1.2}}>{card.value}</div>
              </div>
            ))}
          </div>
          {lastRun?.error_message && (
            <div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--danger)'}}>
              ❌ Last error: {lastRun.error_message}
            </div>
          )}
          {lastRun?.firecrawl_cost_usd > 0 && (
            <div style={{fontSize:12,color:'var(--text-muted)',marginTop:8}}>
              Last crawl cost: ${Number(lastRun.firecrawl_cost_usd).toFixed(4)} · Pages crawled: {lastRun.pages_crawled}
            </div>
          )}
        </div>
      </div>

      {/* Config Card */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">⚙️ Crawl Configuration</div></div>
        <div className="card-body">
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div className="setting-field">
              <label className="setting-label">Website(s) to Crawl</label>
              <textarea
                className="setting-input"
                value={config.startUrl}
                onChange={e => setConfig({...config, startUrl: e.target.value})}
                placeholder={"https://www.yourcity.gov\nhttps://www.yourcitylibrary.org"}
                rows={3}
                style={{resize:'vertical',fontFamily:'inherit'}}
              />
              <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>CivicBot will crawl each site (and its linked pages, up to your page limit per site). Add one URL per line for a separate library, utility district, or other affiliated site.</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div className="setting-field">
                <label className="setting-label">Max Pages</label>
                <select className="setting-input" value={config.maxPages} disabled={config.maxPagesLocked && !canManualCrawl} onChange={e => setConfig({...config, maxPages: parseInt(e.target.value)})} style={{cursor:(config.maxPagesLocked && !canManualCrawl)?'not-allowed':'pointer'}}>
                  {![50,100,300,500,1000].includes(config.maxPages) && <option value={config.maxPages}>{config.maxPages} pages</option>}
                  <option value={50}>50 pages</option>
                  <option value={100}>100 pages</option>
                  <option value={300}>300 pages</option>
                  <option value={500}>500 pages</option>
                  <option value={1000}>1,000 pages</option>
                </select>
                {config.maxPagesLocked && !canManualCrawl && (
                  <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>🔒 Set by CivicBot staff. Contact us if you need a different limit.</span>
                )}
                {canManualCrawl && (
                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'var(--text-muted)',marginTop:6,cursor:'pointer'}}>
                    <input type="checkbox" checked={!!config.maxPagesLocked} onChange={e => setConfig({...config, maxPagesLocked: e.target.checked})} />
                    Lock page limit (only CivicBot staff can change it)
                  </label>
                )}
              </div>
              <div className="setting-field">
                <label className="setting-label">Crawl Schedule</label>
                <div style={{padding:'9px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text-dim)'}}>🕑 Weekly — Sundays at 2:00 AM MDT (auto)</div>
                <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Crawls run automatically. Use manual crawl below only when needed.</span>
              </div>
            </div>
            <div>
              <button className="save-btn" onClick={saveConfig} disabled={saving} style={{padding:'9px 24px'}}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Card */}
      <div className="card">
        <div className="card-header"><div className="card-title">🚀 Actions</div></div>
        <div className="card-body">
          {(billingStatus === 'trialing' || billingStatus === 'trial') ? (
            <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 18px',fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>
              ⏳ <strong style={{color:'var(--text-dim)'}}>Crawl pending trial activation</strong> — your initial knowledge base crawl will start automatically when your trial converts to a paid plan.
            </div>
          ) : !canManualCrawl ? (
            <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 18px',fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>
              🗓️ <strong style={{color:'var(--text-dim)'}}>Crawls run automatically</strong> — your knowledge base refreshes every Sunday. Need an off-cycle refresh? Contact CivicBot support.
            </div>
          ) : (
            <>
              <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                {!crawlConfirm ? (
                  <button
                    onClick={startCrawl}
                    disabled={crawling || !config.startUrl.trim()}
                    style={{
                      padding:'10px 24px',
                      background: crawling || !config.startUrl.trim() ? 'var(--surface3)' : 'var(--accent)',
                      border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',
                      cursor: crawling || !config.startUrl.trim() ? 'not-allowed' : 'pointer',
                      display:'flex',alignItems:'center',gap:8,transition:'background 0.2s'
                    }}
                  >
                    {crawling
                      ? <><div className="spinner" style={{width:14,height:14,borderColor:'#ffffff40',borderTopColor:'#fff',borderWidth:2}} />Crawling...</>
                      : '🕷️ Run Manual Crawl'}
                  </button>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:10,background:'#F59E0B15',border:'1px solid #F59E0B40',borderRadius:10,padding:'10px 16px'}}>
                    <span style={{fontSize:13,color:'var(--warning)',fontWeight:600}}>⚠️ This uses Firecrawl credits. Confirm?</span>
                    <button onClick={startCrawl} style={{padding:'6px 16px',background:'#F59E0B',border:'none',borderRadius:6,color:'white',fontSize:12,fontWeight:600,fontFamily:'inherit',cursor:'pointer'}}>Yes, Run Crawl</button>
                    <button onClick={() => setCrawlConfirm(false)} style={{padding:'6px 14px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Cancel</button>
                  </div>
                )}
                {crawling && <span style={{fontSize:12,color:'var(--text-muted)'}}>Usually takes 2–10 minutes depending on site size. Status refreshes automatically.</span>}
              </div>
              {!config.startUrl.trim() && (
                <div style={{marginTop:12,fontSize:12,color:'var(--warning)'}}>⚠️ Save a Start URL above before running a crawl.</div>
              )}
            </>
          )}
          <div style={{marginTop:16,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-muted)',lineHeight:1.6}}>
            💡 <strong style={{color:'var(--text-dim)'}}>How it works:</strong> CivicBot crawls your city website, splits content into searchable chunks, and generates AI embeddings so residents can ask natural language questions and get grounded answers with source links.
          </div>
        </div>
      </div>

      {/* Unanswered Q&A Log */}
      <div className="card" style={{marginTop:20}}>
        <div className="card-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div className="card-title">❓ Unanswered Q&A Log</div>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>Questions residents asked that CivicBot couldn't answer</span>
          </div>
          {qaLog.length > 0 && (
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {clearConfirm ? (
                <>
                  <span style={{fontSize:12,color:'var(--warning)',fontWeight:600}}>Are you sure? This cannot be undone.</span>
                  <button onClick={clearQaLog} style={{padding:'5px 14px',background:'var(--danger)',border:'none',borderRadius:6,color:'white',fontSize:12,fontWeight:600,fontFamily:'inherit',cursor:'pointer'}}>Yes, Clear All</button>
                  <button onClick={() => setClearConfirm(false)} style={{padding:'5px 12px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Cancel</button>
                </>
              ) : (
                <button onClick={() => setClearConfirm(true)} style={{padding:'5px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Clear All</button>
              )}
            </div>
          )}
        </div>
        <div className="card-body" style={{padding:0}}>
          {qaLog.length === 0 ? (
            <div style={{padding:'24px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>No unanswered questions yet — great job! 🎉</div>
          ) : (
            <div style={{overflowY:'auto',maxHeight:400}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:'1px solid var(--border)',background:'var(--surface2)'}}>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:50}}>#</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5}}>Question</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:90}}>Asked</th>
                    <th style={{padding:'8px 14px',textAlign:'center',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:80}}>Resolved</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:90}}>Resolved On</th>
                  </tr>
                </thead>
                <tbody>
                  {qaLog.map(row => (
                    <tr key={row.id} style={{borderBottom:'1px solid var(--border)',opacity: row.resolved ? 0.55 : 1, background: row.resolved ? 'var(--surface2)' : 'transparent'}}>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',fontVariantNumeric:'tabular-nums'}}>{row.id}</td>
                      <td style={{padding:'10px 14px',color:'var(--text)',lineHeight:1.4,textDecoration: row.resolved ? 'line-through' : 'none'}}>{row.question}</td>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{row.created_at ? row.created_at.slice(0,10) : '—'}</td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="checkbox" checked={!!row.resolved} onChange={e => resolveQaEntry(row.id, e.target.checked)} style={{width:16,height:16,cursor:'pointer',accentColor:'var(--accent)'}} />
                      </td>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{row.resolved_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Embed Code Generator ──────────────────────────────────────────────────────
function generateEmbedCode(templates, ids) {
  const templateIds = ids || templates.map(t => t.ProblemSid);
  return `<script src="https://civicbot.centricitygis.com/widget.js"\n  data-city-id="your-city-id"\n  data-templates="${templateIds.join(',')}"\n  data-color="#3B82F6"\n  async>\n</script>`;
}
